import React, { useState, useRef, useCallback, useEffect, useContext, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { FlowChartContext } from '../../app/providers/FlowChartContext';
import { TicketContext } from '../../app/providers/TicketContext';
import Editor from "@monaco-editor/react";
import mxgraph from 'mxgraph';
import gridIcon from '../../assets/toolbar-icon/grid.gif';
import { fetchWrapper } from '../../shared/api/fetchWrapper';
import { useNavigate } from 'react-router-dom';
// FlowChartTabBar kaldırıldı — tab geçişi artık code editor tab'larından yapılıyor
import FlowChartToolbar from './components/FlowChartToolbar';
import FlowChartSidebar from './components/FlowChartSidebar';
import FlowChartGraphArea from './components/FlowChartGraphArea';
import FlowChartCodeEditorPanel from './components/FlowChartCodeEditorPanel';
import FloatingCodeEditor from './components/FloatingCodeEditor';
import FlowChartModals from './components/FlowChartModals';
import FlowChartFunctionWindows from './components/FlowChartFunctionWindows';
import FlowChartPromptWindows from './components/FlowChartPromptWindows';
import FlowChartStatusBar from './components/FlowChartStatusBar';
import FlowChartTerminalDock from './components/FlowChartTerminalDock';
import useTabManager from './hooks/useTabManager';
import useIncludeManager from './hooks/useIncludeManager';
import useFunctionWindows from './hooks/useFunctionWindows';
import useAIHelpers from './hooks/useAIHelpers';

const { mxGraph, mxClient, mxRubberband, mxEvent, mxUtils, mxPoint, mxConnectionConstraint, mxConstants, mxShape, mxCellRenderer, mxCellOverlay, mxImage } = mxgraph();

// Custom Triangle Shape for mxGraph
function mxTriangleShape() {
  mxShape.call(this);
}
mxUtils.extend(mxTriangleShape, mxShape);
mxTriangleShape.prototype.paintVertexShape = function(c, x, y, w, h) {
  c.translate(x, y);
  c.begin();
  c.moveTo(w / 2, 0);
  c.lineTo(w, h);
  c.lineTo(0, h);
  c.close();
  c.fillAndStroke();
};
mxTriangleShape.prototype.getLabelBounds = function(rect) {
  const bounds = new mxPoint(rect.x, rect.y);
  bounds.width = rect.width;
  bounds.height = rect.height;
  bounds.y += rect.height * 0.25;
  bounds.height *= 0.75;
  return bounds;
};

// Custom Predefined Process Shape (double border rectangle) for mxGraph
function mxPredefinedProcessShape() {
  mxShape.call(this);
}
mxUtils.extend(mxPredefinedProcessShape, mxShape);
mxPredefinedProcessShape.prototype.paintVertexShape = function(c, x, y, w, h) {
  c.translate(x, y);
  const inset = 8;
  // Outer rectangle
  c.rect(0, 0, w, h);
  c.fillAndStroke();
  // Inner rectangle (double border effect)
  c.begin();
  c.moveTo(inset, inset);
  c.lineTo(w - inset, inset);
  c.lineTo(w - inset, h - inset);
  c.lineTo(inset, h - inset);
  c.close();
  c.stroke();
};
mxPredefinedProcessShape.prototype.getLabelBounds = function(rect) {
  const bounds = new mxPoint(rect.x, rect.y);
  bounds.width = rect.width;
  bounds.height = rect.height;
  bounds.x += 8;
  bounds.y += 8;
  bounds.width -= 16;
  bounds.height -= 16;
  return bounds;
};

// Attach icon oluşturma fonksiyonu - mxGraph overlay için
// Base64 encoded SVG icon (yeşil daire içinde paperclip)
const createAttachIcon = () => {
  // SVG icon'u base64'e çevir - paperclip icon (basit ve görünür)
  const svgString = `
    <svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
      <circle cx="10" cy="10" r="9" fill="#4caf50" stroke="white" stroke-width="1.5"/>
      <path d="M8 6.5c-1.5 0-2.5 1-2.5 2.5v4c0 1.5 1 2.5 2.5 2.5s2.5-1 2.5-2.5v-3c0-0.5 0.5-1 1-1s1 0.5 1 1v3c0 1.5-1 2.5-2.5 2.5s-2.5-1-2.5-2.5v-4c0-1.5 1-2.5 2.5-2.5" 
            stroke="white" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
    </svg>
  `.trim();
  
  const base64Svg = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgString)));
  
  // mxImage kullanarak icon oluştur
  return new mxImage(base64Svg, 20, 20);
};

/**
 * FlowChartDevelopmentScreen - mxGraph Implementation
 * 
 * Modern, kaliteli ve stabil bir flowchart development environment.
 * mxGraph kullanarak tüm özellikleri destekler:
 * - 8 handle noktası (4 köşe + 4 kenar orta) connection constraint'ler ile
 * - Editable node ve edge labels
 * - Design Mode toggle
 * - Shape ekleme (triangle, cylinder, preProcess, rectangle, rhombus, startCircle, stopCircle)
 * - Node ve Edge silme
 * - Code Editor (Monaco Editor)
 * - Save/Load functionality
 * - Context menu
 * - Node resizing
 * - Edge connection ve update
 * - Keyboard shortcuts
 * - Waypoint/breakpoint desteği (mxGraph'te edge'ler zaten bendable)
 */
const FlowChartDevelopmentScreen = ({ 
  workflowData, 
  onClose
}) => {
  
  // ========== STATE MANAGEMENT ==========
  const [isDesignMode, setIsDesignMode] = useState(() => {
    try {
      const saved = localStorage.getItem('flowchart-design-mode');
      return saved === 'true';
    } catch {
      return false;
    }
  });
  const [showCodeEditor, setShowCodeEditor] = useState(false);
  /* ── Çoklu code editor tab ── */
  const [editorTabs, setEditorTabs] = useState([]);          // [{ shapeId, shapeLabel }]
  const [floatingEditors, setFloatingEditors] = useState([]); // [{ id, shapeId, shapeLabel, code, x, y, w, h }]
  const [showTerminal, setShowTerminal] = useState(false);
  const [code, setCode] = useState("");
  const [isSavingScript, setIsSavingScript] = useState(false);
  const [saveScriptStatus, setSaveScriptStatus] = useState(null);
  const [terminalInput, setTerminalInput] = useState('');
  const [terminalLines, setTerminalLines] = useState(() => ([
    { id: 'ready', type: 'system', text: '$ ready' },
    { id: 'hint', type: 'muted', text: '# AI terminal hazır' },
  ]));
  const [isTerminalBusy, setIsTerminalBusy] = useState(false);
  const [pendingChange, setPendingChange] = useState(null);
  const [isApplyingChange, setIsApplyingChange] = useState(false);
  const [shapeColor, setShapeColor] = useState('#FFFF00');
  const [defaultText, setDefaultText] = useState('Yeni Şekil');
  const [selectedShapeLabel, setSelectedShapeLabel] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [selectedShapeColor, setSelectedShapeColor] = useState('#FFFF00');
  const [rulerState, setRulerState] = useState({ scale: 1, translateX: 0, translateY: 0 });
  const [isSpacePressed, setIsSpacePressed] = useState(false);
  const [currentPrompt, setCurrentPrompt] = useState('');
  const navigate = useNavigate();
  const monacoRef = useRef(null);
  const editorRef = useRef(null);
  const graphContainerRef = useRef(null);
  const graphRef = useRef(null);
  const rubberbandRef = useRef(null);
  const workflowIdRef = useRef(workflowData?.workflow?.id || null);
  const shapeCodeCacheRef = useRef({});
  const promptCacheRef = useRef({});
  const isUpdatingEditorRef = useRef(false);
  const resizeStartXRef = useRef(0);
  const resizeStartWidthRef = useRef(0);
  const previousSelectedShapeRef = useRef(null);
  const previousActiveTabIdRef = useRef(null);
  const { saveWorkflow, selectedShape, setSelectedShape, saveScript, requestFiles } = useContext(FlowChartContext);
  const { selectedTicketId, SendEvent } = useContext(TicketContext);
  const {
    aiStatus,
    setAiStatus,
    showAIPrompt,
    setShowAIPrompt,
    aiPrompt,
    setAiPrompt,
    isGeneratingCode,
    aiMode,
    setAiMode,
    selectedCode,
    setSelectedCode,
    getOpenAIKey,
    getAICodeSuggestions,
    generateCodeFromPrompt,
    handleGenerateCode,
    handleOpenAIPrompt,
  } = useAIHelpers({
    code,
    setCode,
    selectedShape,
    editorRef,
    monacoRef,
    shapeCodeCacheRef,
    isUpdatingEditorRef,
  });

  const {
    showIncludeModal,
    setShowIncludeModal,
    selectedIncludeShape,
    setSelectedIncludeShape,
    includeFoldersData,
    setIncludeFoldersData,
    isLoadingIncludeData,
    selectedIncludePath,
    setSelectedIncludePath,
    selectedFolder,
    setSelectedFolder,
    includeFilesData,
    setIncludeFilesData,
    isLoadingFiles,
    selectedFile,
    setSelectedFile,
    shapeIncludePaths,
    setShapeIncludePaths,
    shapeIncludeFolders,
    setShapeIncludeFolders,
    shapeOriginalLabels,
    setShapeOriginalLabels,
    shapesWithInclude,
    setShapesWithInclude,
    includeError,
    setIncludeError,
    showIncludeInfoModal,
    setShowIncludeInfoModal,
    includeInfoData,
    setIncludeInfoData,
    shapeIncludePathsRef,
    shapeIncludeFoldersRef,
  } = useIncludeManager({ selectedTicketId, navigate, fetchWrapper });
  const [codePanelWidth, setCodePanelWidth] = useState(50); // Code panel genişliği (yüzde)
  const [isResizing, setIsResizing] = useState(false);
  const [functionLibrary, setFunctionLibrary] = useState(null); // Function kütüphanesi verisi
  const [isLoadingFunctionLibrary, setIsLoadingFunctionLibrary] = useState(false); // Function library yüklenirken
  const [showFunctionLibraryModal, setShowFunctionLibraryModal] = useState(false); // Function library modal açık/kapalı
  const [functionLibraryError, setFunctionLibraryError] = useState(null); // Function library hata mesajı
  const [toast, setToast] = useState(null); // { type: 'success'|'error', message: string }
  const [isLoadingFxGroups, setIsLoadingFxGroups] = useState(false); // Fx groups yüklenirken
  const [showFxGroupsModal, setShowFxGroupsModal] = useState(false); // Fx groups modal açık/kapalı
  const [fxGroups, setFxGroups] = useState([]); // Fx groups listesi
  const [fxGroupsError, setFxGroupsError] = useState(null); // Fx groups hata mesajı
  const [selectedFxGroup, setSelectedFxGroup] = useState(null); // Seçilen Fx Group
  const [functions, setFunctions] = useState([]); // Functions listesi (fname ve fdesc içeren)
  const [isLoadingFunctions, setIsLoadingFunctions] = useState(false); // Functions yüklenirken
  const [functionsError, setFunctionsError] = useState(null); // Functions hata mesajı
  const [selectedFunction, setSelectedFunction] = useState(null); // Seçilen function
  const [showFunctionsModal, setShowFunctionsModal] = useState(false); // Functions modal açık/kapalı
  const {
    functionWindows,
    setFunctionWindows,
    draggingWindowId,
    setDraggingWindowId,
    dragOffset,
    setDragOffset,
    resizingWindowId,
    setResizingWindowId,
    resizeStart,
    setResizeStart,
    resizeDirection,
    setResizeDirection,
    functionWindowEditorRefs,
  } = useFunctionWindows();

  const {
    functionWindows: promptWindows,
    setFunctionWindows: setPromptWindows,
    draggingWindowId: draggingPromptWindowId,
    setDraggingWindowId: setDraggingPromptWindowId,
    dragOffset: promptDragOffset,
    setDragOffset: setPromptDragOffset,
    resizingWindowId: resizingPromptWindowId,
    setResizingWindowId: setResizingPromptWindowId,
    resizeStart: promptResizeStart,
    setResizeStart: setPromptResizeStart,
    resizeDirection: promptResizeDirection,
    setResizeDirection: setPromptResizeDirection,
  } = useFunctionWindows();
  const [fileSearchQuery, setFileSearchQuery] = useState(''); // File list arama sorgusu
  const [selectedFileCategory, setSelectedFileCategory] = useState('all'); // Seçili file kategorisi (folder)
  const {
    tabs,
    setTabs,
    activeTabId,
    setActiveTabId,
    tabCounter,
    setTabCounter,
    tabCodeCacheRef,
    workflowDataRef,
    handleFileClick,
    handleTabSwitch,
    handleTabClose,
  } = useTabManager({
    workflowData,
    navigate,
    fetchWrapper,
    selectedShape,
    setSelectedShape,
    setCode,
    editorRef,
    isUpdatingEditorRef,
    shapeCodeCacheRef,
    previousSelectedShapeRef,
    previousActiveTabIdRef,
  });

  // ========== BULK AI: UPDATE SCRIPTS FROM COMMENT INSTRUCTIONS ==========
  const [showBulkAiModal, setShowBulkAiModal] = useState(false);
  const [bulkAiItems, setBulkAiItems] = useState([]);
  const [isBulkAiGenerating, setIsBulkAiGenerating] = useState(false);
  const [isBulkAiApplying, setIsBulkAiApplying] = useState(false);
  const [bulkAiError, setBulkAiError] = useState(null);
  const [bulkAiExpanded, setBulkAiExpanded] = useState({});
  const [bulkAiSelected, setBulkAiSelected] = useState(() => ({}));

  const stripCodeFences = useCallback((text) => {
    if (!text) return '';
    const t = String(text).trim();
    const noStart = t.replace(/^```[a-zA-Z0-9_-]*\s*/m, '');
    const noEnd = noStart.replace(/```$/m, '');
    return noEnd.trim();
  }, []);

  const detectLanguageFromScript = useCallback((script) => {
    const s = (script || '').trim();
    const head = s.slice(0, 800).toLowerCase();
    const body = s.toLowerCase();

    if (head.includes('<?php') || body.includes('$this->') || /\$\w+/.test(s)) return 'php';
    if (head.includes('#!/bin/bash') || head.includes('#!/usr/bin/env bash')) return 'bash';
    if (/\bselect\b[\s\S]{0,200}\bfrom\b/i.test(s) || /\binsert\b[\s\S]{0,200}\binto\b/i.test(s)) return 'sql';
    if (/\bdef\s+\w+\s*\(/.test(s) || /\bimport\s+\w+/.test(s) || /\bprint\s*\(/.test(s)) return 'python';
    if (/\bconsole\./.test(s) || /\bexport\s+/.test(s) || /\bimport\s+/.test(s)) return 'javascript';
    if (/\binterface\s+\w+/.test(s) || /\btype\s+\w+\s*=/.test(s)) return 'typescript';
    return 'unknown';
  }, []);

  const extractInstructionComments = useCallback((script) => {
    const s = String(script || '');
    const lines = s.split('\n');
    const maxScan = Math.min(lines.length, 250);
    const explicitRe =
      /(TODO|FIXME|AI|İSTEK|ISTEK|REQUEST|GÜNCELLE|GUNCELLE|DEĞİŞTİR|DEGISTIR|EKLE|KALDIR|REFACTOR|OPTIMIZE|İYİLEŞTİR|IYILESTIR|BUG)/i;

    const out = [];
    const pushIf = (raw) => {
      const t = String(raw || '').trim();
      if (!t) return;
      if (t.length > 500) return;
      out.push(t);
    };

    // 1) Line comments (explicit)
    for (let i = 0; i < maxScan; i++) {
      const t = (lines[i] || '').trim();
      if (!t) continue;
      const isLineComment = t.startsWith('//') || t.startsWith('#') || t.startsWith('--') || t.startsWith(';--');
      if (!isLineComment) continue;
      const cleaned = t.replace(/^\/\//, '').replace(/^#/, '').replace(/^;?--/, '').trim();
      if (explicitRe.test(cleaned)) pushIf(cleaned);
    }
    if (out.length > 0) return out.slice(0, 40);

    // 2) Top-of-file block comment (explicit)
    const top = lines.slice(0, Math.min(lines.length, 80)).join('\n');
    const blockMatch = top.match(/\/\*[\s\S]*?\*\//);
    if (blockMatch && blockMatch[0]) {
      const block = blockMatch[0]
        .replace(/^\/\*/, '')
        .replace(/\*\/$/, '')
        .split('\n')
        .map((l) => l.replace(/^\s*\*\s?/, '').trim())
        .filter(Boolean)
        .filter((l) => explicitRe.test(l));
      if (block.length > 0) return block.slice(0, 40);
    }

    return [];
  }, []);

  const getShapeDisplayName = useCallback((shapeId, shapeData) => {
    try {
      const graph = graphRef.current;
      const model = graph?.getModel?.();
      const cell = model?.getCell?.(shapeId);
      const v = cell?.getValue?.();
      const label = v !== undefined && v !== null ? String(v).trim() : '';
      if (label) return label;
    } catch (_e) {
      // ignore
    }
    const def = shapeData?.definition ? String(shapeData.definition).trim() : '';
    return def || String(shapeId);
  }, []);

  const getCurrentWorkflowDataForBulk = useCallback(() => {
    // workflowDataRef.current her zaman en güncel kaynak olmalı (prompt edit / include / vs.)
    const refData = workflowDataRef.current;
    if (refData) return refData;
    const activeTab = tabs?.find?.((t) => t.id === activeTabId);
    return activeTab?.workflowData || workflowData;
  }, [activeTabId, tabs, workflowData]); // workflowDataRef çıkarıldı - ref'ler dependency'ye girmemeli

  const getScriptValueForShape = useCallback(
    (shapeId, shapeData) => {
      const tabCache =
        activeTabId && tabCodeCacheRef.current && tabCodeCacheRef.current[activeTabId]
          ? tabCodeCacheRef.current[activeTabId][shapeId]
          : undefined;
      if (tabCache !== undefined) return tabCache !== null ? String(tabCache) : '';

      const legacyCache = shapeCodeCacheRef.current ? shapeCodeCacheRef.current[shapeId] : undefined;
      if (legacyCache !== undefined) return legacyCache !== null ? String(legacyCache) : '';

      const fromData = shapeData?.script;
      return typeof fromData === 'string' ? fromData : '';
    },
    [activeTabId], // Ref'ler dependency'ye girmemeli (tabCodeCacheRef, shapeCodeCacheRef)
  );

  const updateShapeAIPrompt = useCallback(
    (shapeId, newPrompt) => {
      const promptStr = newPrompt !== null && newPrompt !== undefined ? String(newPrompt) : '';

      // Bağımsız prompt cache — hiçbir dış effect tarafından ezilemez
      promptCacheRef.current[String(shapeId)] = promptStr;

      // Update workflowDataRef (aktif data)
      try {
        const current = workflowDataRef.current || workflowData;
        if (current && Array.isArray(current.contents)) {
          const updatedContents = current.contents.map((it) => {
            if (!it || String(it.id) !== String(shapeId)) return it;
            return { ...it, ai_prompt: promptStr };
          });
          // workflowDataRef.current null olsa bile set edelim ki tüm okuma noktaları aynı kaynaktan görsün
          workflowDataRef.current = { ...current, contents: updatedContents };
          
          // DEBUG: Güncelleme başarılı
        }
      } catch (_e) {
        // ignore
      }

      // Update active tab workflowData (UI state)
      if (activeTabId) {
        setTabs((prev) =>
          prev.map((tab) => {
            if (!tab || tab.id !== activeTabId) return tab;
            const currentData = tab.workflowData || workflowDataRef.current || workflowData;
            if (!currentData || !Array.isArray(currentData.contents)) return tab;
            const updatedContents = currentData.contents.map((it) => {
              if (!it || String(it.id) !== String(shapeId)) return it;
              return { ...it, ai_prompt: promptStr };
            });
            return { ...tab, workflowData: { ...currentData, contents: updatedContents } };
          }),
        );
      }
    },
    [activeTabId, setTabs, workflowData], // workflowDataRef çıkarıldı - ref'ler dependency'ye girmemeli
  );

  const openPromptEditorWindow = useCallback(
    (shapeId, options = {}) => {
      const { x = 120, y = 120 } = options;
      const current = workflowDataRef.current || workflowData;
      const shapeData =
        current && Array.isArray(current.contents)
          ? current.contents.find((it) => it && String(it.id) === String(shapeId))
          : null;
      const label = getShapeDisplayName(shapeId, shapeData);
      // Önce cache'ten oku (kullanıcı düzenlemeleri), sonra workflowData
      const cachedPrompt = promptCacheRef.current[String(shapeId)];
      const currentPrompt = cachedPrompt !== undefined ? cachedPrompt : (shapeData?.ai_prompt !== undefined && shapeData?.ai_prompt !== null ? String(shapeData.ai_prompt) : '');

      const winId = `prompt-${shapeId}`;
      setPromptWindows((prev) => {
        const existing = prev.find((w) => w.id === winId);
        const base = existing
          ? {
              ...existing,
              position: existing.position || { x, y },
              stepLabel: label, // Label'ı da güncelle
              prompt: currentPrompt, // ✅ FIX: HER ZAMAN en güncel prompt'u kullan
              originalPrompt: currentPrompt,
              saveStatus: null, // Status'u resetle
            }
          : {
              id: winId,
              shapeId: String(shapeId),
              stepLabel: label,
              prompt: currentPrompt,
              originalPrompt: currentPrompt,
              position: { x, y },
              width: 620,
              height: 360,
              isMinimized: false,
              saveStatus: null,
            };
        // bring to front
        const filtered = prev.filter((w) => w.id !== winId);
        return [...filtered, base];
      });
    },
    [getShapeDisplayName, setPromptWindows, workflowData],
  );

  const closePromptWindow = useCallback((windowId) => {
    setPromptWindows((prev) => prev.filter((w) => w.id !== windowId));
  }, [setPromptWindows]);

  const toggleMinimizePromptWindow = useCallback((windowId) => {
    setPromptWindows((prev) =>
      prev.map((w) => (w.id === windowId ? { ...w, isMinimized: !w.isMinimized } : w)),
    );
  }, [setPromptWindows]);

  const onChangePromptWindow = useCallback(
    (windowId, nextValue) => {
      setPromptWindows((prev) =>
        prev.map((w) => (w.id === windowId ? { ...w, prompt: nextValue, saveStatus: null } : w)),
      );
      // Anında workflow içine yaz ki üstteki Kaydet butonu prompt’u da göndersin
      // windowId formatı: "prompt-<shapeId>"
      const parsedShapeId = windowId && windowId.startsWith('prompt-') ? windowId.slice('prompt-'.length) : null;
      const win = promptWindows.find((w) => w.id === windowId);
      const shapeId = win?.shapeId || parsedShapeId;
      if (shapeId) updateShapeAIPrompt(shapeId, nextValue);
    },
    [promptWindows, setPromptWindows, updateShapeAIPrompt],
  );

  const savePromptWindow = useCallback(
    (windowId) => {
      const parsedShapeId = windowId && windowId.startsWith('prompt-') ? windowId.slice('prompt-'.length) : null;
      const win = promptWindows.find((w) => w.id === windowId);
      const shapeId = win?.shapeId || parsedShapeId;
      if (!shapeId) return;
      updateShapeAIPrompt(shapeId, win?.prompt || '');
      setPromptWindows((prev) =>
        prev.map((w) =>
          w.id === windowId ? { ...w, saveStatus: { state: 'ok', message: 'Prompt güncellendi' } } : w,
        ),
      );
    },
    [promptWindows, setPromptWindows, updateShapeAIPrompt],
  );

  // Inline prompt editing handler (code editor panel)
  const handlePromptChange = useCallback((value) => {
    setCurrentPrompt(value);
    // Anında local state'e kaydet (workflowDataRef.current)
    // Toolbar'daki Kaydet butonu ile birlikte sunucuya gönderilecek
    if (selectedShape) {
      updateShapeAIPrompt(selectedShape, value);
    }
  }, [selectedShape, updateShapeAIPrompt]);

  /* ── Şekil seçildiğinde + code editor açıksa tab ekle / label güncelle ── */
  useEffect(() => {
    if (!selectedShape || !showCodeEditor) return;
    const label = selectedShapeLabel || String(selectedShape);
    setEditorTabs((prev) => {
      const existing = prev.find((t) => t.shapeId === selectedShape);
      if (existing) {
        // Label değiştiyse güncelle
        if (existing.shapeLabel !== label && label !== String(selectedShape)) {
          return prev.map((t) => t.shapeId === selectedShape ? { ...t, shapeLabel: label } : t);
        }
        return prev;
      }
      return [...prev, { shapeId: selectedShape, shapeLabel: label }];
    });
  }, [selectedShape, showCodeEditor, selectedShapeLabel]);

  /* ── Flow değişince tüm docked tab'ları temizle ── */
  useEffect(() => {
    setEditorTabs([]);
  }, [activeTabId]);

  /* ── Flow değiştiğinde prompt cache'ini yeniden doldur ── */
  useEffect(() => {
    promptCacheRef.current = {};
    const wd = workflowDataRef.current || workflowData;
    if (wd && Array.isArray(wd.contents)) {
      wd.contents.forEach((item) => {
        if (!item || !item.id) return;
        const p = item.ai_prompt;
        if (p !== undefined && p !== null && p !== 'null') {
          promptCacheRef.current[String(item.id)] = String(p);
        }
      });
    }
  }, [activeTabId, workflowData]);

  /* ── Code editor tab yönetimi ── */
  const handleCodeTabClose = useCallback((shapeId) => {
    setEditorTabs((prev) => {
      const next = prev.filter((t) => t.shapeId !== shapeId);
      if (next.length === 0) setShowCodeEditor(false);
      return next;
    });
  }, []);

  const handleTabActivate = useCallback((shapeId) => {
    if (setSelectedShape) setSelectedShape(shapeId);
  }, [setSelectedShape]);

  const handleTabDetach = useCallback((shapeId, shapeLabel) => {
    const code = shapeCodeCacheRef.current[shapeId] || '';
    /* Şeklin mevcut AI prompt'unu al — önce cache, sonra workflowData */
    const cached = promptCacheRef.current[String(shapeId)];
    const shapeData = (workflowDataRef.current || workflowData)?.contents?.find(
      (it) => it && String(it.id) === String(shapeId),
    );
    const prompt = cached !== undefined ? cached : (shapeData?.ai_prompt != null ? String(shapeData.ai_prompt) : '');
    setFloatingEditors((prev) => {
      const alreadyExists = prev.some((f) => f.shapeId === shapeId);
      if (alreadyExists) return prev;
      const offset = prev.length * 30;
      return [...prev, {
        id: `float-${shapeId}-${Date.now()}`,
        shapeId, shapeLabel: shapeLabel || String(shapeId),
        code, prompt, x: 80 + offset, y: 60 + offset, w: 640, h: 480,
      }];
    });
    /* Tab listesinden çıkar ve bir önceki taba geç */
    setEditorTabs((prev) => {
      const idx = prev.findIndex((t) => t.shapeId === shapeId);
      const next = prev.filter((t) => t.shapeId !== shapeId);
      if (next.length > 0 && setSelectedShape) {
        const switchTo = idx > 0 ? next[idx - 1] : next[0];
        setSelectedShape(switchTo.shapeId);
      }
      return next;
    });
  }, [workflowData, setSelectedShape]);

  const handleFloatingClose = useCallback((id) => {
    setFloatingEditors((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const handleFloatingCodeChange = useCallback((shapeId, newCode) => {
    shapeCodeCacheRef.current[shapeId] = newCode;
    if (activeTabId && tabCodeCacheRef.current[activeTabId]) {
      tabCodeCacheRef.current[activeTabId][shapeId] = newCode;
    }
  }, [activeTabId]);

  const handleFloatingSave = useCallback((shapeId, code) => {
    return saveScript(shapeId, code);
  }, [saveScript]);

  const handleFloatingPromptChange = useCallback((shapeId, newPrompt) => {
    updateShapeAIPrompt(shapeId, newPrompt);
  }, [updateShapeAIPrompt]);

  // selectedShape değiştiğinde prompt'u yükle (önce cache, sonra workflowData)
  useEffect(() => {
    if (!selectedShape) {
      setCurrentPrompt('');
      return;
    }
    const cached = promptCacheRef.current[String(selectedShape)];
    if (cached !== undefined) {
      setCurrentPrompt(cached);
      return;
    }
    const current = workflowDataRef.current || workflowData;
    const shapeData =
      current && Array.isArray(current.contents)
        ? current.contents.find((it) => it && String(it.id) === String(selectedShape))
        : null;
    const prompt = shapeData?.ai_prompt !== undefined && shapeData?.ai_prompt !== null ? String(shapeData.ai_prompt) : '';
    setCurrentPrompt(prompt);
  }, [selectedShape, workflowData]);

  // ✅ FIX: workflowData değiştiğinde açık prompt window'larını güncelle
  useEffect(() => {
    if (!workflowData || !Array.isArray(workflowData.contents)) return;
    if (promptWindows.length === 0) return;

    setPromptWindows((prev) =>
      prev.map((win) => {
        const shapeData = workflowData.contents.find((it) => it && String(it.id) === String(win.shapeId));
        if (!shapeData) return win; // Shape silinmişse window'u koru (kullanıcı kapatır)
        
        const currentPrompt = shapeData.ai_prompt !== undefined && shapeData.ai_prompt !== null ? String(shapeData.ai_prompt) : '';
        const label = getShapeDisplayName(win.shapeId, shapeData);
        
        // Sadece değişiklik varsa güncelle (gereksiz render'ları önle)
        if (win.prompt === currentPrompt && win.stepLabel === label) return win;
        
        return {
          ...win,
          prompt: currentPrompt,
          originalPrompt: currentPrompt,
          stepLabel: label,
        };
      })
    );
  }, [workflowData, promptWindows, setPromptWindows, getShapeDisplayName]);

  const getOpenAIProxyUrl = useCallback(() => {
    const envUrl = process.env.REACT_APP_OPENAI_PROXY_URL;
    if (envUrl) return envUrl;
    if (process.env.NODE_ENV === 'production') return '/openai-proxy.php';
    return null;
  }, []);

  const callAI = useCallback(
    async ({ model, messages, max_tokens, temperature }) => {
      const proxyProcessId = process.env.REACT_APP_OPENAI_PROXY_PROCESS;
      const proxyUrl = getOpenAIProxyUrl();
      const apiKey = getOpenAIKey();

      // Öncelik 1: call.php üzerinden process ile proxy (prod için en stabil yol)
      if (proxyProcessId) {
        const token = localStorage.getItem('token');
        const bodyParams = {
          token,
          model,
          messages,
          max_tokens,
          temperature,
          // Bazı backend'lerde payload $data['data'] altında bekleniyor
          data: {
            token,
            model,
            messages,
            max_tokens,
            temperature,
          },
        };

        const res = await fetchWrapper(proxyProcessId, bodyParams, navigate);

        const cs = res?.CSResponse ?? res?.result?.CSResponse ?? res?.result ?? res;
        if (typeof cs === 'string') {
          // Bazı süreçler string JSON döndürüyor
          try {
            const parsed = JSON.parse(cs);
            if (parsed && typeof parsed === 'object') {
              if (typeof parsed.content === 'string') return parsed.content;
              if (parsed?.CSResponse?.content) return String(parsed.CSResponse.content);
              return JSON.stringify(parsed);
            }
          } catch (_e) {
            // ignore
          }
          return cs;
        }
        if (cs && typeof cs === 'object') {
          if (typeof cs.content === 'string') return cs.content;
          if (cs?.CSResponse && typeof cs.CSResponse?.content === 'string') return cs.CSResponse.content;
        }
        return '';
      }

      if (proxyUrl) {
        const resp = await fetch(proxyUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ model, messages, max_tokens, temperature }),
        });
        const text = await resp.text();
        let json;
        try {
          json = JSON.parse(text);
        } catch (_e) {
          throw new Error(`Proxy JSON parse hatası. Response: ${text.substring(0, 200)}`);
        }
        if (!resp.ok || json?.CSResponse?.ok === 0) {
          throw new Error(json?.error || `Proxy HTTP ${resp.status}`);
        }
        return String(json?.CSResponse?.content || '');
      }

      if (!apiKey) {
        throw new Error(
          "OpenAI API key bulunamadı ve proxy tanımlı değil. Dev ortamında `localStorage.openai_api_key` ile ekleyebilir veya `.env` içinde `REACT_APP_OPENAI_API_KEY` kullanabilirsiniz.",
        );
      }

      // Browser direct (CORS engeline takılabilir)
      const OpenAI = (await import('openai')).default;
      const openai = new OpenAI({ apiKey, dangerouslyAllowBrowser: true });
      const completion = await openai.chat.completions.create({
        model,
        messages,
        max_tokens,
        temperature,
      });
      return String(completion?.choices?.[0]?.message?.content || '');
    },
    [getOpenAIKey, getOpenAIProxyUrl, navigate],
  );

  const handleBulkUpdateScriptsFromComments = useCallback(async () => {
    if (isBulkAiGenerating || isBulkAiApplying) return;

    setBulkAiError(null);
    const proxyUrl = getOpenAIProxyUrl();
    const apiKey = getOpenAIKey();
    if (!apiKey && !proxyUrl) {
      setBulkAiError(
        "OpenAI API key bulunamadı ve proxy tanımlı değil. Önerilen: server-side `/openai-proxy.php` + OPENAI_API_KEY.",
      );
      setShowBulkAiModal(true);
      return;
    }

    const currentWorkflowData = getCurrentWorkflowDataForBulk();
    const contents = currentWorkflowData?.contents || [];
    const vertices = contents.filter(
      (it) => it && (it.vertex === '1' || it.vertex === 1 || it.vertex === true) && it.id != null,
    );

    const candidates = vertices
      .map((shapeData) => {
        const shapeId = String(shapeData.id);
        const oldCode = getScriptValueForShape(shapeId, shapeData);
        const lang = detectLanguageFromScript(oldCode);
        const instructions = extractInstructionComments(oldCode);
        const label = getShapeDisplayName(shapeId, shapeData);
        return {
          shapeId,
          label,
          lang,
          instructions,
          oldCode,
          newCode: '',
          status: instructions.length ? 'pending' : 'skipped',
          error: null,
        };
      })
      .filter((x) => x.status !== 'skipped');

    if (candidates.length === 0) {
      setBulkAiError(
        'Hiçbir şekilde “AI/TODO/FIXME/İSTEK/REQUEST …” gibi açık talimat içeren yorum satırı bulunamadı. (İpucu: script içine `// AI: ...` veya `// TODO: ...` yazın.)',
      );
      setBulkAiItems([]);
      setBulkAiSelected({});
      setBulkAiExpanded({});
      setShowBulkAiModal(true);
      return;
    }

    setBulkAiItems(candidates);
    setBulkAiExpanded({});
    setBulkAiSelected(Object.fromEntries(candidates.map((c) => [c.shapeId, true])));
    setShowBulkAiModal(true);
    setIsBulkAiGenerating(true);

    const model = process.env.REACT_APP_FLOWCHART_AI_MODEL || process.env.REACT_APP_OPENAI_MODEL || 'gpt-3.5-turbo';

    // Preflight: daha anlaşılır hata mesajı için tek bir küçük istek
    try {
      await callAI({
        model,
        messages: [{ role: 'user', content: 'ping' }],
        max_tokens: 1,
        temperature: 0,
      });
    } catch (err) {
      const status = err?.status || err?.code || '';
      const msg = err?.message || String(err);
      const cause = err?.cause?.message ? ` | cause: ${err.cause.message}` : '';
      setBulkAiError(`OpenAI bağlantı testi başarısız. model=${model}${status ? ` | status=${status}` : ''}\n${msg}${cause}`);
      setIsBulkAiGenerating(false);
      return;
    }

    for (const item of candidates) {
      setBulkAiItems((prev) =>
        prev.map((p) => (p.shapeId === item.shapeId ? { ...p, status: 'running', error: null } : p)),
      );

      try {
        const oldCode = item.oldCode || '';
        if (oldCode.length > 20000) {
          throw new Error('Script çok uzun (20000+ karakter). Önce kısaltın veya parçalayın.');
        }

        const instructionText = item.instructions.map((x) => `- ${x}`).join('\n');
        const userPrompt = `${instructionText}

Mevcut Kod:
${oldCode}`;

        const raw = await callAI({
          model,
          messages: [
            { role: 'user', content: userPrompt },
          ],
          temperature: 0.8,
          max_tokens: 2000,
        });
        const newCode = stripCodeFences(raw);

        setBulkAiItems((prev) =>
          prev.map((p) => {
            if (p.shapeId !== item.shapeId) return p;
            const changed = newCode && newCode.trim() && newCode.trim() !== (p.oldCode || '').trim();
            return { ...p, newCode: newCode || '', status: changed ? 'ready' : 'nochange', error: null };
          }),
        );
      } catch (err) {
        const status = err?.status || err?.code || '';
        const msg = err?.message || String(err);
        const cause = err?.cause?.message ? ` | cause: ${err.cause.message}` : '';
        setBulkAiItems((prev) =>
          prev.map((p) =>
            p.shapeId === item.shapeId
              ? {
                  ...p,
                  status: 'error',
                  error: `${msg}${status ? ` (status=${status})` : ''}${cause}`,
                }
              : p,
          ),
        );
      }
    }

    setIsBulkAiGenerating(false);
  }, [
    activeTabId,
    detectLanguageFromScript,
    extractInstructionComments,
    getCurrentWorkflowDataForBulk,
    getOpenAIKey,
    getScriptValueForShape,
    getShapeDisplayName,
    isBulkAiApplying,
    isBulkAiGenerating,
    stripCodeFences,
    tabCodeCacheRef,
    tabs,
    workflowData,
    // workflowDataRef çıkarıldı - ref'ler dependency'ye girmemeli
  ]);

  const handleApplyBulkAiUpdates = useCallback(async () => {
    if (isBulkAiGenerating || isBulkAiApplying) return;
    const toApply = bulkAiItems.filter(
      (it) => bulkAiSelected[it.shapeId] && (it.status === 'ready' || it.status === 'nochange') && it.newCode,
    );

    if (toApply.length === 0) {
      setBulkAiError('Uygulanacak güncelleme yok.');
      return;
    }

    setIsBulkAiApplying(true);
    setBulkAiError(null);


    for (const item of toApply) {
      setBulkAiItems((prev) =>
        prev.map((p) => (p.shapeId === item.shapeId ? { ...p, status: 'saving', error: null } : p)),
      );
      try {
        const updatedCode = String(item.newCode || '');

        shapeCodeCacheRef.current[item.shapeId] = updatedCode;
        if (activeTabId) {
          if (!tabCodeCacheRef.current[activeTabId]) tabCodeCacheRef.current[activeTabId] = {};
          tabCodeCacheRef.current[activeTabId][item.shapeId] = updatedCode;
        }

        if (selectedShape && String(selectedShape) === String(item.shapeId)) {
          isUpdatingEditorRef.current = true;
          setCode(updatedCode);
          if (editorRef.current) editorRef.current.setValue(updatedCode);
          setTimeout(() => {
            isUpdatingEditorRef.current = false;
          }, 100);
        }


        const saveResult = await saveScript(item.shapeId, updatedCode, selectedTicketId);
        if (!saveResult?.success) {
          throw new Error(saveResult?.error || 'Script kaydetme başarısız');
        }

        setBulkAiItems((prev) =>
          prev.map((p) => (p.shapeId === item.shapeId ? { ...p, status: 'saved', error: null } : p)),
        );
      } catch (err) {
        setBulkAiItems((prev) =>
          prev.map((p) =>
            p.shapeId === item.shapeId ? { ...p, status: 'error', error: err?.message || String(err) } : p,
          ),
        );
      }
    }

    setIsBulkAiApplying(false);
  }, [
    activeTabId,
    bulkAiItems,
    bulkAiSelected,
    editorRef,
    isBulkAiApplying,
    isBulkAiGenerating,
    isUpdatingEditorRef,
    saveScript,
    selectedShape,
    selectedTicketId,
    setCode,
    shapeCodeCacheRef,
    tabCodeCacheRef,
  ]);

  // ========== AI: COMPILE SCRIPTS FROM JSON ai_prompt + AI_GLOBAL ==========
  const [showCompileAiModal, setShowCompileAiModal] = useState(false);
  const [compileAiItems, setCompileAiItems] = useState([]);
  const [isCompileAiGenerating, setIsCompileAiGenerating] = useState(false);
  const [isCompileAiApplying, setIsCompileAiApplying] = useState(false);
  const [compileAiError, setCompileAiError] = useState(null);
  const [compileAiExpanded, setCompileAiExpanded] = useState({});
  const [compileAiSelected, setCompileAiSelected] = useState(() => ({}));

  const truncate = useCallback((text, max = 220) => {
    const t = String(text || '').trim();
    if (!t) return '';
    if (t.length <= max) return t;
    return `${t.slice(0, max)}…`;
  }, []);

  const normalizePhpTags = useCallback((code) => {
    const t = String(code || '').trim();
    // "kodu sil" gibi durumlarda gerçekten boş kalabilsin
    if (!t) return '';
    let out = t;
    if (!out.includes('<?php')) {
      out = `<?php\n${out}`;
    }
    if (!out.trim().endsWith('?>')) {
      out = `${out}\n?>`;
    }
    return out.trim();
  }, []);

  const validateGeneratedCodeAgainstPrompt = useCallback((prompt, code) => {
    const p = String(prompt || '').toLowerCase();
    const c = String(code || '');
    const issues = [];

    // 1) Genel değişken sayısı kontrolü (tamsayı, değişken vb.)
    const varCountMatch = p.match(/(\d+)\s*(adet\s*)?(değişken|degisken|tamsayı|tamsayi|integer|variable)/i);
    if (varCountMatch && varCountMatch[1]) {
      const expected = Math.max(0, parseInt(varCountMatch[1], 10) || 0);
      // PHP değişkenlerini say ($var şeklinde)
      const varMatches = c.match(/\$[a-zA-Z_][a-zA-Z0-9_]*/g) || [];
      // Unique değişkenleri al (goitem_id hariç)
      const uniqueVars = [...new Set(varMatches)].filter(v => v !== '$goitem_id');
      const actual = uniqueVars.length;
      if (expected > 0 && actual < expected) {
        issues.push(`Değişken sayısı: beklenen ${expected}, bulunan ${actual} (${uniqueVars.slice(0, 5).join(', ')}${uniqueVars.length > 5 ? '...' : ''})`);
      }
    }

    // 2) "N random değişken" kontrolü (rand/mt_rand sayısı)
    const randCountMatch = p.match(/(\d+)\s*(adet\s*)?(random|rastgele)\s*(değişken|degisken)/i);
    if (randCountMatch && randCountMatch[1]) {
      const expected = Math.max(0, parseInt(randCountMatch[1], 10) || 0);
      const actual =
        (c.match(/\brand\s*\(/g) || []).length +
        (c.match(/\bmt_rand\s*\(/g) || []).length;
      if (expected > 0 && actual < expected) {
        issues.push(`Random değişken sayısı: beklenen ${expected}, bulunan ${actual}`);
      }
    }

    // 3) karekök isteniyorsa sqrt olmalı
    if (p.includes('karekök') || p.includes('karekok') || p.includes('sqrt')) {
      if (!/\bsqrt\s*\(/i.test(c)) {
        issues.push('Karekök (sqrt) kullanılmamış');
      }
    }

    // 4) goitem_id kuralı isteniyorsa 10/20 setleri olmalı
    if (p.includes('goitem_id')) {
      if (!/\$goitem_id\s*=\s*10\s*;/i.test(c)) issues.push('$goitem_id=10 set edilmiyor');
      if (!/\$goitem_id\s*=\s*20\s*;/i.test(c)) issues.push('$goitem_id=20 set edilmiyor');
    }

    // 5) "X'ten büyükse/küçükse" basit threshold kontrolü (varsa)
    const gtMatch = p.match(/sonuç\s*(\d+)\s*(ten|dan|den|tan)?\s*büyükse/i) || p.match(/(\d+)\s*(ten|dan|den|tan)?\s*büyükse/i);
    if (gtMatch && gtMatch[1]) {
      const x = gtMatch[1];
      if (!new RegExp(`>\\s*${x}\\b`).test(c)) {
        issues.push(`Karşılaştırma eşiği (> ${x}) kodda bulunamadı`);
      }
    }
    const ltMatch = p.match(/sonuç\s*(\d+)\s*(ten|dan|den|tan)?\s*küçükse/i) || p.match(/(\d+)\s*(ten|dan|den|tan)?\s*küçükse/i);
    if (ltMatch && ltMatch[1]) {
      const x = ltMatch[1];
      if (!new RegExp(`<\\s*${x}\\b`).test(c)) {
        issues.push(`Karşılaştırma eşiği (< ${x}) kodda bulunamadı`);
      }
    }

    return issues;
  }, []);

  const handleCompileScriptsFromJsonPrompts = useCallback(async () => {
    if (isCompileAiGenerating || isCompileAiApplying) return;

    setCompileAiError(null);
    const proxyUrl = getOpenAIProxyUrl();
    const apiKey = getOpenAIKey();
    if (!apiKey && !proxyUrl) {
      setCompileAiError(
        "OpenAI API key bulunamadı ve proxy tanımlı değil. Önerilen: server-side `/openai-proxy.php` + OPENAI_API_KEY.",
      );
      setShowCompileAiModal(true);
      return;
    }

    const currentWorkflowData = getCurrentWorkflowDataForBulk();
    const contents = Array.isArray(currentWorkflowData?.contents) ? currentWorkflowData.contents : [];

    const steps = contents.filter((it) => it && (it.vertex === '1' || it.vertex === 1 || it.vertex === true) && it.id);
    const edges = contents.filter((it) => it && (it.edge === '1' || it.edge === 1 || it.edge === true));

    const stepsById = new Map(steps.map((s) => [String(s.id), s]));
    const outgoingBySource = new Map();
    const incomingByTarget = new Map();
    for (const e of edges) {
      const sourceId = e?.source != null ? String(e.source) : '';
      const targetId = e?.target != null ? String(e.target) : '';
      if (!sourceId || !targetId) continue;
      if (!outgoingBySource.has(sourceId)) outgoingBySource.set(sourceId, []);
      outgoingBySource.get(sourceId).push(e);
      if (!incomingByTarget.has(targetId)) incomingByTarget.set(targetId, []);
      incomingByTarget.get(targetId).push(e);
    }

    const workflowInfo = currentWorkflowData?.workflow || {};
    const globalRulesRaw = currentWorkflowData?.AI_GLOBAL || workflowData?.AI_GLOBAL || [];
    const globalRules = Array.isArray(globalRulesRaw)
      ? globalRulesRaw.map((x) => String(x || '').trim()).filter(Boolean)
      : [String(globalRulesRaw || '').trim()].filter(Boolean);

    const candidates = steps
      .map((shapeData) => {
        const shapeId = String(shapeData.id);
        const aiPrompt = String(shapeData.ai_prompt || '').trim();
        const oldCode = getScriptValueForShape(shapeId, shapeData);
        const langGuess = detectLanguageFromScript(oldCode);
        const lang = langGuess && langGuess !== 'unknown' ? langGuess : 'php';
        const label = getShapeDisplayName(shapeId, shapeData);
        
        // ✅ Token Optimizasyonu: Önceki compile'dan eski prompt var mı?
        const existingItem = compileAiItems.find(it => it.shapeId === shapeId);
        const hasExistingGeneration = existingItem && existingItem.originalPrompt && existingItem.newCode;
        const promptUnchanged = hasExistingGeneration && existingItem.originalPrompt === aiPrompt;
        const shouldSkipAI = promptUnchanged && existingItem.newCode;
        
        return {
          shapeId,
          label,
          lang,
          aiPrompt,
          originalPrompt: aiPrompt, // Şimdiki promptu kaydet
          globalRules,
          oldCode,
          newCode: shouldSkipAI ? existingItem.newCode : '', // ✅ Cached kodu kullan
          status: !aiPrompt ? 'skipped' : shouldSkipAI ? 'cached' : 'pending',
          error: null,
        };
      })
      .filter((x) => x.status !== 'skipped');

    if (candidates.length === 0) {
      setCompileAiError('Hiçbir step için `ai_prompt` bulunamadı.');
      setCompileAiItems([]);
      setCompileAiSelected({});
      setCompileAiExpanded({});
      setShowCompileAiModal(true);
      return;
    }

    // Modal'ı aç ve listeyi göster
    setCompileAiItems(candidates);
    setCompileAiExpanded({});
    setCompileAiSelected(Object.fromEntries(candidates.map((c) => [c.shapeId, true])));
    setShowCompileAiModal(true);
    
    // NOT: Modal sadece açıldı, henüz generation başlamadı
    // Kullanıcı modal'da promptları görebilir ve Generate'e bastığında
    // EN GÜNCEL promptlarla generation başlar
  }, [
    callAI,
    compileAiItems, // ✅ Token optimization için gerekli
    detectLanguageFromScript,
    getCurrentWorkflowDataForBulk,
    getOpenAIKey,
    getOpenAIProxyUrl,
    getScriptValueForShape,
    getShapeDisplayName,
    workflowData,
  ]);

  // Generate butonu için ayrı handler
  const handleStartCompileAiGeneration = useCallback(async () => {
    if (isCompileAiGenerating || isCompileAiApplying) return;

    setIsCompileAiGenerating(true);
    setCompileAiError(null);

    // EN GÜNCEL workflowData'yı oku
    const currentWorkflowData = getCurrentWorkflowDataForBulk();
    const contents = Array.isArray(currentWorkflowData?.contents) ? currentWorkflowData.contents : [];
    const steps = contents.filter((it) => it && (it.vertex === '1' || it.vertex === 1 || it.vertex === true) && it.id);
    const edges = contents.filter((it) => it && (it.edge === '1' || it.edge === 1 || it.edge === true));

    const stepsById = new Map(steps.map((s) => [String(s.id), s]));
    const outgoingBySource = new Map();
    const incomingByTarget = new Map();
    for (const e of edges) {
      const sourceId = e?.source != null ? String(e.source) : '';
      const targetId = e?.target != null ? String(e.target) : '';
      if (!sourceId || !targetId) continue;
      if (!outgoingBySource.has(sourceId)) outgoingBySource.set(sourceId, []);
      outgoingBySource.get(sourceId).push(e);
      if (!incomingByTarget.has(targetId)) incomingByTarget.set(targetId, []);
      incomingByTarget.get(targetId).push(e);
    }

    const workflowInfo = currentWorkflowData?.workflow || {};
    const globalRulesRaw = currentWorkflowData?.AI_GLOBAL || workflowData?.AI_GLOBAL || [];
    const globalRules = Array.isArray(globalRulesRaw)
      ? globalRulesRaw.map((x) => String(x || '').trim()).filter(Boolean)
      : [String(globalRulesRaw || '').trim()].filter(Boolean);

    // ✅ Mevcut listeyi GÜNCELLE (yeniden oluşturma!)
    // Modal açıldıktan sonra kullanıcı prompt değiştirmiş olabilir, EN GÜNCEL prompt'ları al
    const candidates = compileAiItems.map((existingItem) => {
      const shapeData = stepsById.get(existingItem.shapeId);
      if (!shapeData) return existingItem; // Shape silinmişse mevcut item'ı koru

      const aiPrompt = String(shapeData.ai_prompt || '').trim();
      const oldCode = getScriptValueForShape(existingItem.shapeId, shapeData);

      // Token Optimizasyonu: Prompt değişti mi kontrol et
      const promptChanged = existingItem.originalPrompt !== aiPrompt;
      
      // Eğer prompt değiştiyse status'u pending'e çevir
      if (promptChanged && existingItem.status === 'cached') {
        return {
          ...existingItem,
          aiPrompt,
          originalPrompt: aiPrompt, // Yeni promptu kaydet
          oldCode,
          status: aiPrompt ? 'pending' : 'skipped',
          error: null,
        };
      }

      // Prompt değişmediyse mevcut item'ı koru
      return {
        ...existingItem,
        aiPrompt, // En güncel promptu yükle
        oldCode, // En güncel kodu yükle
      };
    });

    // Listeyi güncelle
    setCompileAiItems(candidates);

    const model = process.env.REACT_APP_FLOWCHART_AI_MODEL || process.env.REACT_APP_OPENAI_MODEL || 'gpt-3.5-turbo';

    try {
      await callAI({
        model,
        messages: [{ role: 'user', content: 'ping' }],
        max_tokens: 1,
        temperature: 0,
      });
    } catch (err) {
      const status = err?.status || err?.code || '';
      const msg = err?.message || String(err);
      const cause = err?.cause?.message ? ` | cause: ${err.cause.message}` : '';
      setCompileAiError(
        `OpenAI bağlantı testi başarısız. model=${model}${status ? ` | status=${status}` : ''}\n${msg}${cause}`,
      );
      setIsCompileAiGenerating(false);
      return;
    }

    for (const item of candidates) {
      // ✅ Token Optimizasyonu: Cached (prompt değişmemiş) item'ları skip et
      if (item.status === 'cached') {
        continue;
      }

      setCompileAiItems((prev) =>
        prev.map((p) => (p.shapeId === item.shapeId ? { ...p, status: 'running', error: null } : p)),
      );

      try {
        // Deterministik: "kodu tamamen sil" gibi promptlarda AI'ya gitmeden script'i boşalt
        // TÜRKÇE KARAKTER DESTEĞİ: İ → i dönüşümü için toLocaleLowerCase('tr-TR') kullan
        const promptLower = (item.aiPrompt || '').toLocaleLowerCase('tr-TR');
        const shouldDeleteCode = 
          /kodu\s+tamamen\s+sil/.test(promptLower) ||
          /tamamen\s+sil/.test(promptLower) ||
          /delete\s+code/.test(promptLower) ||
          /remove\s+all\s+code/.test(promptLower) ||
          /clear\s+script/.test(promptLower) ||
          promptLower.includes('kodu sil') ||
          promptLower.includes('scripti sil') ||
          promptLower.includes('kodu tamamen sil');
        
        if (shouldDeleteCode) {
          const newCode = '';
          setCompileAiItems((prev) =>
            prev.map((p) => {
              if (p.shapeId === item.shapeId) {
                const hasOldCode = (p.oldCode || '').trim().length > 0;
                const newStatus = hasOldCode ? 'ready' : 'nochange';
                return { ...p, newCode, status: newStatus, error: null };
              }
              return p;
            }),
          );
          continue;
        }

        if ((item.oldCode || '').length > 20000) {
          throw new Error('Script çok uzun (20000+ karakter). Önce kısaltın veya parçalayın.');
        }

        const stepData = stepsById.get(item.shapeId);
        const outEdges = outgoingBySource.get(item.shapeId) || [];
        const inEdges = incomingByTarget.get(item.shapeId) || [];

        const outgoingText =
          outEdges.length === 0
            ? '— (terminal step / çıkış yok)'
            : outEdges
                .map((e) => {
                  const kosul = e?.kosul != null ? String(e.kosul) : '';
                  const targetId = e?.target != null ? String(e.target) : '';
                  const targetDef = stepsById.get(targetId)?.definition || targetId;
                  return `- kosul(goitem_id)=${kosul} => target=${targetDef} (${targetId})`;
                })
                .join('\n');

        const incomingText =
          inEdges.length === 0
            ? '—'
            : inEdges
                .map((e) => {
                  const kosul = e?.kosul != null ? String(e.kosul) : '';
                  const sourceId = e?.source != null ? String(e.source) : '';
                  const sourceDef = stepsById.get(sourceId)?.definition || sourceId;
                  const srcPrompt = truncate(stepsById.get(sourceId)?.ai_prompt || '', 140);
                  return `- source=${sourceDef} (${sourceId}) [kosul=${kosul}] prompt="${srcPrompt}"`;
                })
                .join('\n');

        const globalRulesText = globalRules.length ? globalRules.map((x) => `- ${x}`).join('\n') : '';

        const userPrompt = `AI_GLOBAL:
${globalRulesText}

AI_PROMPT:
${item.aiPrompt}

Mevcut Kod:
${item.oldCode || ''}`;

        // AI Request Log
        const raw = await callAI({
          model,
          messages: [
            { role: 'user', content: userPrompt },
          ],
          temperature: 1.0,
          max_tokens: 2200,
        });

        let newCode = stripCodeFences(raw);
        if (item.lang === 'php') newCode = normalizePhpTags(newCode);

        // AI Response Log

        // Prompt doğrulama + 1 kez retry
        const issues = validateGeneratedCodeAgainstPrompt(item.aiPrompt, newCode);
        if (issues.length) {
          const retryPrompt = userPrompt + `\n\nRETRY - Hatalar:\n${issues.map(e => `- ${e}`).join('\n')}`;
          
          // AI Request Log (Retry)
          
          const retryRaw = await callAI({
            model,
            messages: [
              {
                role: 'user',
                content: retryPrompt,
              },
            ],
            temperature: 1.0,
            max_tokens: 2200,
          });
          let retryCode = stripCodeFences(retryRaw);
          if (item.lang === 'php') retryCode = normalizePhpTags(retryCode);
          
          // AI Response Log (Retry)
          
          const retryIssues = validateGeneratedCodeAgainstPrompt(item.aiPrompt, retryCode);
          if (retryIssues.length === 0) {
            newCode = retryCode;
          } else {
            // yine başarısızsa item hata verelim ama yine de newCode'u gösterelim
            setCompileAiItems((prev) =>
              prev.map((p) =>
                p.shapeId === item.shapeId
                  ? { ...p, status: 'error', error: `Prompt doğrulama başarısız: ${retryIssues.join(' | ')}` }
                  : p,
              ),
            );
          }
        }

        setCompileAiItems((prev) =>
          prev.map((p) => {
            if (p.shapeId !== item.shapeId) return p;
            const newCodeTrim = (newCode || '').trim();
            const oldCodeTrim = (p.oldCode || '').trim();
            const changed = newCodeTrim && newCodeTrim !== oldCodeTrim;
            const status = changed ? 'ready' : 'nochange';
            
            // DEBUG: Status belirleme mantığını logla
            
            return { ...p, newCode: newCode || '', status, error: null };
          }),
        );
      } catch (err) {
        const status = err?.status || err?.code || '';
        const msg = err?.message || String(err);
        const cause = err?.cause?.message ? ` | cause: ${err.cause.message}` : '';
        setCompileAiItems((prev) =>
          prev.map((p) =>
            p.shapeId === item.shapeId
              ? { ...p, status: 'error', error: `${msg}${status ? ` (status=${status})` : ''}${cause}` }
              : p,
          ),
        );
      }
    }

    setIsCompileAiGenerating(false);
  }, [
    callAI,
    compileAiItems,
    detectLanguageFromScript,
    getCurrentWorkflowDataForBulk,
    getOpenAIKey,
    getOpenAIProxyUrl,
    getScriptValueForShape,
    getShapeDisplayName,
    isCompileAiApplying,
    isCompileAiGenerating,
    normalizePhpTags,
    stripCodeFences,
    truncate,
    validateGeneratedCodeAgainstPrompt,
    workflowData,
  ]);

  const handleApplyCompileAiUpdates = useCallback(async () => {
    if (isCompileAiGenerating || isCompileAiApplying) return;
    const toApply = compileAiItems.filter(
      (it) => compileAiSelected[it.shapeId] && (it.status === 'ready' || it.status === 'cached') && it.newCode,
    );
    if (toApply.length === 0) {
      setCompileAiError('Uygulanacak güncelleme yok.');
      return;
    }

    setIsCompileAiApplying(true);
    setCompileAiError(null);


    for (const item of toApply) {
      setCompileAiItems((prev) =>
        prev.map((p) => (p.shapeId === item.shapeId ? { ...p, status: 'saving', error: null } : p)),
      );
      try {
        const updatedCode = String(item.newCode || '');

        shapeCodeCacheRef.current[item.shapeId] = updatedCode;
        if (activeTabId) {
          if (!tabCodeCacheRef.current[activeTabId]) tabCodeCacheRef.current[activeTabId] = {};
          tabCodeCacheRef.current[activeTabId][item.shapeId] = updatedCode;
        }

        if (selectedShape && String(selectedShape) === String(item.shapeId)) {
          isUpdatingEditorRef.current = true;
          setCode(updatedCode);
          if (editorRef.current) editorRef.current.setValue(updatedCode);
          setTimeout(() => {
            isUpdatingEditorRef.current = false;
          }, 100);
        }


        const saveResult = await saveScript(item.shapeId, updatedCode, selectedTicketId);
        if (!saveResult?.success) {
          throw new Error(saveResult?.error || 'Script kaydetme başarısız');
        }

        setCompileAiItems((prev) =>
          prev.map((p) => (p.shapeId === item.shapeId ? { ...p, status: 'saved', error: null } : p)),
        );
      } catch (err) {
        setCompileAiItems((prev) =>
          prev.map((p) =>
            p.shapeId === item.shapeId ? { ...p, status: 'error', error: err?.message || String(err) } : p,
          ),
        );
      }
    }

    setIsCompileAiApplying(false);
  }, [
    activeTabId,
    compileAiItems,
    compileAiSelected,
    editorRef,
    isCompileAiApplying,
    isCompileAiGenerating,
    isUpdatingEditorRef,
    saveScript,
    selectedShape,
    selectedTicketId,
    setCode,
    shapeCodeCacheRef,
    tabCodeCacheRef,
  ]);
  // Save design mode to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('flowchart-design-mode', String(isDesignMode));
    } catch (error) {
      console.warn('Failed to save design mode:', error);
    }
  }, [isDesignMode]);
  
  
  // Shape definitions - mxGraph'in built-in shape'lerini kullan
  // Her şekil için flowchart algısına uygun renkler
  const shapes = useMemo(() => [
    { name: 'startCircle', label: 'Başlangıç', mxShape: 'ellipse', color: '#22c55e' }, // Yeşil
    { name: 'stopCircle', label: 'Bitiş', mxShape: 'ellipse', color: '#ef4444' }, // Kırmızı
    { name: 'rectangle', label: 'İşlem', mxShape: 'rectangle', color: '#3b82f6' }, // Mavi
    { name: 'preProcess', label: 'Alt İşlem', mxShape: 'predefinedProcess', color: '#60a5fa' }, // Açık mavi - çift kenarlı
    { name: 'rhombus', label: 'Karar', mxShape: 'rhombus', color: '#f59e0b' }, // Turuncu
    { name: 'triangle', label: 'Üçgen', mxShape: 'triangle', color: '#a855f7' }, // Mor - custom triangle
    { name: 'hexagon', label: 'Altıgen', mxShape: 'hexagon', color: '#8b5cf6' }, // Mor-altıgen
    { name: 'cylinder', label: 'Veri', mxShape: 'cylinder', color: '#06b6d4' }, // Turkuaz
  ], []);

  // ========== CODE EDITOR FUNCTIONS ==========
  const handleEditorDidMount = useCallback((editor, monaco) => {
    if (!editor || !monaco) return;
    
    editorRef.current = editor;
    monacoRef.current = monaco;
    
    // Editor container'ına data attribute ekle - keyboard event kontrolü için
    try {
      const editorDomNode = editor.getDomNode();
      if (editorDomNode) {
        editorDomNode.setAttribute('data-monaco-editor', 'true');
      }
    } catch (error) {
    }

    // AI Completion Provider'ı kaydet
    const apiKey = getOpenAIKey();
    if (apiKey) {
      setAiStatus({ enabled: true, loading: false, error: null });
      
      const completionProvider = {
        provideCompletionItems: async (model, position) => {
          try {
            const textUntilPosition = model.getValueInRange({
              startLineNumber: 1,
              startColumn: 1,
              endLineNumber: position.lineNumber,
              endColumn: position.column,
            });

            // AI önerilerini al
            const aiSuggestions = await getAICodeSuggestions(textUntilPosition, position);
            
            return {
              suggestions: aiSuggestions,
            };
          } catch (error) {
            return { suggestions: [] };
          }
        },
        triggerCharacters: ['.', '(', '$', '>', ' ', '\n'],
      };

      monaco.languages.registerCompletionItemProvider('php', completionProvider);
    } else {
      setAiStatus({ enabled: false, loading: false, error: 'OpenAI API key bulunamadı. Lütfen REACT_APP_OPENAI_API_KEY environment variable\'ını ayarlayın veya localStorage\'a "openai_api_key" olarak kaydedin.' });
    }
    
    setTimeout(() => {
      if (editor && editor.focus) {
        editor.focus();
      }
    }, 100);
  }, [getOpenAIKey, getAICodeSuggestions]);

  const handleCodeChange = useCallback((value) => {
    // Eğer editor programatik olarak güncelleniyorsa, onChange'i ignore et
    if (isUpdatingEditorRef.current) {
      return;
    }
    
    // Editor'dan gelen değişiklikleri state'e kaydet
    const newValue = value || '';
    
    setCode(newValue);
    
    // Tab bazlı cache'e kaydet (her değişiklikte) - ÖNEMLİ!
    if (selectedShape && activeTabId) {
      if (!tabCodeCacheRef.current[activeTabId]) {
        tabCodeCacheRef.current[activeTabId] = {};
      }
      tabCodeCacheRef.current[activeTabId][selectedShape] = newValue;
    } else {
    }
    
    // Eski cache'e de kaydet (geriye dönük uyumluluk için)
    if (selectedShape) {
      shapeCodeCacheRef.current[selectedShape] = newValue;
    }
  }, [selectedShape, activeTabId]);

  const handleSaveScript = useCallback(async () => {
    if (!selectedShape || !code) return;
    
    setIsSavingScript(true);
    setSaveScriptStatus(null);
    
    try {
      const result = await saveScript(selectedShape, code);
      if (result.success) {
        setSaveScriptStatus({ type: 'success', message: 'Script başarıyla kaydedildi!' });
        setTimeout(() => setSaveScriptStatus(null), 3000);
      } else {
        setSaveScriptStatus({ type: 'error', message: result.error || 'Script kaydetme başarısız!' });
      }
    } catch (error) {
      setSaveScriptStatus({ type: 'error', message: error.message || 'Script kaydetme başarısız!' });
    } finally {
      setIsSavingScript(false);
    }
  }, [selectedShape, code, saveScript]);

  const appendTerminalLine = useCallback((line) => {
    const safeLine = {
      id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
      type: 'system',
      text: '',
      ...line,
    };
    setTerminalLines((prev) => [...prev, safeLine]);
  }, []);

  const handleTerminalClear = useCallback(() => {
    setTerminalLines([
      { id: 'ready', type: 'system', text: '$ ready' },
      { id: 'hint', type: 'muted', text: '# AI terminal temizlendi' },
    ]);
  }, []);

  useEffect(() => {
    if (!showTerminal) return;
    if (!selectedShape) {
      appendTerminalLine({ type: 'muted', text: '# Şekil seçilmedi' });
      return;
    }
    appendTerminalLine({
      type: 'system',
      text: `# Seçili şekil: ${selectedShapeLabel || selectedShape}`,
    });
  }, [showTerminal, selectedShape, selectedShapeLabel, appendTerminalLine]);

  useEffect(() => {
    if (!pendingChange) return;
    if (selectedShape && pendingChange.shapeId !== selectedShape) {
      setPendingChange(null);
      appendTerminalLine({ type: 'muted', text: '# Şekil değişti, önizleme iptal edildi.' });
    }
  }, [pendingChange, selectedShape, appendTerminalLine]);

  const handleTerminalSubmit = useCallback(async () => {
    const prompt = terminalInput.trim();
    if (!prompt) return;

    appendTerminalLine({ type: 'user', text: `> ${prompt}` });
    setTerminalInput('');

    if (!selectedShape) {
      appendTerminalLine({ type: 'error', text: '! Önce bir şekil seçmelisin.' });
      return;
    }

    const apiKey = getOpenAIKey();
    if (!apiKey) {
      appendTerminalLine({
        type: 'error',
        text: '! OpenAI API key bulunamadı. Ayarlarını kontrol et.',
      });
      return;
    }

    setIsTerminalBusy(true);
    appendTerminalLine({ type: 'system', text: '# Kod güncelleniyor…' });

    try {
      const currentCode = code || '';
      const updatedCode = await generateCodeFromPrompt(prompt, currentCode, 'modify', currentCode);

      if (!updatedCode) {
        appendTerminalLine({ type: 'error', text: '! Kod üretilemedi. Lütfen tekrar dene.' });
        return;
      }

      setPendingChange({
        shapeId: selectedShape,
        shapeLabel: selectedShapeLabel || selectedShape,
        oldCode: currentCode,
        newCode: updatedCode,
        prompt,
      });
      isUpdatingEditorRef.current = true;
      setCode(updatedCode);
      if (editorRef.current) {
        editorRef.current.setValue(updatedCode);
      }
      setTimeout(() => {
        isUpdatingEditorRef.current = false;
      }, 100);

      appendTerminalLine({ type: 'ai', text: '✓ Önizleme hazır. Keep/Undo ile onayla.' });
    } catch (error) {
      appendTerminalLine({
        type: 'error',
        text: `! İşlem hatası: ${error?.message || 'Bilinmeyen hata'}`,
      });
    } finally {
      setIsTerminalBusy(false);
    }
  }, [
    terminalInput,
    appendTerminalLine,
    selectedShape,
    selectedShapeLabel,
    getOpenAIKey,
    generateCodeFromPrompt,
    code,
  ]);

  /* ── Agent panel'den tek şekil için AI kodu üret ── */
  const handleRunShapeAI = useCallback(async (prompt) => {
    if (!prompt?.trim() || !selectedShape) return;
    const apiKey = getOpenAIKey();
    if (!apiKey) {
      appendTerminalLine({ type: 'error', text: '! OpenAI API key bulunamadı.' });
      return;
    }
    setIsTerminalBusy(true);
    appendTerminalLine({ type: 'system', text: `# "${selectedShapeLabel || selectedShape}" için AI kodu üretiliyor…` });
    try {
      const currentCode = code || '';
      const updatedCode = await generateCodeFromPrompt(prompt.trim(), currentCode, 'modify', currentCode);
      if (!updatedCode) {
        appendTerminalLine({ type: 'error', text: '! Kod üretilemedi. Lütfen tekrar dene.' });
        return;
      }
      setPendingChange({
        shapeId: selectedShape,
        shapeLabel: selectedShapeLabel || selectedShape,
        oldCode: currentCode,
        newCode: updatedCode,
        prompt: prompt.trim(),
      });
      isUpdatingEditorRef.current = true;
      setCode(updatedCode);
      if (editorRef.current) editorRef.current.setValue(updatedCode);
      setTimeout(() => { isUpdatingEditorRef.current = false; }, 100);
      appendTerminalLine({ type: 'ai', text: '✓ Önizleme hazır. Keep ile onayla.' });
    } catch (err) {
      appendTerminalLine({ type: 'error', text: `! Hata: ${err?.message || 'Bilinmeyen hata'}` });
    } finally {
      setIsTerminalBusy(false);
    }
  }, [
    selectedShape, selectedShapeLabel, getOpenAIKey, generateCodeFromPrompt,
    code, appendTerminalLine, setPendingChange, setCode,
  ]);

  const handleApplyPendingChange = useCallback(async () => {
    if (!pendingChange || !pendingChange.newCode || !pendingChange.shapeId) return;
    if (pendingChange.shapeId !== selectedShape) {
      appendTerminalLine({ type: 'error', text: '! Değişiklik başka bir şekle ait.' });
      return;
    }

    setIsApplyingChange(true);
    try {
      const updatedCode = pendingChange.newCode;
      isUpdatingEditorRef.current = true;
      setCode(updatedCode);
      if (editorRef.current) {
        editorRef.current.setValue(updatedCode);
      }

      shapeCodeCacheRef.current[selectedShape] = updatedCode;
      if (selectedShape && activeTabId) {
        if (!tabCodeCacheRef.current[activeTabId]) {
          tabCodeCacheRef.current[activeTabId] = {};
        }
        tabCodeCacheRef.current[activeTabId][selectedShape] = updatedCode;
      }

      setTimeout(() => {
        isUpdatingEditorRef.current = false;
      }, 100);

      const saveResult = await saveScript(selectedShape, updatedCode);
      if (saveResult.success) {
        appendTerminalLine({ type: 'ai', text: '✓ Script güncellendi ve kaydedildi.' });
        setPendingChange(null);
      } else {
        appendTerminalLine({
          type: 'error',
          text: `! Kaydetme başarısız: ${saveResult.error || 'Bilinmeyen hata'}`,
        });
      }
    } catch (error) {
      appendTerminalLine({
        type: 'error',
        text: `! İşlem hatası: ${error?.message || 'Bilinmeyen hata'}`,
      });
    } finally {
      setIsApplyingChange(false);
    }
  }, [
    pendingChange,
    selectedShape,
    activeTabId,
    appendTerminalLine,
    saveScript,
  ]);

  const handleDiscardPendingChange = useCallback(() => {
    if (!pendingChange) return;
    const previousCode = pendingChange.oldCode || '';
    isUpdatingEditorRef.current = true;
    setCode(previousCode);
    if (editorRef.current) {
      editorRef.current.setValue(previousCode);
    }
    setTimeout(() => {
      isUpdatingEditorRef.current = false;
    }, 100);
    setPendingChange(null);
    appendTerminalLine({ type: 'muted', text: '# Değişiklik iptal edildi.' });
  }, [pendingChange, appendTerminalLine]);

  const handleNewScript = useCallback(() => {
    const newScript = `<?php\n// Yeni script oluşturuldu\n// Tarih: ${new Date().toLocaleDateString('tr-TR')}\n\nfunction processData() {\n    // Kodunuzu buraya yazın\n    echo "Merhaba Dünya!";\n}\n\n// Ana işlem\nprocessData();\n?>`;
    isUpdatingEditorRef.current = true;
    setCode(newScript);
    if (editorRef.current) {
      editorRef.current.setValue(newScript);
    }
    // Tab bazlı cache'e kaydet
    if (selectedShape && activeTabId) {
      if (!tabCodeCacheRef.current[activeTabId]) {
        tabCodeCacheRef.current[activeTabId] = {};
      }
      tabCodeCacheRef.current[activeTabId][selectedShape] = newScript;
    }
    // Eski cache'e de kaydet (geriye dönük uyumluluk için)
    if (selectedShape) {
      shapeCodeCacheRef.current[selectedShape] = newScript;
    }
    setTimeout(() => {
      isUpdatingEditorRef.current = false;
    }, 100);
  }, [selectedShape, activeTabId]);

  // Load script when shape is selected or tab is changed
  useEffect(() => {
    const previousShape = previousSelectedShapeRef.current;
    const previousTabId = previousActiveTabIdRef.current;
    
    // Tab veya şekil değiştiyse önceki şeklin kodunu cache'e kaydet
    const shapeChanged = previousShape !== selectedShape;
    const tabChanged = previousTabId !== activeTabId;
    
    // Önceki şeklin kodunu cache'e kaydet (eğer editor'da değişiklik yapıldıysa)
    // ÖNEMLİ: Bu işlemi yeni şekle/tab'a geçmeden ÖNCE yapmalıyız
    // ÖNEMLİ: Her zaman editor'dan al, cache'den değil - çünkü editor en güncel değeri tutar
    if (previousShape && previousTabId) {
      let valueToCache = '';
      
      // Önce cache'deki mevcut değeri kontrol et
      const existingCacheValue = tabCodeCacheRef.current[previousTabId] && 
        tabCodeCacheRef.current[previousTabId][previousShape] !== undefined
        ? tabCodeCacheRef.current[previousTabId][previousShape]
        : null;
      
      // Her zaman editor'dan al - en güncel değer orada
      if (editorRef.current) {
        try {
          const currentEditorValue = editorRef.current.getValue();
          const editorValue = currentEditorValue !== undefined && currentEditorValue !== null 
            ? String(currentEditorValue) 
            : '';
          
          // ÖNEMLİ: Eğer editor boşsa ama cache'de değer varsa, cache'deki değeri koru
          if (editorValue === '' && existingCacheValue !== null && existingCacheValue !== undefined && existingCacheValue !== '') {
            valueToCache = String(existingCacheValue);
          } else {
            valueToCache = editorValue;
          }
        } catch (error) {
          // Hata durumunda tab bazlı cache'den al
          if (existingCacheValue !== null && existingCacheValue !== undefined) {
            valueToCache = String(existingCacheValue);
          } else {
            valueToCache = '';
          }
        }
      } else {
        // Editor yoksa tab bazlı cache'den al
        if (existingCacheValue !== null && existingCacheValue !== undefined) {
          valueToCache = String(existingCacheValue);
        } else {
          valueToCache = '';
        }
      }
      
      // Tab bazlı cache'e kaydet (boş string bile olsa kaydet) - ÖNEMLİ: Her zaman kaydet!
      if (!tabCodeCacheRef.current[previousTabId]) {
        tabCodeCacheRef.current[previousTabId] = {};
      }
      tabCodeCacheRef.current[previousTabId][previousShape] = valueToCache;
      
      // Eski cache'e de kaydet (geriye dönük uyumluluk için)
      shapeCodeCacheRef.current[previousShape] = valueToCache;
    } else {
    }
    
    // Tab değiştiyse ve şekil değişmediyse, sadece tab bazlı cache'den yükle
    if (tabChanged && !shapeChanged && selectedShape && activeTabId) {
      // Tab bazlı cache'i başlat
      if (!tabCodeCacheRef.current[activeTabId]) {
        tabCodeCacheRef.current[activeTabId] = {};
      }
      
      // Tab bazlı cache'den oku
      const cachedValue = tabCodeCacheRef.current[activeTabId][selectedShape];
      let scriptValue = '';
      
      if (cachedValue !== undefined) {
        scriptValue = cachedValue !== null ? String(cachedValue) : '';
      } else {
        // Cache'de yoksa workflowData'dan oku
        const activeTab = tabs.find(tab => tab.id === activeTabId);
        const currentWorkflowData = activeTab?.workflowData || workflowDataRef.current;
        const shapeData = currentWorkflowData?.contents?.find(item => item.id === selectedShape);
        const script = shapeData?.script || '';
        scriptValue = typeof script === 'string' ? script : '';
        
        // Tab bazlı cache'e kaydet
        tabCodeCacheRef.current[activeTabId][selectedShape] = scriptValue;
        shapeCodeCacheRef.current[selectedShape] = scriptValue;
      }
      
      // Editor'ü güncelle
      if (editorRef.current) {
        isUpdatingEditorRef.current = true;
        editorRef.current.setValue(scriptValue);
        setTimeout(() => {
          isUpdatingEditorRef.current = false;
        }, 300);
      }
      
      setCode(scriptValue);
      previousSelectedShapeRef.current = selectedShape;
      previousActiveTabIdRef.current = activeTabId;
      return;
    }
    
    // Sadece şekil değiştiyse ve tab aynıysa, normal akışı devam ettir
    if (!shapeChanged && !tabChanged) {
      return;
    }
    
    // Yeni şekle/tab'a geç
    // ÖNEMLİ: Ref'leri güncellemeden önce, eğer selectedShape null ise ve tab değiştiyse,
    // önceki tab'daki şeklin kodunu cache'e kaydetmiş olmalıyız (handleTabSwitch içinde yapıldı)
    previousSelectedShapeRef.current = selectedShape;
    previousActiveTabIdRef.current = activeTabId;
    
    const currentWorkflowData = workflowDataRef.current || workflowData;
    if (!selectedShape || !currentWorkflowData?.contents) {
      // selectedShape null ise editor'ı temizle
      isUpdatingEditorRef.current = true;
      setCode('');
      if (editorRef.current) {
        editorRef.current.setValue('');
      }
      setTimeout(() => {
        isUpdatingEditorRef.current = false;
      }, 100);
      return;
    }
    
    // Önce tab bazlı cache'den kontrol et, yoksa workflowData'dan oku
    let scriptValue = '';
    
    // Tab bazlı cache'i başlat
    if (!tabCodeCacheRef.current[activeTabId]) {
      tabCodeCacheRef.current[activeTabId] = {};
    }
    
    // Tab bazlı cache'de varsa cache'den al (undefined kontrolü ile)
    const cachedValue = activeTabId && tabCodeCacheRef.current[activeTabId] 
      ? tabCodeCacheRef.current[activeTabId][selectedShape] 
      : undefined;
    
    if (cachedValue !== undefined) {
      // Cache'de var - cache'den al (boş string bile olsa cache'den al)
      scriptValue = cachedValue !== null ? String(cachedValue) : '';
    } else {
      // Cache'de yoksa workflowData'dan oku
      const shapeData = currentWorkflowData.contents.find(item => item.id === selectedShape);
      const script = shapeData?.script || '';
      scriptValue = typeof script === 'string' ? script : '';
    
      // İlk yüklemede tab bazlı cache'e kaydet (boş string bile olsa kaydet)
      if (activeTabId) {
        tabCodeCacheRef.current[activeTabId][selectedShape] = scriptValue;
      }
      
      // Eski cache'e de kaydet (geriye dönük uyumluluk için)
      shapeCodeCacheRef.current[selectedShape] = scriptValue;
    }
    
    // Editor'ü güncelle - cache'deki değeri kullan (her zaman!)
    if (editorRef.current) {
      const currentValue = editorRef.current.getValue();
      // ÖNEMLİ: Cache'deki değeri koru - editor güncellemesinden önce
      const cachedValueBeforeUpdate = shapeCodeCacheRef.current[selectedShape];
      
      // Flag'i set et - onChange'i engellemek için
      isUpdatingEditorRef.current = true;
      
      // Her zaman cache'deki değeri kullan (önemli!)
        editorRef.current.setValue(scriptValue);
      
      // ÖNEMLİ: Tab bazlı cache'i koru - eğer editor güncellemesi cache'i boşaltırsa geri yükle
      if (cachedValueBeforeUpdate !== undefined && activeTabId) {
        if (!tabCodeCacheRef.current[activeTabId]) {
          tabCodeCacheRef.current[activeTabId] = {};
        }
        tabCodeCacheRef.current[activeTabId][selectedShape] = cachedValueBeforeUpdate;
      }
      
      // Flag'i reset et - setTimeout ile onChange'in tamamlanmasını bekle
      // Daha uzun bir timeout kullanarak onChange'in kesinlikle engellendiğinden emin ol
      setTimeout(() => {
        // Flag resetlenmeden önce tab bazlı cache'i tekrar kontrol et ve koru
        if (activeTabId && tabCodeCacheRef.current[activeTabId]) {
          const currentCacheValue = tabCodeCacheRef.current[activeTabId][selectedShape];
          if (cachedValueBeforeUpdate !== undefined && currentCacheValue !== cachedValueBeforeUpdate) {
            tabCodeCacheRef.current[activeTabId][selectedShape] = cachedValueBeforeUpdate;
          }
        }
        
        isUpdatingEditorRef.current = false;
      }, 300);
      
        // Cursor pozisyonunu koru
        const position = editorRef.current.getPosition();
        if (position) {
          setTimeout(() => {
          if (editorRef.current) {
            editorRef.current.setPosition(position);
          }
          }, 10);
        }
    }
    
    // State'i güncelle - cache'deki değeri kullan
    setCode(scriptValue);
  }, [selectedShape, activeTabId, tabs]); // code dependency'sini kaldırdık - cache'i bozuyordu

  // Seçili şeklin rengini ve label'ını güncelle
  useEffect(() => {
    if (!selectedShape || !graphRef.current) {
      setSelectedShapeLabel('');
      return;
    }
    
    const graph = graphRef.current;
    const model = graph.getModel();
    const cell = model.getCell(selectedShape);
    
    if (cell && model.isVertex(cell)) {
      const style = cell.getStyle() || '';
      const fillColorMatch = style.match(/fillColor=([^;]+)/);
      if (fillColorMatch) {
        setSelectedShapeColor(fillColorMatch[1]);
      } else {
        setSelectedShapeColor('#FFFF00');
      }
      
      // Şeklin label'ını al
      const cellValue = cell.getValue();
      const label = cellValue ? String(cellValue).trim() : '';
      setSelectedShapeLabel(label || selectedShape);
    } else {
      setSelectedShapeLabel('');
    }
  }, [selectedShape]);

  const normalizeIncludeId = useCallback((value) => {
    if (value === null || value === undefined) {
      return '';
    }
    const str = String(value).trim();
    if (!str || str === 'null') {
      return '';
    }
    return str;
  }, []);

  const openIncludeDetails = useCallback((shapeId, options = {}) => {
    const { showPopup = false } = options;
    const currentWorkflowData = workflowDataRef.current || workflowData;
    const nodeData = currentWorkflowData?.contents?.find(item =>
      item && String(item.id) === String(shapeId)
    );
    const includeValue = normalizeIncludeId(
      shapeIncludePathsRef.current?.[shapeId] ??
      nodeData?.include ??
      nodeData?.diagrun_id ??
      nodeData?.fileId
    );
    const foldername =
      shapeIncludeFoldersRef.current?.[shapeId] ||
      nodeData?.foldername ||
      nodeData?.folderName ||
      '';
    const filename =
      nodeData?.filename ||
      nodeData?.fileName ||
      nodeData?.name ||
      '';
    if (includeValue) {
      handleFileClick({
        diagrun_id: includeValue,
        name: filename || `Include ${includeValue}`,
      });
    }
    if (showPopup) {
      setIncludeInfoData({
        shapeId,
        include: includeValue,
        foldername,
        filename,
      });
      setShowIncludeInfoModal(true);
    }
  }, [handleFileClick, normalizeIncludeId, workflowData]);
  

  // Move Request
  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  }, []);

  const handleMoveRequest = useCallback(async () => {
    const ticid = selectedTicketId || workflowData?.TICID;
    const flowid = workflowData?.workflow?.id || workflowData?.FLOWID || '';
    const sirano = workflowData?.sirano ?? 0;

    if (!ticid || !flowid) {
      showToast('error', 'TICID veya FLOWID bulunamadı!');
      return;
    }

    const eventData = {
      inputName: 'SELECT',
      message: '-requestmove yes',
      data: JSON.stringify({ ticid: String(ticid), flowid: String(flowid), sirano }),
    };

    try {
      await SendEvent(eventData, [], 'FlowChartDevelopmentScreen-MoveRequest');
      showToast('success', 'Move Request başarıyla gönderildi.');
    } catch {
      showToast('error', 'Move Request gönderilemedi. Lütfen tekrar deneyin.');
    }
  }, [workflowData, selectedTicketId, SendEvent, showToast]);

  // Fx Groups API çağrısı
  const fetchFxGroups = useCallback(async () => {
    setIsLoadingFxGroups(true);
    setFxGroupsError(null);
    setFxGroups([]);
    setSelectedFxGroup(null);
    setFunctions([]);
    setFunctionsError(null);
    setSelectedFunction(null);
    setShowFunctionsModal(false);
    try {
      const processId = process.env.REACT_APP_GET_FX_GROUP_LIST || '100023235';
      const bodyParams = {
        process: '100023235',
      };
      
      const response = await fetchWrapper(processId, bodyParams, navigate);
      
      // Response'u kontrol et
      if (response && response.result && response.result.contents) {
        setFxGroups(response.result.contents);
        setIsLoadingFxGroups(false);
        setShowFxGroupsModal(true);
      } else {
        throw new Error(response?.error || response?.message || 'Fx groups alınamadı');
      }
    } catch (error) {
      setFxGroupsError(error.message || 'Fx groups yüklenirken bir hata oluştu');
      setIsLoadingFxGroups(false);
    }
  }, [navigate, workflowData?.REQID]);

  // Functions API çağrısı (Fx Group seçildiğinde)
  const fetchFunctions = useCallback(async (fxGroupName) => {
    setIsLoadingFunctions(true);
    setFunctionsError(null);
    setFunctions([]);
    
    try {      
      // fxgroup: Son "_" karakterinden sonraki kısmı al
      // Örnek: "Function_Create_DR" -> "DR"
      const fxGroupShort = fxGroupName.includes('_') 
        ? fxGroupName.split('_').pop() 
        : fxGroupName;
      
      const processId = process.env.REACT_APP_GET_FX_LIST || '100023236';
      const bodyParams = {
        process: '100023236',
        fxgroup: fxGroupShort,
      };
      
      const response = await fetchWrapper(processId, bodyParams, navigate);
      
      // Response'u kontrol et
      if (response && response.result && response.result.contents) {
        setFunctions(response.result.contents);
        setSelectedFxGroup(fxGroupName);
        setIsLoadingFunctions(false);
        // Fx Groups modal'ını kapat ve Functions modal'ını aç
        setShowFxGroupsModal(false);
        setShowFunctionsModal(true);
      } else {
        throw new Error(response?.error || response?.message || 'Functions alınamadı - response.result.contents yok');
      }
    } catch (error) {
      setFunctionsError(error.message || 'Functions yüklenirken bir hata oluştu');
      setIsLoadingFunctions(false);
      // Hata durumunda Functions modal'ını aç ki kullanıcı hatayı görsün
      setShowFxGroupsModal(false);
      setShowFunctionsModal(true);
    }
  }, [navigate]);

  // Function Script gösterimi (Function seçildiğinde) - Draggable Window olarak aç
  const fetchFunctionScript = useCallback((func) => {
    if (!func || !func.fname) {
      return;
    }
    
    // Function objesinden script'i al
    const script = func.script || '';
    const fname = func.fname;
    
    // Aynı function zaten açık mı kontrol et
    const existingWindow = functionWindows.find(w => w.functionName === fname);
    if (existingWindow) {
      // Zaten açıksa, o window'u öne getir
      setFunctionWindows(prev => {
        const filtered = prev.filter(w => w.id !== existingWindow.id);
        return [...filtered, { ...existingWindow, isMinimized: false }];
      });
      setShowFunctionsModal(false);
      return;
    }
    
    // Maksimum 5 window kontrolü
    if (functionWindows.length >= 5) {
      alert('Maksimum 5 function window açabilirsiniz. Önce bir tanesini kapatın.');
      return;
    }
    
    // Yeni window oluştur
    const windowId = `func-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newWindow = {
      id: windowId,
      functionName: fname,
      script: script,
      position: { 
        x: 100 + (functionWindows.length * 30), 
        y: 100 + (functionWindows.length * 30) 
      },
      width: 800,
      height: 600,
      isMinimized: false,
      originalScript: script, // Save için original script'i sakla
      saveStatus: null, // { state: 'saving' | 'success' | 'error', message: string }
    };
    
    setFunctionWindows(prev => [...prev, newWindow]);
    setShowFunctionsModal(false);
  }, [functionWindows]);

  // Window kapatma
  const closeFunctionWindow = useCallback((windowId) => {
    setFunctionWindows(prev => prev.filter(w => w.id !== windowId));
    // Editor ref'i temizle
    if (functionWindowEditorRefs.current[windowId]) {
      delete functionWindowEditorRefs.current[windowId];
    }
  }, []);

  // Window minimize/maximize
  const toggleMinimizeWindow = useCallback((windowId) => {
    setFunctionWindows(prev => 
      prev.map(w => {
        if (w.id !== windowId) return w;
        
        if (w.isMinimized) {
          // Maximize: Eski boyutlara dön
          return { ...w, isMinimized: false };
        } else {
          // Minimize: Mevcut boyutları sakla ve çubuk haline getir
          return { 
            ...w, 
            isMinimized: true,
            // Boyutlar zaten w.width ve w.height'ta saklı
          };
        }
      })
    );
  }, []);

  // Window save
  const saveFunctionWindow = useCallback(async (windowId) => {
    const window = functionWindows.find(w => w.id === windowId);
    if (!window) return;
    
    const editorRef = functionWindowEditorRefs.current[windowId];
    if (!editorRef || !editorRef.current) {
      alert('Editor bulunamadı');
      return;
    }
    
    const currentScript = editorRef.current.getValue();

    setFunctionWindows(prev =>
      prev.map(w =>
        w.id === windowId
          ? { ...w, saveStatus: { state: 'saving', message: 'Kaydediliyor...' } }
          : w
      )
    );

    const processId = process.env.REACT_APP_DIAGRUN_FX_UPDATE;
    if (!processId) {
      alert('REACT_APP_DIAGRUN_FX_UPDATE tanımlı değil');
      return;
    }

    const bodyParams = {
      TICID: selectedTicketId || workflowData?.TICID || null,
      fxname: window.functionName,
      script: currentScript,
      space: 'jubi',
      REQID: workflowData?.REQID || null,
      process: processId,
    };

    try {
      const response = await fetchWrapper(processId, bodyParams, navigate);

      const isSuccess =
        response?.result === true ||
        response?.success === true ||
        response?.result?.sy_subrc === 0;

      if (isSuccess) {
        // Original script'i güncelle
        setFunctionWindows(prev =>
          prev.map(w =>
            w.id === windowId
              ? {
                  ...w,
                  originalScript: currentScript,
                  saveStatus: { state: 'success', message: response?.result?.sy_message || 'Kaydedildi' }
                }
              : w
          )
        );
        setTimeout(() => {
          setFunctionWindows(prev =>
            prev.map(w =>
              w.id === windowId ? { ...w, saveStatus: null } : w
            )
          );
        }, 3000);
        return;
      }

      throw new Error(
        response?.result?.sy_message ||
        response?.error ||
        response?.message ||
        'Kaydetme başarısız'
      );
    } catch (error) {
      setFunctionWindows(prev =>
        prev.map(w =>
          w.id === windowId
            ? { ...w, saveStatus: { state: 'error', message: error.message || 'Kaydetme hatası' } }
            : w
        )
      );
    }
  }, [functionWindows, navigate, selectedTicketId, workflowData]);

  const handleSave = useCallback(async () => {
    if (!graphRef.current) return;
    
    setIsSaving(true);
    setSaveStatus(null);

    // Kaydetmeden önce aktif tab'ın graph snapshot'ını diğer tab'lara da yansıt
    if (activeTabId && graphRef.current) {
      const _graph = graphRef.current;
      const _model = _graph.getModel();
      const _cells = _graph.getChildCells(_graph.getDefaultParent());
      if (_cells && _cells.length > 0) {
        const snap = _cells.map((c) => {
          const iv = _model.isVertex(c);
          const g = c.getGeometry();
          const r = {
            id: c.getId() ? String(c.getId()) : '',
            vertex: iv ? '1' : '0',
            edge: !iv ? '1' : '0',
            definition: _model.getValue(c) ? String(_model.getValue(c)) : '',
            GeometryX: g.x, GeometryY: g.y, GeometryWidth: g.width, GeometryHeight: g.height,
            source: !iv && c.getTerminal(true) ? String(c.getTerminal(true).getId()) : null,
            target: !iv && c.getTerminal(false) ? String(c.getTerminal(false).getId()) : null,
            style: c.getStyle() ? String(c.getStyle()) : '',
          };
          if (!iv) {
            const lbl = r.definition;
            if (lbl && lbl.includes(':Next')) { const km = lbl.match(/(\d+):Next/); if (km) r.kosul = km[1]; }
            if (c.getGeometry().points) {
              try { r.GeometryPoints = JSON.stringify(c.getGeometry().points.map((p) => ({ x: p.x, y: p.y }))); } catch (_) { /* */ }
            }
          }
          return r;
        });
        setTabs((prev) =>
          prev.map((t) => {
            if (t.id !== activeTabId) return t;
            const pWd = t.workflowData || workflowDataRef.current || workflowData;
            if (!pWd) return t;
            const oc = pWd.contents || [];
            const merged = snap.map((s) => {
              const o = oc.find((x) => String(x.id) === String(s.id));
              return o ? { ...o, ...s, script: o.script, ai_prompt: o.ai_prompt, include: o.include } : s;
            });
            return { ...t, workflowData: { ...pWd, contents: merged } };
          }),
        );
      }
    }
    
    try {
      const graph = graphRef.current;
      const model = graph.getModel();
      const cells = graph.getChildCells(graph.getDefaultParent());
      
      const contents = cells.map(cell => {
        const isVertex = model.isVertex(cell);
        const geometry = cell.getGeometry();
        const cellId = cell.getId() ? String(cell.getId()) : '';
        // Model'den güncel value'yu al - label değişikliklerini yakalamak için
        const cellValue = model.getValue(cell);
        const cellDefinition = cellValue ? String(cellValue) : '';
        const cellStyle = cell.getStyle() ? String(cell.getStyle()) : '';
        
        const result = {
          id: cellId,
          vertex: isVertex ? "1" : "0",
          edge: !isVertex ? "1" : "0",
          definition: cellDefinition,
          GeometryX: geometry.x,
          GeometryY: geometry.y,
          GeometryWidth: geometry.width,
          GeometryHeight: geometry.height,
          source: !isVertex && cell.getTerminal(true) ? String(cell.getTerminal(true).getId()) : null,
          target: !isVertex && cell.getTerminal(false) ? String(cell.getTerminal(false).getId()) : null,
          style: cellStyle
        };
        
        // Edge için kosul değerini label'dan çıkar
        if (!isVertex) {
          const label = cellDefinition;
          if (label && label.includes(':Next')) {
            const kosulMatch = label.match(/(\d+):Next/);
            if (kosulMatch) {
              result.kosul = kosulMatch[1];
              // Server'a definition "Next" olarak gönder
              result.definition = 'Next';
            }
          }
        }
        
        if (!isVertex && cell.getGeometry().points) {
          try {
            const points = cell.getGeometry().points.map(p => ({ x: p.x, y: p.y }));
            result.GeometryPoints = JSON.stringify(points);
          } catch (err) {
            result.GeometryPoints = null;
          }
        }
        
        return result;
      });

      // ✅ EN GÜNCEL workflowData'yı kullan (workflowDataRef her zaman en güncel)
      const currentWorkflowData = workflowDataRef.current || workflowData;
      const originalObjects = currentWorkflowData?.contents || [];
      
      contents.forEach((newObj) => {
        const originalObj = originalObjects.find(obj => String(obj.id) === String(newObj.id));
        if (originalObj) {
          const requiredFields = ['kosul', 'filename', 'parent', 'include', 'script', 'ai_prompt', 'GeometryRelative', 'GeometryAs', 'LOEKZ', 'LODAT', 'sourceHandle', 'targetHandle'];
          requiredFields.forEach(field => {
            if (!(field in newObj)) {
              // ✅ ai_prompt için ekstra kontrol: workflowDataRef'ten EN GÜNCEL değeri al
              let originalValue = originalObj[field];
              
              // ✅ ai_prompt için double-check: updateShapeAIPrompt ile güncellenmiş olabilir
              if (field === 'ai_prompt') {
                const freshData = workflowDataRef.current;
                if (freshData && Array.isArray(freshData.contents)) {
                  const freshObj = freshData.contents.find(obj => String(obj.id) === String(newObj.id));
                  if (freshObj && freshObj.ai_prompt !== undefined) {
                    originalValue = freshObj.ai_prompt; // ✅ EN GÜNCEL prompt
                  }
                }
              }
              
              // ✅ script için double-check: editor'dan güncellenmiş olabilir
              if (field === 'script' && String(newObj.id) === String(selectedShape)) {
                // Seçili shape ise, editor'daki kodu kullan
                const editorCode = code || tabCodeCacheRef.current?.[activeTabId]?.[selectedShape] || shapeCodeCacheRef.current?.[selectedShape];
                if (editorCode !== undefined) {
                  originalValue = editorCode; // ✅ EN GÜNCEL kod
                }
              }
              
              // "null" string'ini de null olarak kabul et
              if (originalValue !== undefined && originalValue !== null && originalValue !== 'null') {
                // null, undefined veya "null" string değilse, değeri set et
                if (typeof originalValue === 'object') {
                  newObj[field] = JSON.stringify(originalValue);
                } else {
                  newObj[field] = String(originalValue);
                }
              } else {
                // undefined, null veya "null" string ise, default değerleri set et
                if (field === 'parent') {
                  newObj[field] = currentWorkflowData?.workflow?.id ? String(currentWorkflowData.workflow.id) : '100022941';
                } else if (field === 'script') {
                  newObj[field] = ''; // script boş string olmalı
                } else if (field === 'LODAT') {
                  newObj[field] = null; // LODAT null olabilir
                } else if (field === 'GeometryRelative') {
                  newObj[field] = '0';
                } else if (field === 'GeometryAs') {
                  newObj[field] = 'geometry';
                } else {
                  newObj[field] = '';
                }
              }
            }
          });
        } else {
          // Yeni oluşturulmuş shape (originalObj yok)
          newObj.kosul = newObj.kosul || '';
          newObj.filename = '';
          newObj.parent = currentWorkflowData?.workflow?.id ? String(currentWorkflowData.workflow.id) : '100022941';
          newObj.include = '';
          
          // ✅ Yeni shape için de EN GÜNCEL değerleri kontrol et
          // (Kullanıcı shape'i oluşturduktan hemen sonra prompt girebilir)
          const freshData = workflowDataRef.current;
          if (freshData && Array.isArray(freshData.contents)) {
            const freshObj = freshData.contents.find(obj => String(obj.id) === String(newObj.id));
            if (freshObj) {
              newObj.script = freshObj.script || '';
              newObj.ai_prompt = freshObj.ai_prompt || '';
            } else {
              newObj.script = '';
              newObj.ai_prompt = '';
            }
          } else {
            newObj.script = '';
            newObj.ai_prompt = '';
          }
          newObj.GeometryRelative = '0';
          newObj.GeometryAs = 'geometry';
          newObj.LOEKZ = '';
          newObj.LODAT = null;
          newObj.sourceHandle = '';
          newObj.targetHandle = '';
        }
        
        // "null" string değerlerini temizle (eğer varsa)
        if (newObj.script === 'null') {
          newObj.script = '';
        }
        if (newObj.ai_prompt === 'null') {
          newObj.ai_prompt = '';
        }
        if (newObj.LODAT === 'null') {
          newObj.LODAT = null;
        }
      });
      
      // Son kontrol: promptCacheRef yetkin kaynaktır — tüm panellerin promptları buradadır.
      // Önce workflowDataRef'ten fallback oku, sonra cache ile üzerine yaz.
      const freshRef = workflowDataRef.current;
      contents.forEach((obj) => {
        const id = String(obj.id);
        // 1) workflowDataRef'ten fallback
        if (freshRef && Array.isArray(freshRef.contents)) {
          const src = freshRef.contents.find((c) => c && String(c.id) === id);
          if (src && src.ai_prompt !== undefined && src.ai_prompt !== null && src.ai_prompt !== 'null') {
            obj.ai_prompt = String(src.ai_prompt);
          }
        }
        // 2) promptCacheRef her zaman son sözü söyler
        const cached = promptCacheRef.current[id];
        if (cached !== undefined) {
          obj.ai_prompt = String(cached);
        }
      });

      const finalData = {
        process: currentWorkflowData?.workflow?.id ? String(currentWorkflowData.workflow.id) : "100022941",
        ...(selectedTicketId && { TICID: selectedTicketId }),
        filename: currentWorkflowData?.workflow?.filename ? String(currentWorkflowData.workflow.filename) : "sstforyusuf.php",
        desc: currentWorkflowData?.workflow?.desc ? String(currentWorkflowData.workflow.desc) : "saestforyusuf.php",
        longdesc: currentWorkflowData?.workflow?.longdesc ? String(currentWorkflowData.workflow.longdesc) : "testforyusuf.php",
        cat_id: currentWorkflowData?.workflow?.cat_id ? String(currentWorkflowData.workflow.cat_id) : "90008417",
        diagrun: currentWorkflowData?.workflow?.id ? String(currentWorkflowData.workflow.id) : "100022941",
        objects: contents
      };
      
      /* ═══════════════════════════════════════════════════════
         1) Aktif tab'ın workflow'u (graph'tan okunan contents)
         ═══════════════════════════════════════════════════════ */
      const promises = [];
      promises.push(
        saveWorkflow(finalData, selectedTicketId)
          .then((r) => ({ tab: 'active', type: 'workflow', result: r })),
      );

      /* ═══════════════════════════════════════════════════════
         2) Diğer tab'ların workflow'ları (snapshot'tan)
         ═══════════════════════════════════════════════════════ */
      for (const tab of tabs) {
        if (tab.id === activeTabId) continue;
        const wd = tab.workflowData;
        if (!wd || !wd.contents || !wd.workflow?.id) continue;
        const tabFinalData = {
          process: String(wd.workflow.id),
          ...(selectedTicketId && { TICID: selectedTicketId }),
          filename: wd.workflow.filename ? String(wd.workflow.filename) : '',
          desc: wd.workflow.desc ? String(wd.workflow.desc) : '',
          longdesc: wd.workflow.longdesc ? String(wd.workflow.longdesc) : '',
          cat_id: wd.workflow.cat_id ? String(wd.workflow.cat_id) : '',
          diagrun: String(wd.workflow.id),
          objects: wd.contents,
        };
        promises.push(
          saveWorkflow(tabFinalData, selectedTicketId)
            .then((r) => ({ tab: tab.name, type: 'workflow', result: r })),
        );
      }

      /* ═══════════════════════════════════════════════════════
         3) Tüm değiştirilmiş script'ler (cache'deki her shape)
         ═══════════════════════════════════════════════════════ */
      const savedScriptIds = new Set();

      // Aktif tab'ın code cache'i
      const activeCache = activeTabId && tabCodeCacheRef.current[activeTabId]
        ? tabCodeCacheRef.current[activeTabId] : {};
      // Global shape code cache
      const globalCache = shapeCodeCacheRef.current || {};

      // Aktif editor'daki kodu da cache'e yaz
      if (selectedShape) {
        const editorCode = code || '';
        if (editorCode) {
          activeCache[selectedShape] = editorCode;
          globalCache[selectedShape] = editorCode;
        }
      }

      // Tüm tab cache'lerinden script'leri topla
      for (const [, cache] of Object.entries(tabCodeCacheRef.current)) {
        if (!cache || typeof cache !== 'object') continue;
        for (const [shapeId, scriptVal] of Object.entries(cache)) {
          if (savedScriptIds.has(shapeId)) continue;
          const s = scriptVal != null ? String(scriptVal) : '';
          if (s) {
            savedScriptIds.add(shapeId);
            promises.push(
              saveScript(shapeId, s, selectedTicketId)
                .then((r) => ({ tab: shapeId, type: 'script', result: r })),
            );
          }
        }
      }
      // Global cache'den kalan script'ler
      for (const [shapeId, scriptVal] of Object.entries(globalCache)) {
        if (savedScriptIds.has(shapeId)) continue;
        const s = scriptVal != null ? String(scriptVal) : '';
        if (s) {
          savedScriptIds.add(shapeId);
          promises.push(
            saveScript(shapeId, s, selectedTicketId)
              .then((r) => ({ tab: shapeId, type: 'script', result: r })),
          );
        }
      }

      /* ═══════════════════════════════════════════════════════
         4) Sonuçları değerlendir
         ═══════════════════════════════════════════════════════ */
      const results = await Promise.allSettled(promises);

      let successCount = 0;
      let failCount = 0;
      const errorMessages = [];

      results.forEach((res) => {
        if (res.status === 'fulfilled') {
          const { tab, type, result: saveResult } = res.value;
          if (saveResult.success) {
            successCount++;
          } else {
            failCount++;
            errorMessages.push(`${type}(${tab}): ${saveResult.error || 'Hata'}`);
          }
        } else {
          failCount++;
          errorMessages.push(res.reason?.message || 'Bilinmeyen hata');
        }
      });

      const wfCount = tabs.length;
      const scrCount = savedScriptIds.size;

      if (failCount === 0) {
        setSaveStatus({ type: 'success', message: `Kaydedildi! (${wfCount} flow, ${scrCount} script)` });
      } else if (successCount > 0) {
        setSaveStatus({ type: 'error', message: `Kısmen kaydedildi — ${errorMessages.slice(0, 2).join('; ')}` });
      } else {
        setSaveStatus({ type: 'error', message: errorMessages.slice(0, 2).join(' | ') || 'Kaydetme başarısız!' });
      }
    } catch (error) {
      setSaveStatus({ type: 'error', message: error.message || 'Kaydetme başarısız!' });
    } finally {
      setIsSaving(false);
    }
  }, [saveWorkflow, saveScript, workflowData, selectedShape, code, selectedTicketId, tabs, activeTabId, setTabs]);

  const handleZoomIn = useCallback(() => {
    if (!graphRef.current) return;
    const graph = graphRef.current;
    const currentScale = graph.view.scale || 1;
    const targetScale = Math.min(currentScale * 1.1, 4);
    const steps = 10;
    const stepSize = (targetScale - currentScale) / steps;
    let currentStep = 0;
    const animateZoom = () => {
      if (currentStep < steps) {
        const newScale = currentScale + stepSize * (currentStep + 1);
        graph.view.setScale(newScale);
        graph.view.validate();
        currentStep++;
        requestAnimationFrame(animateZoom);
      }
    };
    animateZoom();
  }, []);

  const handleZoomOut = useCallback(() => {
    if (!graphRef.current) return;
    const graph = graphRef.current;
    const currentScale = graph.view.scale || 1;
    const targetScale = Math.max(currentScale * 0.9, 0.1);
    const steps = 10;
    const stepSize = (targetScale - currentScale) / steps;
    let currentStep = 0;
    const animateZoom = () => {
      if (currentStep < steps) {
        const newScale = currentScale + stepSize * (currentStep + 1);
        graph.view.setScale(newScale);
        graph.view.validate();
        currentStep++;
        requestAnimationFrame(animateZoom);
      }
    };
    animateZoom();
  }, []);

  const handleZoomReset = useCallback(() => {
    if (!graphRef.current) return;
    const graph = graphRef.current;
    const currentScale = graph.view.scale || 1;
    const targetScale = 1;
    if (Math.abs(currentScale - targetScale) < 0.01) return;
    const steps = 12;
    const stepSize = (targetScale - currentScale) / steps;
    let currentStep = 0;
    const animateZoom = () => {
      if (currentStep < steps) {
        const newScale = currentScale + stepSize * (currentStep + 1);
        graph.view.setScale(newScale);
        graph.view.validate();
        currentStep++;
        requestAnimationFrame(animateZoom);
      } else {
        // Sadece scale sıfırla — translate'e dokunma, aksi hâlde şekiller kaybolur
        graph.view.setScale(targetScale);
        graph.view.validate();
      }
    };
    animateZoom();
  }, []);

  // ========== GENERATE UUID FUNCTION ==========
  const generateUUID = useCallback(() => {
    // 0009-095a-443b-985d-2c85 formatında UUID oluştur (5 grup, her biri 4 karakter hex)
    const generateHex = (length) => {
      return Array.from({ length }, () => Math.floor(Math.random() * 16).toString(16)).join('');
    };
    return `${generateHex(4)}-${generateHex(4)}-${generateHex(4)}-${generateHex(4)}-${generateHex(4)}`;
  }, []);

  // ========== ADD SHAPE FUNCTION ==========
  const addShape = useCallback((shapeName, label) => {
    if (!graphRef.current || !isDesignMode) {
      return;
    }
    
    const graph = graphRef.current;
    const parent = graph.getDefaultParent();
    
    // Get center of viewport for new node position
    const bounds = graph.getGraphBounds();
    const centerX = bounds.x + bounds.width / 2;
    const centerY = bounds.y + bounds.height / 2;
    
    const x = centerX + (Math.random() - 0.5) * 200;
    const y = centerY + (Math.random() - 0.5) * 200;
    
    const shape = shapes.find(s => s.name === shapeName);
    if (!shape) return;
    
    // mxGraph'in built-in shape'lerini kullan
    const mxShape = shape.mxShape || 'rectangle';
    
    // Şekil boyutları — daire (40×40) referans, diğerleri oransal
    let w, h;
    switch (shapeName) {
      case 'startCircle':
      case 'stopCircle':
        w = 40; h = 40; break;
      case 'rectangle':
        w = 90; h = 40; break;
      case 'preProcess':
        w = 90; h = 40; break;
      case 'rhombus':
        w = 60; h = 50; break;
      case 'triangle':
        w = 50; h = 45; break;
      case 'hexagon':
        w = 70; h = 40; break;
      case 'cylinder':
        w = 45; h = 50; break;
      default:
        w = 80; h = 40; break;
    }
    
    // Şekil için renk - shape'den al, yoksa shapeColor kullan
    const fillColor = shape.color || shapeColor;
    
    // Style string oluştur - mxGraph'in built-in shape'lerini kullan
    const style = `shape=${mxShape};fillColor=${fillColor};strokeColor=#000000;fontSize=10;fontColor=#000000;whiteSpace=wrap;html=1;connectable=1;selectable=1;movable=1;resizable=1;`;
    
    const id = generateUUID();
    
    try {
      graph.getModel().beginUpdate();
      const vertex = graph.insertVertex(parent, id, defaultText || label, x, y, w, h, style);
      
      // Immediately refresh and validate
      graph.refresh(vertex);
      graph.view.validate();
      graph.setSelectionCell(vertex);
    } catch (error) {
      console.error('Error adding shape:', error);
    } finally {
      graph.getModel().endUpdate();
    }
  }, [isDesignMode, shapeColor, defaultText, shapes, generateUUID]);

  /* ── Graph'ın mevcut durumunu alıp önceki tab'ın workflowData.contents'ine yazan yardımcı ── */
  const snapshotGraphToTab = useCallback((tabId) => {
    if (!graphRef.current || !tabId) return;
    const graph = graphRef.current;
    const model = graph.getModel();
    const cells = graph.getChildCells(graph.getDefaultParent());
    if (!cells || cells.length === 0) return;

    const snappedContents = cells.map((cell) => {
      const isVertex = model.isVertex(cell);
      const geometry = cell.getGeometry();
      const cellId = cell.getId() ? String(cell.getId()) : '';
      const cellValue = model.getValue(cell);
      const cellDefinition = cellValue ? String(cellValue) : '';
      const cellStyle = cell.getStyle() ? String(cell.getStyle()) : '';

      const result = {
        id: cellId,
        vertex: isVertex ? '1' : '0',
        edge: !isVertex ? '1' : '0',
        definition: cellDefinition,
        GeometryX: geometry.x,
        GeometryY: geometry.y,
        GeometryWidth: geometry.width,
        GeometryHeight: geometry.height,
        source: !isVertex && cell.getTerminal(true) ? String(cell.getTerminal(true).getId()) : null,
        target: !isVertex && cell.getTerminal(false) ? String(cell.getTerminal(false).getId()) : null,
        style: cellStyle,
      };

      if (!isVertex) {
        const label = cellDefinition;
        if (label && label.includes(':Next')) {
          const kosulMatch = label.match(/(\d+):Next/);
          if (kosulMatch) result.kosul = kosulMatch[1];
        }
        if (cell.getGeometry().points) {
          try {
            result.GeometryPoints = JSON.stringify(cell.getGeometry().points.map((p) => ({ x: p.x, y: p.y })));
          } catch (_) { /* ignore */ }
        }
      }
      return result;
    });

    setTabs((prev) =>
      prev.map((tab) => {
        if (tab.id !== tabId) return tab;
        const prevWd = tab.workflowData || workflowDataRef.current || workflowData;
        if (!prevWd) return tab;
        const originalContents = prevWd.contents || [];
        const merged = snappedContents.map((snap) => {
          const orig = originalContents.find((o) => String(o.id) === String(snap.id));
          if (orig) {
            return { ...orig, ...snap, script: orig.script, ai_prompt: orig.ai_prompt, include: orig.include };
          }
          return snap;
        });
        return { ...tab, workflowData: { ...prevWd, contents: merged } };
      }),
    );
  }, [workflowData, setTabs]);

  const prevActiveTabIdForSnapshot = useRef(activeTabId);

  useEffect(() => {
    const prevTab = prevActiveTabIdForSnapshot.current;
    if (prevTab && prevTab !== activeTabId) {
      snapshotGraphToTab(prevTab);
    }
    prevActiveTabIdForSnapshot.current = activeTabId;
  }, [activeTabId, snapshotGraphToTab]);

  // ========== GRAPH INITIALIZATION ==========
  useEffect(() => {
    // Aktif tab'ın workflowData'sını kullan
    const activeTab = tabs.find(tab => tab.id === activeTabId);
    const currentWorkflowData = activeTab?.workflowData || workflowDataRef.current;
    const currentWorkflowId = currentWorkflowData?.workflow?.id || null;
    const workflowIdChanged = workflowIdRef.current !== currentWorkflowId;
    const isInitialLoad = workflowIdRef.current === null;
    
    // workflowIdRef'i güncelle
    if (currentWorkflowId) {
      workflowIdRef.current = currentWorkflowId;
    }
    
    const container = graphContainerRef.current;
    if (!container) {
      return;
    }
    if (!mxClient.isBrowserSupported()) {
      alert('mxGraph bu tarayıcıda desteklenmiyor!');
      return;
    }
    
    // Eğer workflow ID değişmediyse ve graph zaten varsa, graph'ı yeniden oluşturma
    // Ancak aktif tab değiştiyse graph'i yeniden render et
    if (!isInitialLoad && !workflowIdChanged && graphRef.current) {
      // Aktif tab değiştiyse graph'i temizle ve yeniden render et
      if (activeTabId) {
        const graph = graphRef.current;
        graph.getModel().clear();
        workflowIdRef.current = null; // Force re-render
      } else {
      if (graphRef.current._applyDesignModeSettings) {
        graphRef.current._applyDesignModeSettings(isDesignMode);
      }
      return;
      }
    }
    
    // Container CSS
    container.style.background = `url('${gridIcon}') repeat`;
    container.style.backgroundColor = '#fafafa';
    container.style.position = 'relative';
    container.style.width = '100%';
    container.style.height = '100%';
    container.style.overflow = 'hidden';
    container.style.outline = 'none';
    container.style.cursor = isDesignMode ? 'default' : 'grab';
    container.style.pointerEvents = 'auto';
    container.style.zIndex = '1';
    
    let graph = null;
    
    // mxGraph'in built-in shape'lerini kullandığımız için image preload'a gerek yok
    (async () => {
      if (graphRef.current) {
        try {
          graphRef.current.destroy();
        } catch (error) {
          // ignore
        }
      }
      
      graph = new mxGraph(container);
      graphRef.current = graph;
      
      // Register custom shapes
      if (mxCellRenderer) {
        mxCellRenderer.registerShape('triangle', mxTriangleShape);
        mxCellRenderer.registerShape('predefinedProcess', mxPredefinedProcessShape);
      }
      
      // Basic graph settings
      graph.setEnabled(true);
      graph.setGridEnabled(true);
      graph.setGridSize(20);
      graph.view.gridColor = '#e0e0e0';
      graph.view.gridImage = null;
      graph.setTooltips(false);
      graph.setAllowDanglingEdges(false);
      graph.setDisconnectOnMove(false);
      graph.setConnectable(true);
      graph.setCellsEditable(true);
      graph.setCellsResizable(true);
      graph.setDropEnabled(true);
      graph.setMultigraph(false);
      
      // Container style
      graph.container.style.width = '100%';
      graph.container.style.height = '100%';
      graph.container.style.position = 'relative';
      graph.container.style.overflow = 'hidden';
      graph.container.style.pointerEvents = 'auto';
      graph.container.style.zIndex = '1';
      
      // Pointer events
      setTimeout(() => {
        if (graph.view) {
          if (graph.view.canvas) {
            graph.view.canvas.style.pointerEvents = 'auto';
          }
          if (graph.view.backgroundPane) {
            graph.view.backgroundPane.style.pointerEvents = 'none';
          }
          if (graph.view.drawPane) {
            graph.view.drawPane.style.pointerEvents = 'auto';
          }
          if (graph.view.overlayPane) {
            graph.view.overlayPane.style.pointerEvents = 'auto';
          }
          if (graph.container) {
            graph.container.style.pointerEvents = 'auto';
            graph.container.style.position = 'relative';
            graph.container.style.width = '100%';
            graph.container.style.height = '100%';
          }
        }
      }, 50);
      
      // Apply design mode settings
      const applyDesignModeSettings = (designMode) => {
        graph.setEnabled(true);
        
        graph.setCellsMovable(designMode);
        graph.setConnectable(designMode);
        graph.setCellsEditable(designMode);
        graph.setCellsResizable(designMode);
        graph.setCellsBendable(designMode);
        graph.setCellsDisconnectable(designMode);
        graph.setAllowDanglingEdges(designMode);
        
        graph.setPanning(true);
        // Pan ayarları - Design Mode'da da boş alanda sol tık ile pan yapılabilir
        graph.panningHandler.useLeftButtonForPanning = true; // Sol tık ile pan yapılabilir (boş alanda)
        graph.panningHandler.useMiddleButtonForPanning = true; // Orta mouse tuşu ile pan yapılabilir
        graph.panningHandler.usePopupTrigger = false;
        graph.panningHandler.ignoreCell = false; // Cell'lerin üzerinde pan yapma - şekilleri hareket ettirebilmek için
        
        // Pan cursor'ı için event listener'lar
        const originalMouseDown = graph.panningHandler.mouseDown;
        const originalMouseUp = graph.panningHandler.mouseUp;
        
        // Space tuşu durumunu kontrol etmek için container'a data attribute ekle
        const updatePanCursor = () => {
          if (!graph.container) return;
          const isSpaceActive = graph.container.dataset.spacePan === 'true';
          if (isSpaceActive) {
            graph.container.style.cursor = 'grab';
            if (graph.view && graph.view.canvas) {
              graph.view.canvas.style.cursor = 'grab';
            }
          }
        };
        
        graph.panningHandler.mouseDown = function(sender, me) {
          const isSpaceActive = graph.container && graph.container.dataset.spacePan === 'true';
          const isMiddleButton = me.getEvent().button === 1;
          const cell = me.getCell();
          const isDesignModeActive = designMode;
          
          // Orta mouse tuşu ile her zaman pan yapılabilir (cell olsa bile)
          if (isMiddleButton) {
            const result = originalMouseDown.call(this, sender, me);
            if (graph.container) {
              graph.container.style.cursor = 'grabbing';
              if (graph.view && graph.view.canvas) {
                graph.view.canvas.style.cursor = 'grabbing';
              }
            }
            return result;
          }
          
          // Design Mode'da: Cell üzerindeyse pan yapma, şekil hareket etsin
          if (isDesignModeActive && cell) {
            // Cell üzerindeyse pan yapma, şekil hareket etsin
            return; // Pan handler'ı çalıştırma
          }
          
          // Boş alanda (cell yoksa): Her zaman pan yapılabilir
          if (!cell) {
            const result = originalMouseDown.call(this, sender, me);
            if (graph.container) {
              graph.container.style.cursor = 'grabbing';
              if (graph.view && graph.view.canvas) {
                graph.view.canvas.style.cursor = 'grabbing';
              }
            }
            return result;
          }
          
          // Diğer durumlarda varsayılan davranış
          return originalMouseDown.call(this, sender, me);
        };
        
        graph.panningHandler.mouseUp = function(sender, me) {
          const result = originalMouseUp.call(this, sender, me);
          // Pan bittiğinde cursor'ı güncelle
          updatePanCursor();
          return result;
        };
        
        // Orta mouse tuşu için cursor güncellemesi
        if (graph.container) {
          const handleMiddleMouseDown = (e) => {
            if (e.button === 1) { // Orta mouse tuşu
              graph.container.style.cursor = 'grabbing';
              if (graph.view && graph.view.canvas) {
                graph.view.canvas.style.cursor = 'grabbing';
              }
            }
          };
          
          const handleMiddleMouseUp = (e) => {
            if (e.button === 1) { // Orta mouse tuşu
              updatePanCursor();
            }
          };
          
          graph.container.addEventListener('mousedown', handleMiddleMouseDown);
          graph.container.addEventListener('mouseup', handleMiddleMouseUp);
          
          // Cleanup için referansları sakla
          graph._middleMouseHandlers = {
            down: handleMiddleMouseDown,
            up: handleMiddleMouseUp
          };
        }
        
        // Zoom ayarları
        graph.centerZoom = true;
        
        // Smooth scroll zoom için container'a wheel event listener ekle
        if (graph.container) {
          let zoomAnimationId = null;
          
          const smoothZoomHandler = (evt) => {
            // Ctrl/Cmd tuşu basılıyken zoom yap
            if (evt.ctrlKey || evt.metaKey) {
              evt.preventDefault();
              evt.stopPropagation();
              
              // Önceki animasyonu iptal et
              if (zoomAnimationId) {
                cancelAnimationFrame(zoomAnimationId);
              }
              
              const delta = evt.deltaY || evt.wheelDelta || 0;
              const currentScale = graph.view.scale || 1;
              
              // Zoom miktarını hesapla (daha küçük adımlar için)
              const zoomFactor = delta < 0 ? 1.05 : 0.95; // %5 artış/azalış
              const targetScale = Math.max(0.1, Math.min(4, currentScale * zoomFactor));
              
              // Smooth zoom animation
              const startScale = currentScale;
              const scaleDiff = targetScale - startScale;
              const duration = 200; // 200ms animasyon
              const startTime = Date.now();
              
              const animateZoom = () => {
                const elapsed = Date.now() - startTime;
                const progress = Math.min(elapsed / duration, 1);
                
                // Easing function (ease-out)
                const easeOut = 1 - Math.pow(1 - progress, 3);
                
                const newScale = startScale + (scaleDiff * easeOut);
                graph.view.setScale(newScale);
                graph.view.validate();
                
                if (progress < 1) {
                  zoomAnimationId = requestAnimationFrame(animateZoom);
                } else {
                  zoomAnimationId = null;
                }
              };
              
              animateZoom();
            }
            // Ctrl/Cmd yoksa mxGraph'in varsayılan davranışı çalışsın
          };
          
          graph.container.addEventListener('wheel', smoothZoomHandler, { passive: false });
          
          // Cleanup için referansı sakla
          graph._smoothZoomHandler = smoothZoomHandler;
        }
        
        if (graph.container) {
          graph.container.style.pointerEvents = 'auto';
        }
        if (graph.view && graph.view.canvas) {
          graph.view.canvas.style.pointerEvents = 'auto';
        }
        
        // Design Mode specific handlers
        if (designMode) {
          graph.isCellSelectable = function(cell) {
            if (!cell) return false;
            return this.getModel().isVertex(cell) || this.getModel().isEdge(cell);
          };
          
          graph.isCellMovable = function(cell) {
            if (!cell) return false;
            return this.getModel().isVertex(cell);
          };
          
          graph.dblClick = function(evt, cell) {
            if (cell != null) {
              graph.startEditingAtCell(cell);
            }
          };
          
          graph.connectionHandler.createEdgeState = function(me) {
            // Edge oluşturulurken label set etme - CONNECT event'inde set edilecek
            // Edge oluşturulurken UUID formatında ID belirle
            const edgeId = generateUUID();
            const edge = graph.createEdge(edgeId, null, null, null, null, 'endArrow=block;edgeStyle=orthogonalEdgeStyle;rounded=0;orthogonalLoop=1;bendable=1;perimeter=1;editable=1;labelBackgroundColor=#fff;labelBorderColor=#e0e0e0;fontColor=#000000;');
            return new window.mxCellState(graph.view, edge, graph.getCellStyle(edge));
          };
          
          // 8 handle noktası için connection constraint'ler (4 köşe + 4 kenar orta)
          // perimeter=true ile edge'ler shape'in border'ına tam temas eder
          graph.getAllConnectionConstraints = function(terminal) {
            if (terminal != null && terminal.shape != null) {
              return [
                // 4 köşe - perimeter=true ile border'a tam temas
                new mxConnectionConstraint(new mxPoint(0, 0), true),      // Sol üst
                new mxConnectionConstraint(new mxPoint(1, 0), true),      // Sağ üst
                new mxConnectionConstraint(new mxPoint(1, 1), true),       // Sağ alt
                new mxConnectionConstraint(new mxPoint(0, 1), true),       // Sol alt
                // 4 kenar orta - perimeter=true ile border'a tam temas
                new mxConnectionConstraint(new mxPoint(0.5, 0), true),     // Üst orta
                new mxConnectionConstraint(new mxPoint(1, 0.5), true),     // Sağ orta
                new mxConnectionConstraint(new mxPoint(0.5, 1), true),     // Alt orta
                new mxConnectionConstraint(new mxPoint(0, 0.5), true),     // Sol orta
              ];
            }
            return null;
          };
          
          graph.connectionHandler.isConnectableCell = function(cell) {
            return graph.getModel().isVertex(cell);
          };
        } else {
          // Design Mode kapalıyken - fonksiyonları temizle veya boş fonksiyon yap
          delete graph.isCellSelectable;
          delete graph.isCellMovable;
          // dblClick her zaman tanımlı olmalı (hata önlemek için)
          graph.dblClick = function(evt, cell) {
            // Design mode kapalıyken double-click'te hiçbir şey yapma
            // Sadece hata önlemek için boş fonksiyon
          };
          graph.connectionHandler.createEdgeState = null;
          graph.getAllConnectionConstraints = null;
          graph.connectionHandler.isConnectableCell = null;
        }
      };
      
      applyDesignModeSettings(isDesignMode);
      graph._applyDesignModeSettings = applyDesignModeSettings;
      
      // Rubberband selection
      if (!rubberbandRef.current) {
        rubberbandRef.current = new mxRubberband(graph);
      }
      
      // Initial validation
      setTimeout(() => {
        if (graph && graph.view) {
          try {
            graph.view.validate();
            graph.refresh();
          } catch (error) {
            // ignore
          }
        }
      }, 100);
      
      // Ruler update function
      const updateRulers = () => {
        if (!graph || !graph.view || !container) return;
        
        try {
          const view = graph.view;
          const scale = view.scale || 1;
          const translate = view.translate || new mxPoint(0, 0);
          const containerRect = container.getBoundingClientRect();
          const gridSize = graph.gridSize || 20;
          const scaledGrid = gridSize * scale;
          const startX = -translate.x * scale;
          const startY = -translate.y * scale;
          const width = containerRect.width;
          const height = containerRect.height;
          
          const leftRuler = document.getElementById('left-ruler');
          const topRuler = document.getElementById('top-ruler');
          
          // Left Ruler (Vertical - Y coordinates)
          if (leftRuler) {
            let rulerHTML = '';
            const tickStep = scaledGrid * 3;
            let lastValue = null;
            let lastPixelY = -100;
            
            for (let y = Math.floor(startY / tickStep) * tickStep; y < startY + height + tickStep; y += tickStep) {
              const pixelY = y - startY;
              const value = Math.round(y / scale);
              if (value === lastValue) continue;
              if (Math.abs(pixelY - lastPixelY) < 25 && lastPixelY !== -100) continue;
              lastValue = value;
              lastPixelY = pixelY;
              
              if (pixelY >= -1 && pixelY <= height + 1) {
                rulerHTML += `
                  <div style="position:absolute;top:${pixelY}px;left:0;width:4px;height:1px;background:#555;"></div>
                  <div style="position:absolute;top:${pixelY - 5}px;left:5px;font-size:7px;color:#777;line-height:1;white-space:nowrap;">${value}</div>
                `;
              }
            }
            leftRuler.innerHTML = rulerHTML;
            leftRuler.style.height = `${height}px`;
          }
          
          // Top Ruler (Horizontal - X coordinates)
          if (topRuler) {
            const graphWrapper = container.parentElement?.parentElement;
            const leftRulerWidth = 20;
            const wrapperWidth = graphWrapper ? graphWrapper.getBoundingClientRect().width - leftRulerWidth : width;
            
            let rulerHTML = '';
            const tickStep = scaledGrid * 3;
            let lastValue = null;
            let lastPixelX = -100;
            
            for (let x = Math.floor(startX / tickStep) * tickStep; x < startX + wrapperWidth + tickStep; x += tickStep) {
              const pixelX = x - startX;
              const value = Math.round(x / scale);
              if (value === lastValue) continue;
              if (Math.abs(pixelX - lastPixelX) < 40 && lastPixelX !== -100) continue;
              lastValue = value;
              lastPixelX = pixelX;
              
              if (pixelX >= -1 && pixelX <= wrapperWidth + 1) {
                rulerHTML += `
                  <div style="position:absolute;left:${pixelX}px;top:0;width:1px;height:4px;background:#555;"></div>
                  <div style="position:absolute;left:${pixelX + 3}px;top:5px;font-size:7px;color:#777;line-height:1;white-space:nowrap;">${value}</div>
                `;
              }
            }
            topRuler.innerHTML = rulerHTML;
            topRuler.style.width = `${wrapperWidth}px`;
          }
        } catch (error) {
          // ignore
        }
      };
      
      // Update rulers on view changes
      const updateRulersHandler = () => {
        updateRulers();
      };
      
      if (graph.view) {
        graph.view.addListener(mxEvent.SCALE, updateRulersHandler);
        graph.view.addListener(mxEvent.SCALE_AND_TRANSLATE, updateRulersHandler);
        graph.view.addListener(mxEvent.TRANSLATE, updateRulersHandler);
      }
      
      // Resize observer
      const handleResize = () => {
        if (graph && container) {
          try {
            graph.view.validate();
            updateRulers();
          } catch (error) {
            // ignore
          }
        }
      };
      
      const resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(container);
      
      // Scroll listener for ruler updates
      const scrollHandler = () => {
        updateRulers();
      };
      if (container) {
        container.addEventListener('scroll', scrollHandler);
      }
      
      setTimeout(() => {
        handleResize();
        updateRulers();
      }, 100);
      
      graph.view.validate();
      
      // Context menu
      if (graph.popupMenuHandler) {
        graph.popupMenuHandler.hideMenu = function() {};
        graph.popupMenuHandler.isPopupTrigger = function() { return false; };
      } else {
        graph.popupMenuHandler = {
          hideMenu: function() {},
          isPopupTrigger: function() { return false; }
        };
      }
      
      const handleContextMenu = (sender, evt) => {
        evt.consume();
        evt.preventDefault();
        
        const me = evt.getProperty('event');
        const cell = evt.getProperty('cell');
        const selectedCell = graph.getSelectionCell();
        const targetCell = cell || selectedCell;
        
        if (!targetCell) {
          return;
        }
        
        graph.setSelectionCell(targetCell);
        
        const existingMenu = document.querySelector('.mx-context-menu');
        if (existingMenu) {
          document.body.removeChild(existingMenu);
        }
        
        const menu = document.createElement('div');
        menu.className = 'mx-context-menu';
        menu.style.cssText = `
          position: fixed;
          left: ${me.clientX}px;
          top: ${me.clientY}px;
          background: #2d2d2d;
          border: 1px solid #3e3e42;
          border-radius: 4px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.3);
          z-index: 100000;
          min-width: 160px;
          padding: 4px 0;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        `;
        
        const isVertex = graph.getModel().isVertex(targetCell);
        
        if (isVertex) {
          const codeBtn = document.createElement('div');
          codeBtn.textContent = '💻 Code Editor';
          codeBtn.style.cssText = 'padding: 10px 16px; cursor: pointer; color: #cccccc; font-size: 13px; transition: background 0.2s;';
          codeBtn.onmouseenter = () => codeBtn.style.background = '#3e3e42';
          codeBtn.onmouseleave = () => codeBtn.style.background = 'transparent';
          codeBtn.onclick = (e) => {
            e.stopPropagation();
            if (targetCell && setSelectedShape) {
              setSelectedShape(targetCell.getId());
              
              // Seçili şeklin rengini al
              const style = targetCell.getStyle() || '';
              const fillColorMatch = style.match(/fillColor=([^;]+)/);
              if (fillColorMatch) {
                setSelectedShapeColor(fillColorMatch[1]);
              } else {
                setSelectedShapeColor('#FFFF00');
              }
              
              setShowCodeEditor(true);
            }
            if (document.body.contains(menu)) {
              document.body.removeChild(menu);
            }
          };
          menu.appendChild(codeBtn);
          
          // Include butonları - şekle include eklenmişse "Remove Include", yoksa "Add Include"
          const shapeId = targetCell.getId();
          
          // Include kontrolü: hem state'den hem de workflowData'dan kontrol et
          // (State asenkron güncellenebilir, bu yüzden her iki kaynağı da kontrol ediyoruz)
          const hasIncludeInState = shapesWithInclude.has(shapeId);
          
          // workflowData'dan da kontrol et
          const currentWorkflowData = workflowDataRef.current || workflowData;
          let hasIncludeInData = false;
          if (currentWorkflowData?.contents) {
            const nodeData = currentWorkflowData.contents.find(item => 
              item && String(item.id) === String(shapeId)
            );
            hasIncludeInData = nodeData && 
                               nodeData.include && 
                               nodeData.include !== '' && 
                               nodeData.include !== 'null' && 
                               nodeData.include !== null;
          }
          
          // Overlay kontrolü - en güvenilir yöntem
          const cellOverlays = graph.getCellOverlays(targetCell);
          const hasIncludeOverlay = cellOverlays && cellOverlays.length > 0;
          
          // Herhangi birinde include varsa Remove Include göster
          const hasInclude = hasIncludeInState || hasIncludeInData || hasIncludeOverlay;
          
          
          if (hasInclude) {
            const includeInfoBtn = document.createElement('div');
            includeInfoBtn.textContent = 'ℹ️ Include Info';
            includeInfoBtn.style.cssText = 'padding: 10px 16px; cursor: pointer; color: #cccccc; font-size: 13px; transition: background 0.2s;';
            includeInfoBtn.onmouseenter = () => includeInfoBtn.style.background = '#3e3e42';
            includeInfoBtn.onmouseleave = () => includeInfoBtn.style.background = 'transparent';
            includeInfoBtn.onclick = (e) => {
              e.stopPropagation();
              openIncludeDetails(shapeId, { showPopup: false });
              if (document.body.contains(menu)) {
                document.body.removeChild(menu);
              }
            };
            menu.appendChild(includeInfoBtn);

            // Include edilmişse "Remove Include" butonu ekle
            const removeIncludeBtn = document.createElement('div');
            removeIncludeBtn.textContent = '🔗 Remove Include';
            removeIncludeBtn.style.cssText = 'padding: 10px 16px; cursor: pointer; color: #ff6b6b; font-size: 13px; transition: background 0.2s;';
            removeIncludeBtn.onmouseenter = () => removeIncludeBtn.style.background = '#3e3e42';
            removeIncludeBtn.onmouseleave = () => removeIncludeBtn.style.background = 'transparent';
            removeIncludeBtn.onclick = async (e) => {
              e.stopPropagation();
              if (targetCell && graphRef.current) {
                try {
                  const processId = process.env.REACT_APP_ADD_INCLUDABLE_LAST_SELECT;
                  if (!processId) {
                    throw new Error('REACT_APP_ADD_INCLUDABLE_LAST_SELECT environment variable tanımlı değil!');
                  }
                  
                  // Include edilmiş şeklin foldername'ini al
                  const removeFoldername = shapeIncludeFolders[shapeId] || '';
                  
                  // Process ID'yi workflowData'dan al
                  const removeWorkflowData = workflowDataRef.current || workflowData;
                  const removeWorkflowProcessId = removeWorkflowData?.workflow?.id 
                    ? String(removeWorkflowData.workflow.id) 
                    : processId; // Fallback olarak REACT_APP_ADD_INCLUDABLE_LAST_SELECT kullan
                  
                  const bodyParams = {
                    stepid: shapeId, // Context menu'den gelen shapeId kullan (selectedIncludeShape değil!)
                    include: '', // Boş string - remove için
                    foldername: removeFoldername, // Include edilirken kaydedilen foldername
                    process: processId, 
                  };
                  
                  const response = await fetchWrapper(processId, bodyParams, navigate);
                  
                  // Response kontrolü
                  const resultData = response?.result;
                  const isSuccess = resultData && 
                                   (resultData.result === 1 || resultData.result === "1") && 
                                   (resultData.message === 'success' || resultData.message === 'Success');
                  
                  if (response && isSuccess) {
                    // Başarılı - Overlay'i kaldır ve state'leri güncelle
                    const graph = graphRef.current;
                    const model = graph.getModel();
                    const cell = model.getCell(shapeId);
                    
                    if (cell && model.isVertex(cell)) {
                      // Overlay'i kaldır - tüm overlay'leri kontrol et ve kaldır
                      const overlays = graph.getCellOverlays(cell);
                      if (overlays && overlays.length > 0) {
                        graph.removeCellOverlay(cell);
                      }
                      
                      // Label'ı eski haline döndür (eğer saklanmışsa)
                      const originalLabel = shapeOriginalLabels[shapeId];
                      if (originalLabel !== undefined) {
                        model.beginUpdate();
                        try {
                          cell.setValue(originalLabel);
                          graph.refresh(cell);
                        } finally {
                          model.endUpdate();
                        }
                      }
                      
                      // Graph'i refresh et - overlay'in kaldırıldığından emin olmak için
                      graph.getView().invalidate(cell);
                      graph.getView().validate();
                      graph.refresh();
                      
                      // Overlay'in gerçekten kaldırıldığını kontrol et
                      const remainingOverlays = graph.getCellOverlays(cell);
                      if (remainingOverlays && remainingOverlays.length > 0) {
                        // Hala overlay varsa tekrar kaldır
                        graph.removeCellOverlay(cell);
                        graph.refresh();
                      }
                    }
                    
                    // State'leri güncelle
                    setShapesWithInclude(prev => {
                      const newSet = new Set(prev);
                      newSet.delete(shapeId);
                      return newSet;
                    });
                    
                    setShapeIncludePaths(prev => {
                      const newPaths = { ...prev };
                      delete newPaths[shapeId];
                      return newPaths;
                    });
                    
                    setShapeIncludeFolders(prev => {
                      const newFolders = { ...prev };
                      delete newFolders[shapeId];
                      return newFolders;
                    });
                    
                    setShapeOriginalLabels(prev => {
                      const newLabels = { ...prev };
                      delete newLabels[shapeId];
                      return newLabels;
                    });

                    // Aktif tab'ın workflowData'sından include bilgisini temizle
                    if (activeTabId) {
                      setTabs(prev => prev.map(tab => {
                        if (tab.id !== activeTabId) return tab;
                        const currentData = tab.workflowData || workflowDataRef.current;
                        if (!currentData?.contents) return tab;
                        const updatedContents = currentData.contents.map(item => {
                          if (!item || String(item.id) !== String(shapeId)) {
                            return item;
                          }
                          return {
                            ...item,
                            include: '',
                          };
                        });
                        return {
                          ...tab,
                          workflowData: {
                            ...currentData,
                            contents: updatedContents,
                          },
                        };
                      }));
                    }
                    
                    // Başarı mesajı göster
                    
                  } else {
                    alert('Include kaldırma başarısız!');
                  }
                } catch (error) {
                  alert('Include kaldırma sırasında bir hata oluştu: ' + error.message);
                }
              }
              if (document.body.contains(menu)) {
                document.body.removeChild(menu);
              }
            };
            menu.appendChild(removeIncludeBtn);
          } else {
            // Include edilmemişse "Add Include" butonu ekle
            const includeBtn = document.createElement('div');
            includeBtn.textContent = '🔗 Add Include';
            includeBtn.style.cssText = 'padding: 10px 16px; cursor: pointer; color: #cccccc; font-size: 13px; transition: background 0.2s;';
            includeBtn.onmouseenter = () => includeBtn.style.background = '#3e3e42';
            includeBtn.onmouseleave = () => includeBtn.style.background = 'transparent';
            includeBtn.onclick = (e) => {
              e.stopPropagation();
              if (targetCell) {
                setSelectedIncludeShape(targetCell.getId());
                setShowIncludeModal(true);
              }
              if (document.body.contains(menu)) {
                document.body.removeChild(menu);
              }
            };
            menu.appendChild(includeBtn);
          }
        }
        
        const deleteBtn = document.createElement('div');
        deleteBtn.textContent = '🗑️ Sil';
        deleteBtn.style.cssText = 'padding: 10px 16px; cursor: pointer; color: #cccccc; font-size: 13px; transition: background 0.2s;';
        deleteBtn.onmouseenter = () => deleteBtn.style.background = '#3e3e42';
        deleteBtn.onmouseleave = () => deleteBtn.style.background = 'transparent';
        deleteBtn.onclick = (e) => {
          e.stopPropagation();
          
          // Close context menu first
          if (document.body.contains(menu)) {
            document.body.removeChild(menu);
          }
          
          // Show confirmation dialog
          const isVertex = graph.getModel().isVertex(targetCell);
          const cellLabel = targetCell.getValue() ? String(targetCell.getValue()) : 'item';
          const itemType = isVertex ? 'shape' : 'connection';
          
          if (window.confirm(`Are you sure you want to delete this ${itemType}?\n\n"${cellLabel}"\n\nThis action cannot be undone.`)) {
            graph.removeCells([targetCell]);
          }
        };
        menu.appendChild(deleteBtn);
        document.body.appendChild(menu);
        
        const closeMenu = (e) => {
          if (!menu.contains(e.target)) {
            if (document.body.contains(menu)) {
              document.body.removeChild(menu);
            }
            document.removeEventListener('click', closeMenu);
            document.removeEventListener('contextmenu', closeMenu);
          }
        };
        
        setTimeout(() => {
          document.addEventListener('click', closeMenu, true);
          document.addEventListener('contextmenu', closeMenu, true);
        }, 10);
      };
      
      const contextMenuHandler = (evt) => {
        const pt = mxUtils.convertPoint(graph.container, mxEvent.getClientX(evt), mxEvent.getClientY(evt));
        const cell = graph.getCellAt(pt.x, pt.y);
        
        const eventObj = {
          getProperty: (prop) => {
            if (prop === 'event') return evt;
            if (prop === 'cell') return cell;
            return null;
          },
          consume: () => {
            mxEvent.consume(evt);
          },
          preventDefault: () => {
            evt.preventDefault();
          }
        };
        
        handleContextMenu(graph, eventObj);
      };
      
      mxEvent.addListener(graph.container, 'contextmenu', contextMenuHandler);
      
      // Node click handler - shape selection için
      graph.addListener(mxEvent.CLICK, function(sender, evt) {
        const cell = evt.getProperty('cell');
        if (cell && graph.getModel().isVertex(cell) && setSelectedShape) {
          setSelectedShape(cell.getId());
          
          // Seçili şeklin rengini al
          const style = cell.getStyle() || '';
          const fillColorMatch = style.match(/fillColor=([^;]+)/);
          if (fillColorMatch) {
            setSelectedShapeColor(fillColorMatch[1]);
          } else {
            setSelectedShapeColor('#FFFF00');
          }
        }
      });
      
      // Helper function: Bir source'dan çıkan edge'lerin en yüksek kosul değerini bul
      const getMaxKosulForSource = (source, excludeEdge = null) => {
        if (!source) return 0;
        
        const model = graph.getModel();
        const sourceId = source.getId ? String(source.getId()) : String(source);
        const kosulValues = [];
        
        // 1. Graph'taki edge'lerin label'larından kosul değerlerini çıkar
        const allCells = model.getChildCells(model.getRoot());
        const allEdges = allCells.filter(c => model.isEdge(c));
        
        const sourceEdges = allEdges.filter(edge => {
          // Exclude edilecek edge'i atla
          if (excludeEdge && edge === excludeEdge) return false;
          
          const edgeSource = edge.getTerminal(true);
          if (!edgeSource) return false;
          
          const edgeSourceId = edgeSource.getId ? String(edgeSource.getId()) : String(edgeSource);
          
          // Source ID'lerini karşılaştır
          return sourceId === edgeSourceId;
        });
        
        // Graph'taki edge'lerin label'larından kosul çıkar
        sourceEdges.forEach(edge => {
          const label = edge.getValue() ? String(edge.getValue()).trim() : '';
          if (label && label.includes(':Next')) {
            const kosulMatch = label.match(/(\d+):Next/);
            if (kosulMatch) {
              const numValue = parseInt(kosulMatch[1], 10);
              if (!isNaN(numValue) && numValue > 0) {
                kosulValues.push(numValue);
              }
            }
          }
        });
        
        // 2. Workflow data'daki edge'lerin kosul değerlerini kontrol et
        const currentWorkflowData = workflowDataRef.current || workflowData;
        if (currentWorkflowData && currentWorkflowData.contents) {
          currentWorkflowData.contents.forEach(item => {
            // Edge kontrolü
            if (item && (item.edge === "1" || item.edge === 1 || item.edge === true)) {
              const edgeSourceId = item.source ? String(item.source) : '';
              
              // Aynı source'tan çıkan edge mi?
              if (edgeSourceId === sourceId) {
                // Exclude edilecek edge'i atla (eğer ID'si varsa)
                if (excludeEdge) {
                  const excludeEdgeId = excludeEdge.getId ? String(excludeEdge.getId()) : '';
                  const itemId = item.id ? String(item.id) : '';
                  if (excludeEdgeId && itemId === excludeEdgeId) {
                    return; // Skip this edge
                  }
                }
                
                // Kosul değerini kontrol et
                if (item.kosul) {
                  const kosulValue = parseInt(String(item.kosul), 10);
                  if (!isNaN(kosulValue) && kosulValue > 0) {
                    kosulValues.push(kosulValue);
                  }
                }
                
                // Label'dan da kosul çıkarmayı dene (eğer kosul field'ı yoksa)
                if (!item.kosul && item.definition) {
                  const label = String(item.definition).trim();
                  if (label && label.includes(':Next')) {
                    const kosulMatch = label.match(/(\d+):Next/);
                    if (kosulMatch) {
                      const numValue = parseInt(kosulMatch[1], 10);
                      if (!isNaN(numValue) && numValue > 0) {
                        kosulValues.push(numValue);
                      }
                    }
                  }
                }
              }
            }
          });
        }
        
        // En yüksek kosul değerini bul (eğer yoksa 0)
        const maxKosul = kosulValues.length > 0 ? Math.max(...kosulValues) : 0;
        
        return maxKosul;
      };
      
      // Edge connection handler - kosul değeri ekle (10'un katları: 10, 20, 30, ...)
      graph.addListener(mxEvent.CONNECT, function(sender, evt) {
        const edge = evt.getProperty('edge');
        const source = edge ? edge.getTerminal(true) : null;
        
        if (edge && source && graph.getModel().isVertex(source)) {
          // Edge model'e eklendikten sonra label'ı set et
          setTimeout(() => {
            try {
              const model = graph.getModel();
              
              // Edge ID'sini UUID formatına çevir (eğer zaten UUID formatında değilse)
              const currentId = edge.getId() ? String(edge.getId()) : '';
              const isUUIDFormat = /^[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}$/i.test(currentId);
              
              if (!isUUIDFormat) {
                const newId = generateUUID();
                model.beginUpdate();
                try {
                  // mxGraph'te cell ID'sini değiştirmek için doğrudan cell.id kullan
                  edge.id = newId;
                  // Model'in cell dictionary'sini güncelle
                  if (model.cells) {
                    delete model.cells[currentId];
                    model.cells[newId] = edge;
                  }
                } finally {
                  model.endUpdate();
                }
              }
              
              // Edge'in mevcut label'ını kontrol et
              const currentLabel = edge.getValue() ? String(edge.getValue()).trim() : '';
              
              // Eğer label yoksa veya :Next ile bitmiyorsa, label'ı hesapla ve set et
              if (!currentLabel || currentLabel === '' || !currentLabel.includes(':Next')) {
                
                // Aynı source'dan çıkan edge'lerin en yüksek kosul değerini bul
                // Edge zaten model'e eklenmiş, bu yüzden excludeEdge olarak geç
                const maxKosul = getMaxKosulForSource(source, edge);
                // Eğer hiç edge yoksa maxKosul = 0, yeni kosul = 10
                // Eğer edge varsa, en yüksek kosul + 10
                const newKosul = maxKosul === 0 ? 10 : maxKosul + 10;
                
                // Debug log
                
                
                // Edge label'ını ve style'ını güncelle
                model.beginUpdate();
                try {
                  // Edge value'sunu set et
                  edge.setValue(`${newKosul}:Next`);
                  
                  // Edge style'ını güncelle - label'ın görünmesi ve editable olması için
                  let style = edge.getStyle() || '';
                  
                  // Label görünürlüğü için
                  if (!style.includes('labelBackgroundColor')) {
                    style += 'labelBackgroundColor=#fff;';
                  }
                  if (!style.includes('labelBorderColor')) {
                    style += 'labelBorderColor=#e0e0e0;';
                  }
                  if (!style.includes('fontColor')) {
                    style += 'fontColor=#000000;';
                  }
                  
                  // Editable olması için
                  if (!style.includes('editable=1')) {
                    style = style.replace(/editable=[^;]*;?/g, '');
                    style += 'editable=1;';
                  }
                  
                  edge.setStyle(style);
                  
                  // Graph'i refresh et
                  graph.refresh(edge);
                  graph.view.validate();
                  
                } finally {
                  model.endUpdate();
                }
              } else {
              }
            } catch (error) {
            }
          }, 150);
        } else {
        }
      });
      
      // CELLS_ADDED event'ini de dinle - yedek olarak label'ı kontrol et
      graph.addListener(mxEvent.CELLS_ADDED, function(sender, evt) {
        const cells = evt.getProperty('cells');
        
        if (cells && Array.isArray(cells)) {
          cells.forEach(cell => {
            if (graph.getModel().isEdge(cell)) {
              
              // Edge ID'sini UUID formatına çevir (eğer zaten UUID formatında değilse)
              const currentId = cell.getId() ? String(cell.getId()) : '';
              const isUUIDFormat = /^[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}$/i.test(currentId);
              
              if (!isUUIDFormat) {
                const model = graph.getModel();
                const newId = generateUUID();
                model.beginUpdate();
                try {
                  // mxGraph'te cell ID'sini değiştirmek için doğrudan cell.id kullan
                  cell.id = newId;
                  // Model'in cell dictionary'sini güncelle
                  if (model.cells) {
                    delete model.cells[currentId];
                    model.cells[newId] = cell;
                  }
                } finally {
                  model.endUpdate();
                }
              }
              
              // Edge'in label'ı var mı kontrol et
              const currentValue = cell.getValue() ? String(cell.getValue()).trim() : '';
              
              // Label yoksa veya :Next ile bitmiyorsa, yeni label hesapla ve set et
              if (!currentValue || currentValue === '' || !currentValue.includes(':Next')) {
                
                setTimeout(() => {
                  try {
                    const source = cell.getTerminal(true);
                    if (source && graph.getModel().isVertex(source)) {
                      const model = graph.getModel();
                      
                      // Aynı source'dan çıkan edge'lerin en yüksek kosul değerini bul
                      const maxKosul = getMaxKosulForSource(source, cell);
                      // Eğer hiç edge yoksa maxKosul = 0, yeni kosul = 10
                      // Eğer edge varsa, en yüksek kosul + 10
                      const newKosul = maxKosul === 0 ? 10 : maxKosul + 10;
                      
                      // Debug log
                      
                      
                      model.beginUpdate();
                      try {
                        cell.setValue(`${newKosul}:Next`);
                        let style = cell.getStyle() || '';
                        if (!style.includes('labelBackgroundColor')) {
                          style += 'labelBackgroundColor=#fff;';
                        }
                        if (!style.includes('labelBorderColor')) {
                          style += 'labelBorderColor=#e0e0e0;';
                        }
                        if (!style.includes('fontColor')) {
                          style += 'fontColor=#000000;';
                        }
                        if (!style.includes('editable=1')) {
                          style = style.replace(/editable=[^;]*;?/g, '');
                          style += 'editable=1;';
                        }
                        cell.setStyle(style);
                        graph.refresh(cell);
                        graph.view.validate();
                        
                      } finally {
                        model.endUpdate();
                      }
                    }
                  } catch (error) {
                  }
                }, 200);
              } else {
              }
            }
          });
        }
      });
      
      // Render workflow data - aktif tab'ın workflowData'sını kullan
      const activeTab = tabs.find(tab => tab.id === activeTabId);
      const currentWorkflowData = activeTab?.workflowData || workflowDataRef.current;
      if (currentWorkflowData && currentWorkflowData.contents) {
        const parent = graph.getDefaultParent();
        const nodes = {};
        graph.getModel().beginUpdate();
        try {
          currentWorkflowData.contents
            .filter(item => item && (item.vertex === "1" || item.vertex === 1 || item.vertex === true))
            .forEach(node => {
              const nodeId = node.id ? String(node.id) : '';
              const nodeStyle = node.style ? String(node.style) : '';
              const nodeDefinition = node.definition ? String(node.definition) : '';
              
              let style = nodeStyle || '';
              if (style && !style.endsWith(';')) {
                style += ';';
              }
              
              // Shape bilgisini style'dan çıkar
              let shapeNameFromStyle = '';
              const shapeMatch = style.match(/shape=([^;]+)/);
              if (shapeMatch) {
                shapeNameFromStyle = shapeMatch[1];
              }
              
              // Eğer style'da shape bilgisi yoksa, node'un definition'ından veya başka bir yerden shape name'i bulmaya çalış
              // Ancak genellikle style'da shape bilgisi olmalı
              // Image'ları kaldır, sadece mxGraph shape'lerini kullan
              style = style.replace(/image=[^;]*;?/g, '');
              style = style.replace(/imageWidth=[^;]*;?/g, '');
              style = style.replace(/imageHeight=[^;]*;?/g, '');
              style = style.replace(/imageAspect=[^;]*;?/g, '');
              style = style.replace(/imageAlign=[^;]*;?/g, '');
              style = style.replace(/imageVerticalAlign=[^;]*;?/g, '');
              
              // Shape'i mxGraph'in built-in shape'lerine map et
              let mxShape = 'rectangle'; // default
              if (shapeNameFromStyle === 'ellipse' || shapeNameFromStyle === 'startCircle' || shapeNameFromStyle === 'stopCircle') {
                mxShape = 'ellipse';
              } else if (shapeNameFromStyle === 'rhombus') {
                mxShape = 'rhombus';
              } else if (shapeNameFromStyle === 'triangle') {
                mxShape = 'triangle'; // Custom triangle shape
              } else if (shapeNameFromStyle === 'hexagon') {
                mxShape = 'hexagon';
              } else if (shapeNameFromStyle === 'cylinder') {
                mxShape = 'cylinder';
              } else if (shapeNameFromStyle === 'preProcess' || shapeNameFromStyle === 'predefinedProcess') {
                mxShape = 'predefinedProcess'; // Custom predefined process (double border)
              } else if (shapeNameFromStyle === 'rectangle') {
                mxShape = 'rectangle';
              } else if (!shapeNameFromStyle || shapeNameFromStyle === 'image') {
                // Eğer shape belirtilmemişse veya image ise, rectangle kullan
                mxShape = 'rectangle';
              } else {
                // Mevcut shape'i koru (eğer geçerli bir mxGraph shape'i ise)
                mxShape = shapeNameFromStyle;
              }
              
              // Shape'i güncelle
              style = style.replace(/shape=[^;]*;?/g, '');
              // fillColor sabit kalmalı - şekil tipine göre shapes array'indeki color kullanılmalı
              // fillColor'ı değiştirmiyoruz, mevcut fillColor korunuyor
              if (!style.includes('fillColor')) {
                // Eğer fillColor yoksa, şekil tipine göre default renk ekle
                const shapeColor = shapes.find(s => s.mxShape === mxShape)?.color || '#FFFF00';
                style += `fillColor=${shapeColor};`;
              }
              // Font rengini ekle - tüm renklerde belirgin olması için
              if (!style.includes('fontColor')) {
                style += 'fontColor=#000000;';
              }
              style = style.replace(/selectable=[^;]*;?/g, '');
              style = style.replace(/movable=[^;]*;?/g, '');
              style = style.replace(/resizable=[^;]*;?/g, '');
              style = style.replace(/connectable=[^;]*;?/g, '');
              style = style.replace(/;;+/g, ';');
              if (!style.endsWith(';')) style += ';';
              
              const x = parseInt(node.GeometryX) || 40;
              const y = parseInt(node.GeometryY) || 40;
              let w = parseInt(node.GeometryWidth) || 60;
              let h = parseInt(node.GeometryHeight) || 60; // Default boyutlar küçültüldü
              
              // Başlangıç ve bitiş şekilleri için tam yuvarlak (width = height)
              if (mxShape === 'ellipse' && (shapeNameFromStyle === 'startCircle' || shapeNameFromStyle === 'stopCircle')) {
                // Width ve height'ın ortalamasını al veya daha büyük olanı kullan
                const avgSize = Math.max(w, h);
                w = avgSize;
                h = avgSize;
              }
              
              const label = nodeDefinition;
              
              // mxGraph shape'ini ekle
              style += `shape=${mxShape};`;
              
              if (isDesignMode) {
                style += 'selectable=1;movable=1;resizable=1;connectable=1;';
              } else {
                style += 'selectable=0;movable=0;resizable=0;';
              }
              
              const vertex = graph.insertVertex(parent, nodeId, label, x, y, w, h, style);
              nodes[nodeId] = vertex;
              
              // Eğer bu şeklin include'u varsa overlay ekle
              if (node.include && node.include !== '' && node.include !== 'null' && node.include !== null) {
                // Include edilmiş şekiller listesine ekle
                setShapesWithInclude(prev => new Set([...prev, nodeId]));
                
                // Overlay ekle - setTimeout ile graph render tamamlandıktan sonra
                setTimeout(() => {
                  try {
                    const attachIcon = createAttachIcon();
                    const overlay = new mxCellOverlay(
                      attachIcon,
                      'Include edildi',
                      mxConstants.ALIGN_RIGHT,
                      mxConstants.ALIGN_BOTTOM,
                      new mxPoint(-2, -2) // Sağ alt köşede, şeklin içinde
                    );
                    graph.addCellOverlay(vertex, overlay);
                    graph.getView().invalidate(vertex);
                    graph.getView().validate();
                    graph.refresh();
                  } catch (error) {
                    console.error('Overlay ekleme hatası:', error);
                  }
                }, 50);
              }
            });

          currentWorkflowData.contents
            .filter(item => item && (item.edge === "1" || item.edge === 1 || item.edge === true))
            .forEach(edge => {
              const edgeId = edge.id ? String(edge.id) : '';
              const edgeStyle = edge.style ? String(edge.style) : '';
              const edgeDefinition = edge.definition ? String(edge.definition) : '';
              const edgeKosul = edge.kosul ? String(edge.kosul) : '';
              const sourceId = edge.source ? String(edge.source) : '';
              const targetId = edge.target ? String(edge.target) : '';
              
              const from = nodes[sourceId];
              const to = nodes[targetId];
              if (from && to) {
                let style = edgeStyle;
                if (!style.includes('endArrow')) style += 'endArrow=block;';
                style += 'edgeStyle=orthogonalEdgeStyle;rounded=0;orthogonalLoop=1;';
                // perimeter=1 ile edge'ler shape'in border'ına tam temas eder
                if (!style.includes('perimeter')) style += 'perimeter=1;';
                style = style.replace(/bendable=[^;]*;?/g, '');
                style = style.replace(/editable=[^;]*;?/g, '');
                style = style.replace(/selectable=[^;]*;?/g, '');
                style = style.replace(/movable=[^;]*;?/g, '');
                style = style.replace(/;;+/g, ';');
                if (!style.endsWith(';')) style += ';';
                
                if (isDesignMode) {
                  style += 'bendable=1;editable=1;selectable=1;movable=1;';
                } else {
                  style += 'bendable=0;editable=0;selectable=0;movable=0;';
                }
                style += 'labelBackgroundColor=#fff;labelBorderColor=#e0e0e0;fontColor=#000000;';
                
                let label = edgeDefinition;
                if (edgeKosul && !label.includes(':Next')) {
                  label = `${edgeKosul}:Next`;
                }
                
                const e = graph.insertEdge(parent, edgeId, label, from, to, style);
                
                if (edge.GeometryPoints) {
                  try {
                    const points = JSON.parse(edge.GeometryPoints);
                    if (Array.isArray(points) && points.length > 0) {
                      const geo = e.getGeometry().clone();
                      geo.points = points.map(pt => new window.mxPoint(pt.x, pt.y));
                      e.setGeometry(geo);
                    }
                  } catch (err) {
                    // ignore
                  }
                }
              }
            });
        } finally {
          graph.getModel().endUpdate();
        }
        
        graph.view.validate();
        graph.refresh();
        
        // Include edilmiş şekillere overlay ekle (workflowData'dan)
        setTimeout(() => {
          if (currentWorkflowData && currentWorkflowData.contents) {
            currentWorkflowData.contents
              .filter(item => item && (item.vertex === "1" || item.vertex === 1 || item.vertex === true))
              .forEach(node => {
                const nodeId = node.id ? String(node.id) : '';
                if (node.include && node.include !== '' && node.include !== 'null' && node.include !== null) {
                  const cell = graph.getModel().getCell(nodeId);
                  if (cell && graph.getModel().isVertex(cell)) {
                    // Mevcut overlay'i kaldır (varsa)
                    graph.removeCellOverlay(cell);
                    
                    // Yeni overlay ekle
                    const attachIcon = createAttachIcon();
                    const overlay = new mxCellOverlay(
                      attachIcon,
                      'Include edildi',
                      mxConstants.ALIGN_RIGHT,
                      mxConstants.ALIGN_BOTTOM,
                      new mxPoint(-2, -2) // Sağ alt köşede, şeklin içinde
                    );
                    overlay.addListener(mxEvent.CLICK, function(sender, evt) {
                      const cell = evt.getProperty('cell');
                      if (cell) {
                        openIncludeDetails(cell.getId(), { showPopup: false });
                      }
                    });
                    graph.addCellOverlay(cell, overlay);
                    graph.getView().invalidate(cell);
                    graph.getView().validate();
                    graph.refresh();
                    
                    // Include edilmiş şekiller listesine ekle
                    setShapesWithInclude(prev => new Set([...prev, nodeId]));
                  }
                }
              });
            
            graph.view.validate();
            graph.refresh();
          }
        }, 150);
        
        if (isDesignMode) {
          setTimeout(() => {
            const cells = graph.getChildCells(graph.getDefaultParent());
            cells.forEach(cell => {
              if (graph.getModel().isVertex(cell)) {
                let style = cell.getStyle() || '';
                if (!style.includes('selectable=1')) {
                  style = style.replace(/selectable=[^;]*;?/g, '');
                  style = style.replace(/movable=[^;]*;?/g, '');
                  style = style.replace(/resizable=[^;]*;?/g, '');
                  cell.setStyle(style + ';selectable=1;movable=1;resizable=1;');
                }
              }
            });
            graph.view.validate();
            graph.refresh();
          }, 50);
        }
        
        workflowIdRef.current = currentWorkflowId;
      }

      return () => {
        try {
          if (container && contextMenuHandler) {
            mxEvent.removeListener(container, 'contextmenu', contextMenuHandler);
          }
          if (container && scrollHandler) {
            container.removeEventListener('scroll', scrollHandler);
          }
          if (graph && graph.view && updateRulersHandler) {
            graph.view.removeListener(mxEvent.SCALE, updateRulersHandler);
            graph.view.removeListener(mxEvent.SCALE_AND_TRANSLATE, updateRulersHandler);
            graph.view.removeListener(mxEvent.TRANSLATE, updateRulersHandler);
          }
          if (resizeObserver) {
            resizeObserver.disconnect();
          }
          if (rubberbandRef.current) {
            try {
              rubberbandRef.current.destroy();
            } catch (e) {
              // ignore
            }
            rubberbandRef.current = null;
          }
          // Cleanup middle mouse handlers
          if (graph && graph.container && graph._middleMouseHandlers) {
            graph.container.removeEventListener('mousedown', graph._middleMouseHandlers.down);
            graph.container.removeEventListener('mouseup', graph._middleMouseHandlers.up);
          }
          // Cleanup smooth zoom handler
          if (graph && graph.container && graph._smoothZoomHandler) {
            graph.container.removeEventListener('wheel', graph._smoothZoomHandler);
          }
          if (graph) {
            try {
              graph.destroy();
            } catch (error) {
              // ignore
            }
          }
          if (graphRef.current) {
            graphRef.current = null;
          }
          if (setSelectedShape) {
            setSelectedShape(null);
          }
        } catch (error) {
          // ignore
        }
      };
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDesignMode, activeTabId, tabs]);

  // Update graph when design mode changes
  useEffect(() => {
    if (!graphRef.current) {
      return;
    }
    if (!graphRef.current._applyDesignModeSettings) {
      return;
    }
    
    const graph = graphRef.current;
    
    if (!rubberbandRef.current) {
      rubberbandRef.current = new mxRubberband(graph);
    }
    
    graph._applyDesignModeSettings(isDesignMode);
    
    if (graphContainerRef.current) {
      graphContainerRef.current.style.cursor = isDesignMode ? 'default' : 'grab';
      if (graph.container) {
        graph.container.style.cursor = isDesignMode ? 'default' : 'grab';
      }
    }
    
    graph.getModel().beginUpdate();
    try {
      const cells = graph.getChildCells(graph.getDefaultParent());
      cells.forEach((cell) => {
        if (graph.getModel().isVertex(cell)) {
          let style = cell.getStyle() || '';
          style = style.replace(/selectable=[^;]*;?/g, '');
          style = style.replace(/movable=[^;]*;?/g, '');
          style = style.replace(/resizable=[^;]*;?/g, '');
          style = style.replace(/connectable=[^;]*;?/g, '');
          style = style.replace(/;;+/g, ';');
          if (!style.endsWith(';')) style += ';';
          
          if (isDesignMode) {
            cell.setStyle(style + 'selectable=1;movable=1;resizable=1;connectable=1;');
          } else {
            cell.setStyle(style + 'selectable=0;movable=0;resizable=0;');
          }
          graph.refresh(cell);
        } else if (graph.getModel().isEdge(cell)) {
          let style = cell.getStyle() || '';
          style = style.replace(/editable=[^;]*;?/g, '');
          style = style.replace(/bendable=[^;]*;?/g, '');
          style = style.replace(/selectable=[^;]*;?/g, '');
          style = style.replace(/movable=[^;]*;?/g, '');
          style = style.replace(/;;+/g, ';');
          if (!style.endsWith(';')) style += ';';
          
          if (isDesignMode) {
            cell.setStyle(style + 'editable=1;bendable=1;selectable=1;movable=1;');
          } else {
            cell.setStyle(style + 'editable=0;bendable=0;selectable=0;movable=0;');
          }
          graph.refresh(cell);
        }
      });
    } finally {
      graph.getModel().endUpdate();
      graph.view.validate();
      graph.refresh();
    }
    
    setTimeout(() => {
      if (graph.container) {
        graph.container.style.pointerEvents = 'auto';
      }
      if (graph.view) {
        if (graph.view.canvas) {
          graph.view.canvas.style.pointerEvents = 'auto';
        }
        if (graph.view.backgroundPane) {
          graph.view.backgroundPane.style.pointerEvents = 'none';
        }
        if (graph.view.drawPane) {
          graph.view.drawPane.style.pointerEvents = 'auto';
        }
        if (graph.view.overlayPane) {
          graph.view.overlayPane.style.pointerEvents = 'auto';
        }
      }
    }, 10);
  }, [isDesignMode]);

  // ========== KEYBOARD SHORTCUTS ==========
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Herhangi bir input/textarea/contenteditable odaktaysa global hotkey'lere karışma
      // (örn. Prompt Düzenle textarea'sında Space yazılamıyordu)
      try {
        const activeElement = document.activeElement;
        const tag = activeElement?.tagName ? String(activeElement.tagName).toUpperCase() : '';
        const isTextField =
          tag === 'INPUT' ||
          tag === 'TEXTAREA' ||
          tag === 'SELECT' ||
          !!activeElement?.isContentEditable ||
          !!activeElement?.closest?.('[data-ignore-global-hotkeys="true"]') ||
          !!activeElement?.closest?.('.monaco-editor') ||
          !!activeElement?.closest?.('.monaco-inputbox');
        if (isTextField) {
          return;
        }
      } catch (_e) {
        // ignore
      }

      // Code Editor açık ve focus editor'deyse, TÜM keyboard event'lerini engelleme
      if (showCodeEditor && editorRef.current) {
        try {
          const editorDomNode = editorRef.current.getDomNode();
          const activeElement = document.activeElement;
          
          // Monaco Editor içindeki tüm input alanlarını kontrol et
          const isInEditor = editorDomNode && (
            editorDomNode.contains(activeElement) || 
            activeElement === editorDomNode ||
            activeElement.closest('.monaco-editor') ||
            activeElement.closest('.monaco-inputbox') ||
            activeElement.tagName === 'TEXTAREA' ||
            activeElement.tagName === 'INPUT' ||
            activeElement.isContentEditable
          );
          
          if (isInEditor) {
            // Editor içindeyse, hiçbir keyboard event'ini yakalama
            return;
          }
        } catch (error) {
          // Hata durumunda da devam etme, editor'a bırak
          console.warn('Editor kontrol hatası:', error);
        }
      }
      
      // Space tuşu basılı tutulduğunda pan yapılabilir
      if (e.code === 'Space' && !e.repeat) {
        e.preventDefault();
        setIsSpacePressed(true);
        if (graphRef.current && graphRef.current.panningHandler) {
          // Space tuşu basılıyken sadece boş alanda pan yapılabilir
          graphRef.current.panningHandler.useLeftButtonForPanning = true;
          graphRef.current.panningHandler.ignoreCell = false; // Cell'lerin üzerinde pan yapma
        }
        if (graphRef.current && graphRef.current.container) {
          // Space pan modunu işaretle
          graphRef.current.container.dataset.spacePan = 'true';
          graphRef.current.container.style.cursor = 'grab';
          // Tüm graph container'ına grab cursor'ı uygula
          if (graphRef.current.view && graphRef.current.view.canvas) {
            graphRef.current.view.canvas.style.cursor = 'grab';
          }
        }
        return;
      }
      // Ctrl+K veya Cmd+K ile AI Prompt aç
      if ((e.ctrlKey || e.metaKey) && e.key === 'k' && showCodeEditor) {
        e.preventDefault();
        handleOpenAIPrompt();
        return;
      }
      
      if (e.key === 'Escape') {
        if (showAIPrompt) {
          setShowAIPrompt(false);
          setAiPrompt('');
        } else if (showCodeEditor) {
          setShowCodeEditor(false);
        } else if (onClose) {
          onClose();
        }
      } else if (e.key === 'Delete' && isDesignMode && graphRef.current) {
        const graph = graphRef.current;
        const cells = graph.getSelectionCells();
        if (cells && cells.length > 0) {
          // Show confirmation dialog
          const model = graph.getModel();
          const vertexCount = cells.filter(c => model.isVertex(c)).length;
          const edgeCount = cells.filter(c => model.isEdge(c)).length;
          
          let message = 'Are you sure you want to delete ';
          if (vertexCount > 0 && edgeCount > 0) {
            message += `${vertexCount} shape(s) and ${edgeCount} connection(s)`;
          } else if (vertexCount > 0) {
            message += `${vertexCount} shape(s)`;
          } else {
            message += `${edgeCount} connection(s)`;
          }
          message += '?\n\nThis action cannot be undone.';
          
          if (window.confirm(message)) {
            graph.removeCells(cells);
          }
        }
      }
    };
    
    const handleKeyUp = (e) => {
      // Input/textarea/contenteditable odaktaysa global hotkey'lere karışma
      try {
        const activeElement = document.activeElement;
        const tag = activeElement?.tagName ? String(activeElement.tagName).toUpperCase() : '';
        const isTextField =
          tag === 'INPUT' ||
          tag === 'TEXTAREA' ||
          tag === 'SELECT' ||
          !!activeElement?.isContentEditable ||
          !!activeElement?.closest?.('[data-ignore-global-hotkeys="true"]') ||
          !!activeElement?.closest?.('.monaco-editor') ||
          !!activeElement?.closest?.('.monaco-inputbox');
        if (isTextField) {
          return;
        }
      } catch (_e) {
        // ignore
      }

      // Code Editor açık ve focus editor'deyse, TÜM keyboard event'lerini engelleme
      if (showCodeEditor && editorRef.current) {
        try {
          const editorDomNode = editorRef.current.getDomNode();
          const activeElement = document.activeElement;
          
          // Monaco Editor içindeki tüm input alanlarını kontrol et
          const isInEditor = editorDomNode && (
            editorDomNode.contains(activeElement) || 
            activeElement === editorDomNode ||
            activeElement.closest('.monaco-editor') ||
            activeElement.closest('.monaco-inputbox') ||
            activeElement.tagName === 'TEXTAREA' ||
            activeElement.tagName === 'INPUT' ||
            activeElement.isContentEditable
          );
          
          if (isInEditor) {
            // Editor içindeyse, hiçbir keyboard event'ini yakalama
            return;
          }
        } catch (error) {
          // Hata durumunda da devam etme, editor'a bırak
          console.warn('Editor kontrol hatası:', error);
        }
      }
      
      // Space tuşu bırakıldığında pan modunu kapat
      if (e.code === 'Space') {
        e.preventDefault();
        setIsSpacePressed(false);
        if (graphRef.current && graphRef.current.panningHandler) {
          graphRef.current.panningHandler.useLeftButtonForPanning = false;
          graphRef.current.panningHandler.ignoreCell = false;
        }
        if (graphRef.current && graphRef.current.container) {
          // Space pan modunu kaldır
          delete graphRef.current.container.dataset.spacePan;
          graphRef.current.container.style.cursor = 'default';
          if (graphRef.current.view && graphRef.current.view.canvas) {
            graphRef.current.view.canvas.style.cursor = 'default';
          }
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    document.body.style.overflow = 'hidden';
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      document.body.style.overflow = 'auto';
    };
  }, [showCodeEditor, onClose, isDesignMode, setIsSpacePressed]);

  // ========== RESIZE HANDLER ==========
  useEffect(() => {
    if (!isResizing) return;
    
    // mxGraph container'ını tamamen devre dışı bırak
    if (graphContainerRef.current) {
      graphContainerRef.current.style.pointerEvents = 'none';
    }
    
    // mxGraph'in panningHandler'ını devre dışı bırak
    let originalUseLeftButtonForPanning = null;
    let originalIgnoreCell = null;
    if (graphRef.current && graphRef.current.panningHandler) {
      originalUseLeftButtonForPanning = graphRef.current.panningHandler.useLeftButtonForPanning;
      originalIgnoreCell = graphRef.current.panningHandler.ignoreCell;
      graphRef.current.panningHandler.useLeftButtonForPanning = false;
      graphRef.current.panningHandler.ignoreCell = true;
    }
    
    const handleMouseMove = (e) => {
      // Mouse'un başlangıç pozisyonundan ne kadar hareket ettiğini hesapla
      const deltaX = resizeStartXRef.current - e.clientX; // Sola giderse pozitif, sağa giderse negatif
      
      // Window genişliğini al
      const windowWidth = window.innerWidth;
      
      // Delta'yı yüzdeye çevir (sola giderse code panel genişler, sağa giderse küçülür)
      const deltaPercent = (deltaX / windowWidth) * 100;
      
      // Yeni genişliği hesapla
      const newWidth = resizeStartWidthRef.current + deltaPercent;
      
      // Sınırlar: min 30%, max 70%
      const clampedWidth = Math.max(30, Math.min(70, newWidth));
      setCodePanelWidth(clampedWidth);
    };
    
    const handleMouseUp = () => {
      setIsResizing(false);
      
      // mxGraph container'ını tekrar etkinleştir
      if (graphContainerRef.current) {
        graphContainerRef.current.style.pointerEvents = 'auto';
      }
      
      // mxGraph'in panningHandler'ını geri yükle
      if (graphRef.current && graphRef.current.panningHandler) {
        if (originalUseLeftButtonForPanning !== null) {
          graphRef.current.panningHandler.useLeftButtonForPanning = originalUseLeftButtonForPanning;
        }
        if (originalIgnoreCell !== null) {
          graphRef.current.panningHandler.ignoreCell = originalIgnoreCell;
        }
      }
    };
    
    // Window'a event listener ekle - mxGraph'in event'lerinden önce yakala
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      
      // Cleanup - mxGraph'i geri yükle
      if (graphContainerRef.current) {
        graphContainerRef.current.style.pointerEvents = 'auto';
      }
      if (graphRef.current && graphRef.current.panningHandler) {
        if (originalUseLeftButtonForPanning !== null) {
          graphRef.current.panningHandler.useLeftButtonForPanning = originalUseLeftButtonForPanning;
        }
        if (originalIgnoreCell !== null) {
          graphRef.current.panningHandler.ignoreCell = originalIgnoreCell;
        }
      }
    };
  }, [isResizing]);

  // ========== RENDER ==========
  const modalContent = (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: '#1e1e1e',
      display: 'flex',
      flexDirection: 'column',
      zIndex: 99999,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }}>
      <FlowChartToolbar
        workflowData={workflowData}
        isDesignMode={isDesignMode}
        setIsDesignMode={setIsDesignMode}
        showCodeEditor={showCodeEditor}
        setShowCodeEditor={setShowCodeEditor}
        selectedShape={selectedShape}
        onBulkUpdateScriptsFromComments={handleBulkUpdateScriptsFromComments}
        isBulkUpdatingScriptsFromComments={isBulkAiGenerating || isBulkAiApplying}
        onCompileScriptsFromJsonPrompts={handleCompileScriptsFromJsonPrompts}
        isCompilingScriptsFromJsonPrompts={isCompileAiGenerating || isCompileAiApplying}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onZoomReset={handleZoomReset}
        onSave={handleSave}
        isSaving={isSaving}
        onFetchFxGroups={fetchFxGroups}
        isLoadingFxGroups={isLoadingFxGroups}
        onMoveRequest={handleMoveRequest}
        onClose={onClose}
        showTerminal={showTerminal}
        onToggleTerminal={() => setShowTerminal((prev) => !prev)}
        shapes={shapes}
        addShape={addShape}
      />

      {showBulkAiModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.72)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100003,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget && !isBulkAiGenerating && !isBulkAiApplying) {
              setShowBulkAiModal(false);
            }
          }}
        >
          <div
            style={{
              width: '94%',
              maxWidth: 1100,
              maxHeight: '86vh',
              background: '#2d2d2d',
              border: '1px solid #3e3e42',
              borderRadius: 10,
              boxShadow: '0 18px 60px rgba(0,0,0,0.6)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                padding: '14px 16px',
                borderBottom: '1px solid #3e3e42',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ color: '#fff', fontSize: 14, fontWeight: 700 }}>
                  AI: Yorum satırlarına göre script güncelle
                </div>
                <div style={{ color: '#9aa0a6', fontSize: 11 }}>
                  Bulunan şekil: {bulkAiItems.length} · Hazır: {bulkAiItems.filter((x) => x.status === 'ready').length} ·
                  Hatasız: {bulkAiItems.filter((x) => x.status !== 'error').length}
                </div>
              </div>
              <button
                onClick={() => setShowBulkAiModal(false)}
                disabled={isBulkAiGenerating || isBulkAiApplying}
                style={{
                  background: 'transparent',
                  border: '1px solid #3e3e42',
                  color: '#cccccc',
                  padding: '6px 10px',
                  borderRadius: 8,
                  cursor: isBulkAiGenerating || isBulkAiApplying ? 'not-allowed' : 'pointer',
                  opacity: isBulkAiGenerating || isBulkAiApplying ? 0.6 : 1,
                }}
                title="Kapat"
              >
                Kapat
              </button>
            </div>

            {bulkAiError && (
              <div
                style={{
                  padding: 12,
                  margin: 12,
                  borderRadius: 10,
                  background: 'rgba(231,76,60,0.12)',
                  border: '1px solid rgba(231,76,60,0.35)',
                  color: 'rgba(255,255,255,0.92)',
                  fontSize: 12,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {bulkAiError}
              </div>
            )}

            <div style={{ flex: 1, overflow: 'auto', padding: 12 }}>
              {bulkAiItems.length === 0 ? (
                <div style={{ padding: 18, color: '#9aa0a6', fontSize: 13 }}>Kayıt yok.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {bulkAiItems.map((item) => {
                    const expanded = !!bulkAiExpanded[item.shapeId];
                    const checked = bulkAiSelected[item.shapeId] !== false;
                    const statusColor =
                      item.status === 'ready'
                        ? '#22c55e'
                        : item.status === 'saved'
                          ? '#60a5fa'
                          : item.status === 'error'
                            ? '#ef4444'
                            : item.status === 'running' || item.status === 'saving'
                              ? '#f59e0b'
                              : '#9aa0a6';
                    return (
                      <div
                        key={item.shapeId}
                        style={{
                          border: '1px solid #3e3e42',
                          borderRadius: 10,
                          background: '#1e1e1e',
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            padding: '10px 12px',
                            borderBottom: expanded ? '1px solid #2a2a2a' : 'none',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={isBulkAiGenerating || isBulkAiApplying}
                            onChange={(e) =>
                              setBulkAiSelected((prev) => ({ ...prev, [item.shapeId]: e.target.checked }))
                            }
                          />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{
                                color: '#e6e6e6',
                                fontSize: 13,
                                fontWeight: 600,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                flexWrap: 'wrap',
                              }}
                            >
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {item.label}
                              </span>
                              <span style={{ color: '#9aa0a6', fontSize: 11 }}>({item.shapeId})</span>
                              <span
                                style={{
                                  color: statusColor,
                                  fontSize: 11,
                                  fontWeight: 700,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.3px',
                                }}
                              >
                                {item.status}
                              </span>
                              <span style={{ color: '#9aa0a6', fontSize: 11 }}>lang: {item.lang}</span>
                            </div>
                            {!!item.instructions?.length && (
                              <div style={{ color: '#9aa0a6', fontSize: 11, marginTop: 4 }}>
                                {item.instructions.slice(0, 2).join(' · ')}
                                {item.instructions.length > 2 ? ' …' : ''}
                              </div>
                            )}
                            {item.error && (
                              <div style={{ color: '#ff8a8a', fontSize: 11, marginTop: 4 }}>{item.error}</div>
                            )}
                          </div>
                          <button
                            onClick={() =>
                              setBulkAiExpanded((prev) => ({ ...prev, [item.shapeId]: !prev[item.shapeId] }))
                            }
                            style={{
                              background: 'transparent',
                              border: '1px solid #3e3e42',
                              color: '#cccccc',
                              padding: '6px 10px',
                              borderRadius: 8,
                              cursor: 'pointer',
                              fontSize: 12,
                            }}
                          >
                            {expanded ? 'Gizle' : 'Detay'}
                          </button>
                        </div>

                        {expanded && (
                          <div style={{ padding: 12, display: 'grid', gap: 10 }}>
                            <div style={{ color: '#cccccc', fontSize: 12, fontWeight: 700 }}>Talimatlar</div>
                            <div
                              style={{
                                background: '#0d1117',
                                border: '1px solid #2a2a2a',
                                borderRadius: 8,
                                padding: 10,
                                color: '#c9d1d9',
                                fontSize: 12,
                                whiteSpace: 'pre-wrap',
                              }}
                            >
                              {(item.instructions || []).map((x) => `- ${x}`).join('\n') || '—'}
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                              <div>
                                <div style={{ color: '#9aa0a6', fontSize: 11, marginBottom: 6 }}>Eski</div>
                                <pre
                                  style={{
                                    margin: 0,
                                    background: '#0d1117',
                                    border: '1px solid #2a2a2a',
                                    borderRadius: 8,
                                    padding: 10,
                                    color: '#c9d1d9',
                                    fontSize: 11,
                                    overflow: 'auto',
                                    maxHeight: 240,
                                  }}
                                >
                                  {item.oldCode || ''}
                                </pre>
                              </div>
                              <div>
                                <div style={{ color: '#9aa0a6', fontSize: 11, marginBottom: 6 }}>Yeni</div>
                                <pre
                                  style={{
                                    margin: 0,
                                    background: '#0d1117',
                                    border: '1px solid #2a2a2a',
                                    borderRadius: 8,
                                    padding: 10,
                                    color: '#c9d1d9',
                                    fontSize: 11,
                                    overflow: 'auto',
                                    maxHeight: 240,
                                  }}
                                >
                                  {item.newCode || ''}
                                </pre>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div
              style={{
                padding: 12,
                borderTop: '1px solid #3e3e42',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 10,
              }}
            >
              <div style={{ color: '#9aa0a6', fontSize: 11 }}>
                Not: “ready” durumundakiler değişiklik üretti. “nochange” kodu aynı döndürdü.
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={handleApplyBulkAiUpdates}
                  disabled={isBulkAiGenerating || isBulkAiApplying}
                  style={{
                    background: '#0e639c',
                    border: 'none',
                    color: 'white',
                    padding: '8px 14px',
                    borderRadius: 8,
                    cursor: isBulkAiGenerating || isBulkAiApplying ? 'not-allowed' : 'pointer',
                    opacity: isBulkAiGenerating || isBulkAiApplying ? 0.6 : 1,
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                  title="Seçili satırları uygular ve kaydeder"
                >
                  {isBulkAiApplying ? 'Kaydediliyor...' : 'Uygula + Kaydet'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCompileAiModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.72)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100003,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget && !isCompileAiGenerating && !isCompileAiApplying) {
              setShowCompileAiModal(false);
            }
          }}
        >
          <div
            style={{
              width: '94%',
              maxWidth: 1100,
              maxHeight: '86vh',
              background: '#2d2d2d',
              border: '1px solid #3e3e42',
              borderRadius: 10,
              boxShadow: '0 18px 60px rgba(0,0,0,0.6)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                padding: '14px 16px',
                borderBottom: '1px solid #3e3e42',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ color: '#fff', fontSize: 14, fontWeight: 700 }}>
                  AI: JSON Prompt’a Göre Derle (AI_GLOBAL + ai_prompt)
                </div>
                <div style={{ color: '#9aa0a6', fontSize: 11 }}>
                  Toplam: {compileAiItems.length} · Değişecek: {compileAiItems.filter((x) => x.status !== 'cached').length}{' '}
                  · Hazır: {compileAiItems.filter((x) => x.status === 'ready').length}{' '}
                  · Önbellekte (değişiklik yok): {compileAiItems.filter((x) => x.status === 'cached').length}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {/* Generate butonu - EN GÜNCEL promptlarla başlat */}
                {!isCompileAiGenerating && compileAiItems.length > 0 && compileAiItems.some(x => x.status !== 'cached' && x.status !== 'ready' && x.status !== 'saved') && (
                  <button
                    onClick={handleStartCompileAiGeneration}
                    disabled={isCompileAiApplying}
                    style={{
                      background: '#10b981',
                      border: 'none',
                      color: 'white',
                      padding: '6px 16px',
                      borderRadius: 8,
                      cursor: isCompileAiApplying ? 'not-allowed' : 'pointer',
                      opacity: isCompileAiApplying ? 0.6 : 1,
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                    title="EN GÜNCEL promptlarla kod üretmeye başla"
                  >
                    🚀 Generate
                  </button>
                )}
                <button
                  onClick={() => setShowCompileAiModal(false)}
                  disabled={isCompileAiGenerating || isCompileAiApplying}
                  style={{
                    background: 'transparent',
                    border: '1px solid #3e3e42',
                    color: '#cccccc',
                    padding: '6px 10px',
                    borderRadius: 8,
                    cursor: isCompileAiGenerating || isCompileAiApplying ? 'not-allowed' : 'pointer',
                    opacity: isCompileAiGenerating || isCompileAiApplying ? 0.6 : 1,
                  }}
                >
                  Kapat
                </button>
              </div>
            </div>

            {compileAiError && (
              <div
                style={{
                  padding: 12,
                  margin: 12,
                  borderRadius: 10,
                  background: 'rgba(231,76,60,0.12)',
                  border: '1px solid rgba(231,76,60,0.35)',
                  color: 'rgba(255,255,255,0.92)',
                  fontSize: 12,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {compileAiError}
              </div>
            )}

            <div style={{ flex: 1, overflow: 'auto', padding: 12 }}>
              {(() => {
                // ✅ Sadece değişiklik yapılacak şekilleri göster (cached olmayanlar)
                const itemsToShow = compileAiItems.filter((x) => x.status !== 'cached');
                
                if (itemsToShow.length === 0) {
                  return <div style={{ padding: 18, color: '#9aa0a6', fontSize: 13 }}>Tüm şekiller önbellekte. Değişiklik yok.</div>;
                }
                
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {itemsToShow.map((item) => {
                    const expanded = !!compileAiExpanded[item.shapeId];
                    const checked = compileAiSelected[item.shapeId] !== false;
                    const statusColor =
                      item.status === 'ready'
                        ? '#22c55e'
                        : item.status === 'saved'
                          ? '#60a5fa'
                          : item.status === 'error'
                            ? '#ef4444'
                            : item.status === 'running' || item.status === 'saving'
                              ? '#f59e0b'
                              : '#9aa0a6';
                    return (
                      <div
                        key={item.shapeId}
                        style={{
                          border: '1px solid #3e3e42',
                          borderRadius: 10,
                          background: '#1e1e1e',
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            padding: '10px 12px',
                            borderBottom: expanded ? '1px solid #2a2a2a' : 'none',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={isCompileAiGenerating || isCompileAiApplying}
                            onChange={(e) =>
                              setCompileAiSelected((prev) => ({ ...prev, [item.shapeId]: e.target.checked }))
                            }
                          />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{
                                color: '#e6e6e6',
                                fontSize: 13,
                                fontWeight: 600,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                flexWrap: 'wrap',
                              }}
                            >
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {item.label}
                              </span>
                              <span style={{ color: '#9aa0a6', fontSize: 11 }}>({item.shapeId})</span>
                              <span
                                style={{
                                  color: statusColor,
                                  fontSize: 11,
                                  fontWeight: 700,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.3px',
                                }}
                              >
                                {item.status}
                              </span>
                              <span style={{ color: '#9aa0a6', fontSize: 11 }}>lang: {item.lang}</span>
                            </div>
                            {!!item.aiPrompt && (
                              <div style={{ color: '#9aa0a6', fontSize: 11, marginTop: 4 }}>
                                {String(item.aiPrompt).split('\n')[0].slice(0, 160)}
                                {String(item.aiPrompt).split('\n')[0].length > 160 ? '…' : ''}
                              </div>
                            )}
                            {item.error && (
                              <div style={{ color: '#ff8a8a', fontSize: 11, marginTop: 4 }}>{item.error}</div>
                            )}
                          </div>
                          <button
                            onClick={() =>
                              setCompileAiExpanded((prev) => ({ ...prev, [item.shapeId]: !prev[item.shapeId] }))
                            }
                            style={{
                              background: 'transparent',
                              border: '1px solid #3e3e42',
                              color: '#cccccc',
                              padding: '6px 10px',
                              borderRadius: 8,
                              cursor: 'pointer',
                              fontSize: 12,
                            }}
                          >
                            {expanded ? 'Gizle' : 'Detay'}
                          </button>
                        </div>

                        {expanded && (
                          <div style={{ padding: 12, display: 'grid', gap: 10 }}>
                            <div style={{ color: '#cccccc', fontSize: 12, fontWeight: 700 }}>ai_prompt</div>
                            <div
                              style={{
                                background: '#0d1117',
                                border: '1px solid #2a2a2a',
                                borderRadius: 8,
                                padding: 10,
                                color: '#c9d1d9',
                                fontSize: 12,
                                whiteSpace: 'pre-wrap',
                              }}
                            >
                              {item.aiPrompt || '—'}
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                              <div>
                                <div style={{ color: '#9aa0a6', fontSize: 11, marginBottom: 6 }}>Eski</div>
                                <pre
                                  style={{
                                    margin: 0,
                                    background: '#0d1117',
                                    border: '1px solid #2a2a2a',
                                    borderRadius: 8,
                                    padding: 10,
                                    color: '#c9d1d9',
                                    fontSize: 11,
                                    overflow: 'auto',
                                    maxHeight: 240,
                                  }}
                                >
                                  {item.oldCode || ''}
                                </pre>
                              </div>
                              <div>
                                <div style={{ color: '#9aa0a6', fontSize: 11, marginBottom: 6 }}>Yeni</div>
                                <pre
                                  style={{
                                    margin: 0,
                                    background: '#0d1117',
                                    border: '1px solid #2a2a2a',
                                    borderRadius: 8,
                                    padding: 10,
                                    color: '#c9d1d9',
                                    fontSize: 11,
                                    overflow: 'auto',
                                    maxHeight: 240,
                                  }}
                                >
                                  {item.newCode || ''}
                                </pre>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                );
              })()}
            </div>

            <div
              style={{
                padding: 12,
                borderTop: '1px solid #3e3e42',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 10,
              }}
            >
              <div style={{ color: '#9aa0a6', fontSize: 11 }}>
                Not: Bu işlem step’lerin `ai_prompt` alanını ve edge ilişkilerini kullanarak script üretir.
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={handleApplyCompileAiUpdates}
                  disabled={isCompileAiGenerating || isCompileAiApplying}
                  style={{
                    background: '#0e639c',
                    border: 'none',
                    color: 'white',
                    padding: '8px 14px',
                    borderRadius: 8,
                    cursor: isCompileAiGenerating || isCompileAiApplying ? 'not-allowed' : 'pointer',
                    opacity: isCompileAiGenerating || isCompileAiApplying ? 0.6 : 1,
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  {isCompileAiApplying ? 'Kaydediliyor...' : 'Uygula + Kaydet'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
        {/* Main Content */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          <FlowChartSidebar
            isDesignMode={isDesignMode}
            requestFiles={requestFiles}
            fileSearchQuery={fileSearchQuery}
            setFileSearchQuery={setFileSearchQuery}
            selectedFileCategory={selectedFileCategory}
            setSelectedFileCategory={setSelectedFileCategory}
            tabs={tabs}
            activeTabId={activeTabId}
            handleFileClick={handleFileClick}
          />
          <FlowChartGraphArea
            showCodeEditor={showCodeEditor}
            codePanelWidth={codePanelWidth}
            graphContainerRef={graphContainerRef}
            gridIcon={gridIcon}
          />
          <FlowChartCodeEditorPanel
            showCodeEditor={showCodeEditor}
            codePanelWidth={codePanelWidth}
            isResizing={isResizing}
            setIsResizing={setIsResizing}
            resizeStartXRef={resizeStartXRef}
            resizeStartWidthRef={resizeStartWidthRef}
            setShowCodeEditor={setShowCodeEditor}
            selectedShape={selectedShape}
            selectedShapeLabel={selectedShapeLabel}
            code={code}
            handleCodeChange={handleCodeChange}
            handleEditorDidMount={handleEditorDidMount}
            aiStatus={aiStatus}
            pendingChange={pendingChange}
            onApplyPendingChange={handleApplyPendingChange}
            onDiscardPendingChange={handleDiscardPendingChange}
            isApplyingChange={isApplyingChange}
            currentPrompt={currentPrompt}
            onPromptChange={handlePromptChange}
            onRunShapeAI={handleRunShapeAI}
            isRunningShapeAI={isTerminalBusy}
            editorTabs={editorTabs}
            onTabActivate={handleTabActivate}
            onTabClose={handleCodeTabClose}
            onTabDetach={handleTabDetach}
          />
        </div>

        {/* Functions Modal */}
        {false && showFunctionsModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100002,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowFunctionsModal(false);
              setFunctions([]);
              setFunctionsError(null);
              setSelectedFunction(null);
            }
          }}
          >
            <div style={{
              background: '#2d2d2d',
              borderRadius: 8,
              padding: 24,
              width: '90%',
              maxWidth: 800,
              maxHeight: '85vh',
              border: '1px solid #3e3e42',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
              display: 'flex',
              flexDirection: 'column',
            }}
            onClick={(e) => e.stopPropagation()}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 20,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <button
                    onClick={() => {
                      setShowFunctionsModal(false);
                      setFunctions([]);
                      setFunctionsError(null);
                      setSelectedFunction(null);
                      setShowFxGroupsModal(true);
                    }}
                    style={{
                      background: 'transparent',
                      border: '1px solid #3e3e42',
                      color: '#cccccc',
                      padding: '6px 12px',
                      borderRadius: 4,
                      fontSize: 12,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <span>←</span>
                    <span>Geri</span>
                  </button>
                  <h3 style={{ color: '#cccccc', margin: 0, fontSize: 18, fontWeight: 600 }}>
                    🔧 Functions - {selectedFxGroup}
                  </h3>
                </div>
                <button
                  onClick={() => {
                    setShowFunctionsModal(false);
                    setFunctions([]);
                    setFunctionsError(null);
                    setSelectedFunction(null);
                  }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#888',
                    fontSize: 24,
                    cursor: 'pointer',
                    padding: 0,
                    width: 30,
                    height: 30,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  ×
                </button>
              </div>

              <div style={{
                flex: 1,
                overflowY: 'auto',
                padding: '0 4px',
              }}>
                {/* Loading State */}
                {isLoadingFunctions && (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '40px 20px',
                    color: '#888',
                  }}>
                    <div style={{
                      width: 40,
                      height: 40,
                      border: '3px solid #3e3e42',
                      borderTopColor: '#8b5cf6',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      marginBottom: 16,
                    }} />
                    <div style={{ fontSize: 13 }}>Functions yükleniyor...</div>
                  </div>
                )}
                
                {/* Error State */}
                {functionsError && (
                  <div style={{
                    padding: 20,
                    background: '#3a1f1f',
                    border: '1px solid #5a2a2a',
                    borderRadius: 4,
                    color: '#ff6b6b',
                    fontSize: 13,
                  }}>
                    <div style={{ fontWeight: 600, marginBottom: 8 }}>Hata:</div>
                    <div>{functionsError}</div>
                  </div>
                )}
                
                {/* Success State - Functions List */}
                {!isLoadingFunctions && !functionsError && functions.length > 0 && (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                  }}>
                    {functions.map((func, index) => (
                      <div
                        key={index}
                        style={{
                          background: selectedFunction === func.fname ? '#3e3e42' : '#2d2d2d',
                          border: `1px solid ${selectedFunction === func.fname ? '#8b5cf6' : '#3e3e42'}`,
                          borderRadius: 4,
                          padding: '12px 16px',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                        }}
                            onClick={() => {
                              fetchFunctionScript(func);
                            }}
                        onMouseEnter={(e) => {
                          if (selectedFunction !== func.fname) {
                            e.currentTarget.style.background = '#3e3e42';
                            e.currentTarget.style.borderColor = '#8b5cf6';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (selectedFunction !== func.fname) {
                            e.currentTarget.style.background = '#2d2d2d';
                            e.currentTarget.style.borderColor = '#3e3e42';
                          }
                        }}
                      >
                        <div style={{
                          color: '#cccccc',
                          fontSize: 13,
                          fontWeight: 500,
                          marginBottom: 4,
                        }}>
                          {func.fname}
                        </div>
                        {func.fdesc && (
                          <div style={{
                            color: '#888',
                            fontSize: 11,
                            fontStyle: 'italic',
                          }}>
                            {func.fdesc}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Empty State */}
                {!isLoadingFunctions && !functionsError && functions.length === 0 && (
                  <div style={{
                    padding: 20,
                    textAlign: 'center',
                    color: '#888',
                    fontSize: 13,
                  }}>
                    Functions bulunamadı.
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
                <button
                  onClick={() => {
                    setShowFunctionsModal(false);
                    setFunctions([]);
                    setFunctionsError(null);
                    setSelectedFunction(null);
                  }}
                  style={{
                    background: '#3e3e42',
                    border: 'none',
                    color: '#cccccc',
                    padding: '8px 16px',
                    borderRadius: 4,
                    fontSize: 13,
                    cursor: 'pointer',
                  }}
                >
                  Kapat
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Toast bildirimi */}
        {toast && (
          <div
            style={{
              position: 'fixed',
              bottom: 24,
              left: 24,
              zIndex: 999999,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '11px 16px',
              borderRadius: 8,
              background: toast.type === 'success' ? '#14532d' : '#450a0a',
              border: `1px solid ${toast.type === 'success' ? '#16a34a' : '#dc2626'}`,
              color: toast.type === 'success' ? '#86efac' : '#fca5a5',
              fontSize: 13,
              fontWeight: 500,
              boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
              maxWidth: 320,
              animation: 'fadeIn 0.2s ease',
            }}
          >
            <span style={{ fontSize: 16, flexShrink: 0 }}>
              {toast.type === 'success' ? '✅' : '❌'}
            </span>
            <span>{toast.message}</span>
            <button
              onClick={() => setToast(null)}
              style={{ marginLeft: 8, background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', opacity: 0.6, fontSize: 16, lineHeight: 1, flexShrink: 0 }}
            >×</button>
          </div>
        )}

        <FlowChartModals
          showAIPrompt={showAIPrompt}
          setShowAIPrompt={setShowAIPrompt}
          aiPrompt={aiPrompt}
          setAiPrompt={setAiPrompt}
          aiMode={aiMode}
          setAiMode={setAiMode}
          selectedCode={selectedCode}
          setSelectedCode={setSelectedCode}
          handleGenerateCode={handleGenerateCode}
          isGeneratingCode={isGeneratingCode}
          aiStatus={aiStatus}
          showFunctionLibraryModal={showFunctionLibraryModal}
          setShowFunctionLibraryModal={setShowFunctionLibraryModal}
          functionLibrary={functionLibrary}
          setFunctionLibrary={setFunctionLibrary}
          isLoadingFunctionLibrary={isLoadingFunctionLibrary}
          functionLibraryError={functionLibraryError}
          setFunctionLibraryError={setFunctionLibraryError}
          showFxGroupsModal={showFxGroupsModal}
          setShowFxGroupsModal={setShowFxGroupsModal}
          fxGroups={fxGroups}
          setFxGroups={setFxGroups}
          fxGroupsError={fxGroupsError}
          setFxGroupsError={setFxGroupsError}
          selectedFxGroup={selectedFxGroup}
          setSelectedFxGroup={setSelectedFxGroup}
          functions={functions}
          setFunctions={setFunctions}
          isLoadingFxGroups={isLoadingFxGroups}
          isLoadingFunctions={isLoadingFunctions}
          functionsError={functionsError}
          setFunctionsError={setFunctionsError}
          selectedFunction={selectedFunction}
          setSelectedFunction={setSelectedFunction}
          showFunctionsModal={showFunctionsModal}
          setShowFunctionsModal={setShowFunctionsModal}
          fetchFunctions={fetchFunctions}
          fetchFunctionScript={fetchFunctionScript}
          showIncludeModal={showIncludeModal}
          setShowIncludeModal={setShowIncludeModal}
          selectedIncludeShape={selectedIncludeShape}
          setSelectedIncludeShape={setSelectedIncludeShape}
          includeFoldersData={includeFoldersData}
          setIncludeFoldersData={setIncludeFoldersData}
          isLoadingIncludeData={isLoadingIncludeData}
          includeError={includeError}
          setIncludeError={setIncludeError}
          selectedIncludePath={selectedIncludePath}
          setSelectedIncludePath={setSelectedIncludePath}
          selectedFolder={selectedFolder}
          setSelectedFolder={setSelectedFolder}
          includeFilesData={includeFilesData}
          setIncludeFilesData={setIncludeFilesData}
          isLoadingFiles={isLoadingFiles}
          selectedFile={selectedFile}
          setSelectedFile={setSelectedFile}
          activeTabId={activeTabId}
          setTabs={setTabs}
          workflowDataRef={workflowDataRef}
          graphRef={graphRef}
          createAttachIcon={createAttachIcon}
          mxCellOverlay={mxCellOverlay}
          mxConstants={mxConstants}
          mxPoint={mxPoint}
          mxEvent={mxEvent}
          openIncludeDetails={openIncludeDetails}
          shapeIncludePaths={shapeIncludePaths}
          setShapeIncludePaths={setShapeIncludePaths}
          shapeIncludeFolders={shapeIncludeFolders}
          setShapeIncludeFolders={setShapeIncludeFolders}
          shapeOriginalLabels={shapeOriginalLabels}
          setShapeOriginalLabels={setShapeOriginalLabels}
          shapesWithInclude={shapesWithInclude}
          setShapesWithInclude={setShapesWithInclude}
          fetchWrapper={fetchWrapper}
          navigate={navigate}
          showIncludeInfoModal={showIncludeInfoModal}
          setShowIncludeInfoModal={setShowIncludeInfoModal}
          includeInfoData={includeInfoData}
          setIncludeInfoData={setIncludeInfoData}
        />
        <FlowChartFunctionWindows
          functionWindows={functionWindows}
          setFunctionWindows={setFunctionWindows}
          draggingWindowId={draggingWindowId}
          setDraggingWindowId={setDraggingWindowId}
          setDragOffset={setDragOffset}
          setResizingWindowId={setResizingWindowId}
          setResizeStart={setResizeStart}
          setResizeDirection={setResizeDirection}
          saveFunctionWindow={saveFunctionWindow}
          closeFunctionWindow={closeFunctionWindow}
          toggleMinimizeWindow={toggleMinimizeWindow}
          functionWindowEditorRefs={functionWindowEditorRefs}
        />
        <FlowChartPromptWindows
          promptWindows={promptWindows}
          setPromptWindows={setPromptWindows}
          draggingWindowId={draggingPromptWindowId}
          setDraggingWindowId={setDraggingPromptWindowId}
          setDragOffset={setPromptDragOffset}
          setResizingWindowId={setResizingPromptWindowId}
          setResizeStart={setPromptResizeStart}
          setResizeDirection={setPromptResizeDirection}
          closePromptWindow={closePromptWindow}
          toggleMinimizeWindow={toggleMinimizePromptWindow}
          onSavePromptWindow={savePromptWindow}
          onChangePrompt={onChangePromptWindow}
        />

        {/* Function Script Modal - DEPRECATED - Draggable windows kullanılıyor */}
        {false && showFunctionScriptModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100003,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowFunctionScriptModal(false);
              setFunctionScript('');
              setSelectedFunction(null);
            }
          }}
          >
            <div style={{
              background: '#2d2d2d',
              borderRadius: 8,
              padding: 24,
              width: '90%',
              maxWidth: 1200,
              height: '85vh',
              border: '1px solid #3e3e42',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
              display: 'flex',
              flexDirection: 'column',
            }}
            onClick={(e) => e.stopPropagation()}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 20,
                flexShrink: 0,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <button
                    onClick={() => {
                      setShowFunctionScriptModal(false);
                      setFunctionScript('');
                      setShowFunctionsModal(true);
                    }}
                    style={{
                      background: 'transparent',
                      border: '1px solid #3e3e42',
                      color: '#cccccc',
                      padding: '6px 12px',
                      borderRadius: 4,
                      fontSize: 12,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <span>←</span>
                    <span>Geri</span>
                  </button>
                  <h3 style={{ color: '#cccccc', margin: 0, fontSize: 18, fontWeight: 600 }}>
                    📝 Function Script - {selectedFunction}
                  </h3>
                </div>
                <button
                  onClick={() => {
                    setShowFunctionScriptModal(false);
                    setFunctionScript('');
                    setSelectedFunction(null);
                  }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#888',
                    fontSize: 24,
                    cursor: 'pointer',
                    padding: 0,
                    width: 30,
                    height: 30,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  ×
                </button>
              </div>

              <div style={{
                flex: 1,
                overflow: 'hidden',
                background: '#1e1e1e',
                borderRadius: 4,
                border: '1px solid #3e3e42',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
              }}>
                {/* Loading State */}
                {isLoadingFunctionScript && (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '40px 20px',
                    color: '#888',
                    height: '100%',
                  }}>
                    <div style={{
                      width: 40,
                      height: 40,
                      border: '3px solid #3e3e42',
                      borderTopColor: '#8b5cf6',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      marginBottom: 16,
                    }} />
                    <div style={{ fontSize: 13 }}>Function script yükleniyor...</div>
                  </div>
                )}
                
                {/* Editor */}
                {!isLoadingFunctionScript && (
                  <div style={{ 
                    flex: 1, 
                    minHeight: 0, 
                    width: '100%', 
                    height: '100%',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                  }}>
                    <Editor
                      height="100%"
                      width="100%"
                      defaultLanguage="php"
                      value={functionScript}
                      theme="vs-dark"
                      options={{
                        readOnly: true,
                        fontSize: 13,
                        minimap: { enabled: true },
                        wordWrap: 'on',
                        automaticLayout: true,
                        lineNumbers: 'on',
                        roundedSelection: true,
                        cursorStyle: 'line',
                        folding: true,
                        scrollBeyondLastLine: true,
                        renderWhitespace: 'selection',
                        tabSize: 4,
                      }}
                    />
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16, flexShrink: 0 }}>
                <button
                  onClick={() => {
                    setShowFunctionScriptModal(false);
                    setFunctionScript('');
                    setSelectedFunction(null);
                  }}
                  style={{
                    background: '#3e3e42',
                    border: 'none',
                    color: '#cccccc',
                    padding: '8px 16px',
                    borderRadius: 4,
                    fontSize: 13,
                    cursor: 'pointer',
                  }}
                >
                  Kapat
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Include Modal */}
        {false && showIncludeModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100001,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowIncludeModal(false);
              setSelectedIncludeShape(null);
              setSelectedIncludePath(null);
              setSelectedFolder(null);
              setIncludeFilesData(null);
              setSelectedFile(null);
              setIncludeFoldersData(null);
              setIncludeError(null);
            }
          }}
          >
            <div style={{
              background: '#2d2d2d',
              borderRadius: 8,
              padding: 24,
              width: '90%',
              maxWidth: 800,
              maxHeight: '80vh',
              border: '1px solid #3e3e42',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
              display: 'flex',
              flexDirection: 'column',
            }}
            onClick={(e) => e.stopPropagation()}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 20,
              }}>
                <h3 style={{ color: '#cccccc', margin: 0, fontSize: 18, fontWeight: 600 }}>
                  🔗 Add Include
                </h3>
                <button
                  onClick={() => {
                    setShowIncludeModal(false);
                    setSelectedIncludeShape(null);
                    setSelectedIncludePath(null);
                    setSelectedFolder(null);
                    setIncludeFilesData(null);
                    setSelectedFile(null);
                    setIncludeFoldersData(null);
                    setIncludeError(null);
                  }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#888',
                    fontSize: 24,
                    cursor: 'pointer',
                    padding: 0,
                    width: 30,
                    height: 30,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  ×
                </button>
              </div>

              <div style={{
                flex: 1,
                overflow: 'auto',
                background: '#1e1e1e',
                borderRadius: 4,
                padding: 16,
                marginBottom: 16,
              }}>
                <div style={{
                  color: '#888',
                  fontSize: 13,
                  marginBottom: 12,
                }}>
                  Seçili Şekil: {selectedIncludeShape}
                </div>
                
                {/* Loading State */}
                {isLoadingIncludeData && (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '40px 20px',
                    color: '#888',
                  }}>
                    <div style={{
                      width: 40,
                      height: 40,
                      border: '3px solid #3e3e42',
                      borderTopColor: '#0e639c',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      marginBottom: 16,
                    }} />
                    <div style={{ fontSize: 13 }}>Folder ve file listesi yükleniyor...</div>
                  </div>
                )}
                
                {/* Error State */}
                {includeError && (
                  <div style={{
                    padding: 20,
                    background: '#5a1e1e',
                    borderRadius: 4,
                    color: '#ff7070',
                    fontSize: 13,
                    marginBottom: 16,
                  }}>
                    ❌ {includeError}
                  </div>
                )}
                
                {/* Folder Listesi - result array'inden folderId ve folderName kullanarak */}
                {!isLoadingIncludeData && !includeError && includeFoldersData && !selectedFolder && (
                <div style={{
                  color: '#cccccc',
                  fontSize: 14,
                }}>
                    {(() => {
                      try {
                        // Response'dan gelen data'yı kontrol et
                        let foldersList = [];
                        
                        // Eğer direkt array ise (result array'i)
                        if (Array.isArray(includeFoldersData)) {
                          foldersList = includeFoldersData;
                        }
                        // Eğer object ise ve result key'i varsa
                        else if (includeFoldersData.result && Array.isArray(includeFoldersData.result)) {
                          foldersList = includeFoldersData.result;
                        }
                        // Eğer object ise ve data key'i varsa
                        else if (includeFoldersData.data && Array.isArray(includeFoldersData.data)) {
                          foldersList = includeFoldersData.data;
                        }
                        // Eğer string ise parse et
                        else if (typeof includeFoldersData === 'string') {
                          try {
                            const parsed = JSON.parse(includeFoldersData);
                            if (Array.isArray(parsed)) {
                              foldersList = parsed;
                            } else if (parsed.result && Array.isArray(parsed.result)) {
                              foldersList = parsed.result;
                            }
                          } catch (e) {
                            return (
                  <div style={{
                                padding: 12,
                                background: '#2d2d2d',
                                borderRadius: 4,
                                color: '#cccccc',
                                fontSize: 12,
                                fontFamily: 'monospace',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                  }}>
                                {includeFoldersData}
                  </div>
                            );
                          }
                        }
                        
                        // Folder listesi boşsa
                        if (foldersList.length === 0) {
                          return (
                  <div style={{
                              padding: 20,
                              textAlign: 'center',
                              color: '#888',
                              fontSize: 13,
                            }}>
                              Folder bulunamadı.
                            </div>
                          );
                        }
                        
                        // Folder listesini render et
                        return (
                          <div style={{
                            display: 'grid',
                            gap: 8,
                          }}>
                            {foldersList.map((folder, index) => {
                              // folderId ve folderName kullan
                              const folderId = folder.folderId || folder.id || String(index);
                              const folderName = folder.folderName || folder.name || 'Unnamed';
                              const isSelected = selectedIncludePath === folderId;
                              
                              return (
                                <div
                                  key={folderId}
                                  onClick={() => {
                                    // Folder seçildiğinde folder bilgisini kaydet ve file listesini getir
                                    setSelectedIncludePath(folderId);
                                    setSelectedFolder(folder);
                                    setSelectedFile(null); // File seçimini temizle
                                  }}
                                  style={{
                                    padding: '12px 16px',
                                    background: isSelected ? '#0e639c' : 'transparent',
                                    border: isSelected ? '2px solid #3e9fdb' : '1px solid #3e3e42',
                                    borderRadius: 6,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 12,
                                    transition: 'all 0.2s',
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!isSelected) {
                                      e.currentTarget.style.background = '#3e3e42';
                                      e.currentTarget.style.borderColor = '#555';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (!isSelected) {
                                      e.currentTarget.style.background = 'transparent';
                                      e.currentTarget.style.borderColor = '#3e3e42';
                                    }
                                  }}
                                >
                                  <div style={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: 6,
                                    background: isSelected ? '#3e9fdb' : '#3e3e42',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 20,
                                    flexShrink: 0,
                                  }}>
                                    📁
                                  </div>
                                  <div style={{
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 4,
                                  }}>
                                    <div style={{
                                      fontSize: 15,
                                      fontWeight: 600,
                                      color: '#cccccc',
                                    }}>
                                      {folderName}
                                    </div>
                                    <div style={{
                    fontSize: 12,
                                      color: '#888',
                                      fontFamily: 'monospace',
                  }}>
                                      ID: {folderId}
                  </div>
                </div>
                                  {isSelected && (
                                    <div style={{
                                      width: 24,
                                      height: 24,
                                      borderRadius: '50%',
                                      background: '#4caf50',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      flexShrink: 0,
                                    }}>
                                      <span style={{ fontSize: 14, color: 'white' }}>✓</span>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        );
                      } catch (error) {
                        console.error('Include data render hatası:', error);
                        return (
                          <div style={{
                            padding: 20,
                            textAlign: 'center',
                            color: '#ff7070',
                            fontSize: 13,
                          }}>
                            Veri render edilemedi: {error.message}
                          </div>
                        );
                      }
                    })()}
                  </div>
                )}

                {/* Seçili Folder ve File Listesi */}
                {selectedFolder && (
                  <div style={{
                    color: '#cccccc',
                    fontSize: 14,
                  }}>
                    {/* Seçili Folder Bilgisi */}
                    <div style={{
                      padding: '12px 16px',
                      background: '#2d2d2d',
                      borderRadius: 6,
                      marginBottom: 16,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                    }}>
                      <button
                        onClick={() => {
                          // Geri dön - folder seçimini temizle
                          setSelectedFolder(null);
                          setSelectedIncludePath(null);
                          setIncludeFilesData(null);
                          setSelectedFile(null);
                        }}
                        style={{
                          background: '#3e3e42',
                          border: 'none',
                          color: '#cccccc',
                          padding: '6px 12px',
                          borderRadius: 4,
                          cursor: 'pointer',
                          fontSize: 12,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4,
                        }}
                      >
                        ← Geri
                      </button>
                      <div style={{
                        width: 32,
                        height: 32,
                        borderRadius: 6,
                        background: '#0e639c',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 16,
                      }}>
                        📁
                      </div>
                      <div>
                        <div style={{
                          fontSize: 14,
                          fontWeight: 600,
                          color: '#cccccc',
                        }}>
                          {selectedFolder.folderName || selectedFolder.foldername || selectedFolder.name}
                        </div>
                        <div style={{
                          fontSize: 11,
                          color: '#888',
                          fontFamily: 'monospace',
                        }}>
                          ID: {selectedFolder.folderId || selectedFolder.id}
                        </div>
                      </div>
                    </div>

                    {/* File Listesi Loading */}
                    {isLoadingFiles && (
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '40px 20px',
                        color: '#888',
                      }}>
                        <div style={{
                          width: 32,
                          height: 32,
                          border: '3px solid #3e3e42',
                          borderTopColor: '#0e639c',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite',
                          marginBottom: 12,
                        }} />
                        <div style={{ fontSize: 12 }}>File listesi yükleniyor...</div>
                      </div>
                    )}

                    {/* File Listesi */}
                    {!isLoadingFiles && includeFilesData && (
                      <div>
                        {(() => {
                          try {
                            let filesList = [];
                            
                            if (Array.isArray(includeFilesData)) {
                              filesList = includeFilesData;
                            } else if (includeFilesData.result && Array.isArray(includeFilesData.result)) {
                              filesList = includeFilesData.result;
                            } else if (includeFilesData.data && Array.isArray(includeFilesData.data)) {
                              filesList = includeFilesData.data;
                            }
                            
                            if (filesList.length === 0) {
                              return (
                                <div style={{
                                  padding: 20,
                                  textAlign: 'center',
                                  color: '#888',
                                  fontSize: 13,
                                }}>
                                  Bu folder'da file bulunamadı.
                                </div>
                              );
                            }
                            
                            return (
                              <div style={{
                                display: 'grid',
                                gap: 8,
                              }}>
                                {filesList.map((file, index) => {
                                  // Response yapısına göre: diagrun_id ve filename
                                  const fileId = file.diagrun_id || file.fileId || file.id || String(index);
                                  const fileName = file.filename || file.fileName || file.name || 'Unnamed';
                                  const lastEdit = file.last_edit || file.lastEdit || null;
                                  const lastCompileDate = file.last_compile_date || file.lastCompileDate || null;
                                  const isSelected = selectedFile === fileId;
                                  
                                  return (
                                    <div
                                      key={fileId}
                                      onClick={() => {
                                        setSelectedFile(fileId);
                                      }}
                                      style={{
                                        padding: '12px 16px',
                                        background: isSelected ? '#0e639c' : 'transparent',
                                        border: isSelected ? '2px solid #3e9fdb' : '1px solid #3e3e42',
                                        borderRadius: 6,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 12,
                                        transition: 'all 0.2s',
                                      }}
                                      onMouseEnter={(e) => {
                                        if (!isSelected) {
                                          e.currentTarget.style.background = '#3e3e42';
                                          e.currentTarget.style.borderColor = '#555';
                                        }
                                      }}
                                      onMouseLeave={(e) => {
                                        if (!isSelected) {
                                          e.currentTarget.style.background = 'transparent';
                                          e.currentTarget.style.borderColor = '#3e3e42';
                                        }
                                      }}
                                    >
                                      <div style={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: 6,
                                        background: isSelected ? '#3e9fdb' : '#3e3e42',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 20,
                                        flexShrink: 0,
                                      }}>
                                        📄
                                      </div>
                                      <div style={{
                                        flex: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 4,
                                      }}>
                                        <div style={{
                                          fontSize: 15,
                                          fontWeight: 600,
                                          color: '#cccccc',
                                        }}>
                                          {fileName}
                                        </div>
                                        <div style={{
                                          fontSize: 11,
                                          color: '#888',
                                          fontFamily: 'monospace',
                                          display: 'flex',
                                          gap: 12,
                                          flexWrap: 'wrap',
                                        }}>
                                          <span>ID: {fileId}</span>
                                          {lastEdit && (
                                            <span>Son Düzenleme: {lastEdit}</span>
                                          )}
                                          {lastCompileDate && (
                                            <span>Son Derleme: {lastCompileDate}</span>
                                          )}
                                        </div>
                                      </div>
                                      {isSelected && (
                                        <div style={{
                                          width: 24,
                                          height: 24,
                                          borderRadius: '50%',
                                          background: '#4caf50',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          flexShrink: 0,
                                        }}>
                                          <span style={{ fontSize: 14, color: 'white' }}>✓</span>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          } catch (error) {
                            console.error('File list render hatası:', error);
                            return (
                              <div style={{
                                padding: 20,
                                textAlign: 'center',
                                color: '#ff7070',
                                fontSize: 13,
                              }}>
                                File listesi render edilemedi: {error.message}
                              </div>
                            );
                          }
                        })()}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Empty State */}
                {!isLoadingIncludeData && !includeError && !includeFoldersData && (
                  <div style={{
                    padding: 20,
                    textAlign: 'center',
                    color: '#888',
                    fontSize: 13,
                  }}>
                    Veri bulunamadı.
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setShowIncludeModal(false);
                    setSelectedIncludeShape(null);
                    setSelectedIncludePath(null);
                    setSelectedFolder(null);
                    setIncludeFilesData(null);
                    setSelectedFile(null);
                    setSelectedFolder(null);
                    setIncludeFilesData(null);
                    setSelectedFile(null);
                    setIncludeFoldersData(null);
                    setIncludeError(null);
                  }}
                  style={{
                    background: '#3e3e42',
                    border: 'none',
                    color: '#cccccc',
                    padding: '8px 16px',
                    borderRadius: 4,
                    fontSize: 13,
                    cursor: 'pointer',
                  }}
                >
                  İptal
                </button>
                <button
                  onClick={() => {
                    if (!selectedFile) {
                      alert('Lütfen bir file seçin!');
                      return;
                    }
                    
                    // Seçili file bilgisini bul
                    let filesList = [];
                    if (Array.isArray(includeFilesData)) {
                      filesList = includeFilesData;
                    } else if (includeFilesData?.result && Array.isArray(includeFilesData.result)) {
                      filesList = includeFilesData.result;
                    } else if (includeFilesData?.data && Array.isArray(includeFilesData.data)) {
                      filesList = includeFilesData.data;
                    }
                    
                    const selectedFileData = filesList.find(f => 
                      (f.diagrun_id || f.fileId || f.id || f.name) === selectedFile
                    );
                    const fileName = selectedFileData?.filename || selectedFileData?.fileName || selectedFileData?.name || selectedFile;
                    const diagrunId = selectedFileData?.diagrun_id || selectedFile;
                    
                    // Include ekleme işlemi - API call
                    if (selectedIncludeShape && diagrunId) {
                      const addInclude = async () => {
                        try {
                          const processId = process.env.REACT_APP_ADD_INCLUDABLE_LAST_SELECT;
                          if (!processId) {
                            throw new Error('REACT_APP_ADD_INCLUDABLE_LAST_SELECT environment variable tanımlı değil!');
                          }
                          
                          const bodyParams = {
                            stepid: selectedIncludeShape,
                            include: diagrunId,
                            process: processId,
                          };
                          
                          const response = await fetchWrapper(processId, bodyParams, navigate);
                          
                          // Response kontrolü - nested result yapısı
                          // Response: { result: { result: 1, message: "Success" } }
                          const resultData = response?.result;
                          const isSuccess = resultData && 
                                           (resultData.result === 1 || resultData.result === "1") && 
                                           (resultData.message === 'success' || resultData.message === 'Success');
                          
                          if (response && isSuccess) {
                            // Başarılı - Include path'i kaydet ve icon ekle
                            const includePath = diagrunId;
                            const foldername = selectedFolder?.folderName || selectedFolder?.foldername || '';

                            // Aktif tab'ın workflowData'sına include bilgisini yaz
                            if (activeTabId) {
                              setTabs(prev => prev.map(tab => {
                                if (tab.id !== activeTabId) return tab;
                                const currentData = tab.workflowData || workflowDataRef.current;
                                if (!currentData?.contents) return tab;
                                const updatedContents = currentData.contents.map(item => {
                                  if (!item || String(item.id) !== String(selectedIncludeShape)) {
                                    return item;
                                  }
                                  return {
                                    ...item,
                                    include: includePath,
                                    filename: fileName || item.filename || '',
                                    foldername: foldername || item.foldername || item.folderName || '',
                                  };
                                });
                                return {
                                  ...tab,
                                  workflowData: {
                                    ...currentData,
                                    contents: updatedContents,
                                  },
                                };
                              }));
                            }
                            
                            setShapeIncludePaths(prev => ({
                              ...prev,
                              [selectedIncludeShape]: includePath
                            }));
                            
                            // Foldername'i kaydet (Remove Include için)
                            setShapeIncludeFolders(prev => ({
                              ...prev,
                              [selectedIncludeShape]: foldername
                            }));
                            
                            // Include edilmiş şekiller listesine ekle
                            setShapesWithInclude(prev => new Set([...prev, selectedIncludeShape]));
                            
                            // mxGraph'te şeklin sağ alt köşesine icon ekle ve label'ı güncelle
                            if (graphRef.current) {
                              const graph = graphRef.current;
                              const model = graph.getModel();
                              const cell = model.getCell(selectedIncludeShape);
                              
                              if (cell && model.isVertex(cell)) {
                                // Önce mevcut label'ı sakla (Remove Include için)
                                const currentLabel = cell.getValue() ? String(cell.getValue()) : '';
                                setShapeOriginalLabels(prev => ({
                                  ...prev,
                                  [selectedIncludeShape]: currentLabel
                                }));
                                
                                // Şeklin label'ını filename ile güncelle
                                model.beginUpdate();
                                try {
                                  cell.setValue(fileName);
                                  graph.refresh(cell);
                                } finally {
                                  model.endUpdate();
                                }
                                
                                // Önce mevcut overlay'i kaldır (varsa)
                                graph.removeCellOverlay(cell);
                                
                                // Yeni overlay oluştur - attach icon
                                const attachIcon = createAttachIcon();
                                
                                // Sağ alt köşe için overlay oluştur
                                // mxPoint offset: x negatif = sağa, y negatif = aşağı
                                const overlay = new mxCellOverlay(
                                  attachIcon,
                                  'Include edildi',
                                  mxConstants.ALIGN_RIGHT,
                                  mxConstants.ALIGN_BOTTOM,
                                  new mxPoint(-2, -2) // Sağ alt köşede, şeklin içinde
                                );
                                
                                // Overlay'e click event ekle
                                overlay.addListener(mxEvent.CLICK, function(sender, evt) {
                                  const cell = evt.getProperty('cell');
                                  if (cell) {
                                    openIncludeDetails(cell.getId(), { showPopup: false });
                                  }
                                });
                                
                                // Overlay'i ekle
                                graph.addCellOverlay(cell, overlay);
                                
                                // Graph'i refresh et ve validate et - overlay'in görünmesi için
                                graph.getView().invalidate(cell);
                                graph.getView().validate();
                                graph.refresh();
                                
                                // Cell'in pozisyonunu ve boyutunu console'da göster
                                const geo = cell.getGeometry();
                              }
                            }
                            
                          } else {
                            throw new Error(response?.message || 'Include ekleme başarısız');
                          }
                        } catch (error) {
                          alert(`Include eklenirken hata oluştu: ${error.message}`);
                          return; // Hata durumunda modal'ı kapatma
                        }
                      };
                      
                      // Async işlemi başlat
                      addInclude().then(() => {
                        // Başarılı olduysa modal'ı kapat
                    setShowIncludeModal(false);
                    setSelectedIncludeShape(null);
                        setSelectedIncludePath(null);
                        setSelectedFolder(null);
                        setIncludeFilesData(null);
                        setSelectedFile(null);
                        setIncludeFoldersData(null);
                        setIncludeError(null);
                      }).catch(() => {
                        // Hata durumunda modal açık kalsın
                      });
                    } else {
                      // Modal'ı kapat
                      setShowIncludeModal(false);
                      setSelectedIncludeShape(null);
                      setSelectedIncludePath(null);
                      setSelectedFolder(null);
                      setIncludeFilesData(null);
                      setSelectedFile(null);
                      setIncludeFoldersData(null);
                      setIncludeError(null);
                    }
                  }}
                  disabled={!selectedFile}
                  style={{
                    background: selectedFile ? '#0e639c' : '#3e3e42',
                    border: 'none',
                    color: 'white',
                    padding: '8px 20px',
                    borderRadius: 4,
                    fontSize: 13,
                    cursor: selectedFile ? 'pointer' : 'not-allowed',
                    fontWeight: 500,
                    opacity: selectedFile ? 1 : 0.5,
                  }}
                >
                  Include Ekle {selectedFile && `(${selectedFile})`}
                </button>
              </div>
            </div>
          </div>
        )}

        {false && showIncludeInfoModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100002,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowIncludeInfoModal(false);
              setIncludeInfoData(null);
            }
          }}
          >
            <div style={{
              background: '#2d2d2d',
              borderRadius: 8,
              padding: 20,
              width: '90%',
              maxWidth: 520,
              border: '1px solid #3e3e42',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
            }}
            onClick={(e) => e.stopPropagation()}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 16,
              }}>
                <h3 style={{ color: '#cccccc', margin: 0, fontSize: 16, fontWeight: 600 }}>
                  🔎 Include Bilgisi
                </h3>
                <button
                  onClick={() => {
                    setShowIncludeInfoModal(false);
                    setIncludeInfoData(null);
                  }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#888',
                    fontSize: 22,
                    cursor: 'pointer',
                    padding: 0,
                    width: 28,
                    height: 28,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  ×
                </button>
              </div>
              <div style={{
                background: '#1e1e1e',
                borderRadius: 4,
                padding: 12,
                color: '#cccccc',
                fontSize: 13,
                display: 'grid',
                gridTemplateColumns: '120px 1fr',
                rowGap: 10,
                columnGap: 10,
              }}>
                <div style={{ color: '#888' }}>Shape ID</div>
                <div>{includeInfoData?.shapeId || '—'}</div>
                <div style={{ color: '#888' }}>Include ID</div>
                <div>{includeInfoData?.include || '—'}</div>
                <div style={{ color: '#888' }}>Folder</div>
                <div>{includeInfoData?.foldername || '—'}</div>
                <div style={{ color: '#888' }}>Filename</div>
                <div>{includeInfoData?.filename || '—'}</div>
              </div>
            </div>
          </div>
        )}
      
      <FlowChartTerminalDock
        isOpen={showTerminal}
        onToggle={() => setShowTerminal(false)}
        lines={terminalLines}
        onSubmit={handleTerminalSubmit}
        onClear={handleTerminalClear}
        statusLabel={isTerminalBusy ? 'Working' : selectedShape ? 'Connected' : 'Idle'}
        isBusy={isTerminalBusy}
        inputValue={terminalInput}
        onInputChange={setTerminalInput}
      />
      <FlowChartStatusBar
        saveStatus={saveStatus}
        isDesignMode={isDesignMode}
        showCodeEditor={showCodeEditor}
        selectedShape={selectedShape}
      />

      {/* ── Yüzer (floating) code editor panelleri ── */}
      {floatingEditors.map((f) => (
        <FloatingCodeEditor
          key={f.id}
          id={f.id}
          shapeId={f.shapeId}
          shapeLabel={f.shapeLabel}
          initialCode={f.code}
          initialPrompt={f.prompt}
          onCodeChange={handleFloatingCodeChange}
          onPromptChange={handleFloatingPromptChange}
          onClose={() => handleFloatingClose(f.id)}
          saveScript={handleFloatingSave}
          defaultX={f.x}
          defaultY={f.y}
          defaultW={f.w}
          defaultH={f.h}
        />
      ))}
    </div>
  );
  
  return createPortal(modalContent, document.body);
};

export default FlowChartDevelopmentScreen;